import React, { useState, useRef, useEffect, useCallback } from "react";
import { connect } from 'react-redux';
import { Formik } from 'formik';

import { Button, SVGIcon } from '../../../../components/custom-essentials';
import { BrowserTabTitle, LoadingOverlay, TooltipWrapper } from '../../../../components/display';
import { SelectSingle, checkResponses, DatePicker } from '../../../../components/form';
import { formatDateFull, formatDateApi, getDataArray } from '../../../../components/functions';
import { Socket } from '../../../../components/ws';
import BookListModal from './BookListModal';

import {
    fetchBookListsAll,
    fetchBookListsId,
    fetchBookListsStudents,
    fetchBooksAll,
    fetchCycleGroupsAll,
    fetchStudentsAll,
    fetchRpCentersAll,
    createBookList,
    updateBookList,
    deleteBookList,
    fetchBookListTemplatesAll,
    fetchBookListTemplatesFull,
    createBookListTemplate,
    updateBookListTemplate,
    deleteBookListTemplate,
    fetchBookListItemsAll,
    fetchBookStudentDatesByBookList,
    saveBookStudentDate
} from '../../../../actions';
import BookListTemplateModal from '../book-list-templates/BookListTemplateModal';

const pageTitle = 'Book List Manager';

function BookLists(props) {
    const mounted = useRef(false);
    const saveQueueRef = useRef(Promise.resolve());
    const lastSavedPayloadSigRef = useRef(null);
    const lastSuccessfulSaveRef = useRef({ ts: 0, bookListId: null, name: null, notes: null, active: null });
    useEffect(() => {
        mounted.current = true;
        return () => (mounted.current = false);
    });
    const formRef = useRef();

    const [hasLoaded, setHasLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState(false);
    const [books, setBooks] = useState([]);
    const [groups, setGroups] = useState([]);
    const [groupMembers, setGroupMembers] = useState([]);
    const [students, setStudents] = useState([]);
    const [centers, setCenters] = useState([]);
    const [bookLists, setBookLists] = useState([]);
    const [selectedBookListData, setSelectedBookListData] = useState(null);
    const [originalBookListName, setOriginalBookListName] = useState(null); // Track original name for change detection
    const [templates, setTemplates] = useState([]);
    const [templateItems, setTemplateItems] = useState([]);

    // Dropdown options
    const [centerOptions, setCenterOptions] = useState([{ value: 'all', label: 'All' }]);
    const [groupOptions, setGroupOptions] = useState([{ value: -1, label: 'Please select...' }]);
    const [studentOptions, setStudentOptions] = useState([{ value: -1, label: 'Please select...' }]);
    const [bookListOptions, setBookListOptions] = useState([{ value: -1, label: 'Please select a student...' }]);
    const [templateOptions, setTemplateOptions] = useState([{ value: -1, label: 'Please select...' }, { value: 'new', label: '+ New Book List' }]);

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedBookList, setSelectedBookList] = useState(null);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [templateModalMode, setTemplateModalMode] = useState('create');
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    // Drag and drop state
    const [draggedItem, setDraggedItem] = useState(null);
    const [draggedOverIndex, setDraggedOverIndex] = useState(null);

    // Date editing state
    const [editingDate, setEditingDate] = useState(null); // { bookId, field: 'date_assigned' | 'date_completed' }
    const [editingDateValue, setEditingDateValue] = useState(''); // Current value being edited
    const editingDateValueRef = useRef(''); // Ref to preserve value during async operations
    const pendingSavedDatesRef = useRef(new Map()); // Track recently saved dates: key = `${bookId}_${field}`, value = { date, timestamp }
    
    // Reading status filter state
    const [readingStatusFilter, setReadingStatusFilter] = useState('read_by_at_least_one'); // 'all', 'read_by_all', 'unread_by_all', 'read_by_at_least_one'
    const [allBookListItems, setAllBookListItems] = useState([]); // All book list items for reading status checks
    const [selectedStudentForReadingStatus, setSelectedStudentForReadingStatus] = useState(null); // Track selected student for reading status table re-renders
    const [selectedGroupForReadingStatus, setSelectedGroupForReadingStatus] = useState(null); // Track selected group for reading status table re-renders
    const [studentDates, setStudentDates] = useState([]); // All student dates for date display


    const { fetchBookListsAll, fetchBookListsId, fetchBookListsStudents, fetchBooksAll,
        fetchCycleGroupsAll, fetchStudentsAll, fetchRpCentersAll,
        createBookList, updateBookList, deleteBookList,
        fetchBookListTemplatesAll, fetchBookListTemplatesFull,
        createBookListTemplate, updateBookListTemplate, deleteBookListTemplate,
        fetchBookListItemsAll,
        fetchBookStudentDatesByBookList, saveBookStudentDate } = props;

    const handleOpenModal = (mode, bookList = null) => {
        setModalMode(mode);
        setSelectedBookList(bookList);
        setShowModal(true);
    };

    const handleCloseModal = useCallback(async (shouldRefresh = false) => {
        setShowModal(false);
        setSelectedBookList(null);
        if (shouldRefresh) {
            await refreshData();
            // After refresh, also refresh the book list dropdown if group/student is selected
            // This ensures newly created book lists show up in the dropdown
            if (formRef.current?.values) {
                const selectedStudent = formRef.current.values.selectedStudent;
                if (selectedStudent && selectedStudent.value === 'all_group') {
                    // Trigger student change handler to refresh dropdown for group
                    await handleStudentChange({ target: { value: selectedStudent } }, formRef.current);
                } else if (selectedStudent && selectedStudent.value !== -1) {
                    // Trigger student change handler to refresh dropdown for individual student
                    await handleStudentChange({ target: { value: selectedStudent } }, formRef.current);
                }
            }
        }
    }, [refreshData, handleStudentChange]);

    // When center changes, filter groups and students
    const handleCenterChange = useCallback((e, formik) => {
        formik.handleChange(e);
        const selectedCenter = e.target.value;

        // Filter groups by center
        let filteredGroups = groups;
        if (selectedCenter && selectedCenter.value !== 'all') {
            filteredGroups = groups.filter(g => parseInt(g.center_id) === selectedCenter.value);
        }

        const newGroupOptions = [
            { value: -1, label: 'Please select...' },
            ...filteredGroups.map(g => ({
                value: parseInt(g.id),
                label: g.name,
                obj: g
            }))
        ];
        setGroupOptions(newGroupOptions);

        // Filter students by center (for individual selection)
        let filteredStudents = students;
        if (selectedCenter && selectedCenter.value !== 'all') {
            filteredStudents = students.filter(s => {
                // Check if student has a center_id field or belongs to a center
                const studentCenter = s.center_id || s.rp_primary_center || s.mp_primary_center;
                return parseInt(studentCenter) === selectedCenter.value;
            });
        }

        const newStudentOptions = [
            { value: -1, label: 'Please select...' },
            ...filteredStudents.map(s => ({
                value: s.user_id,
                label: `${s.first_name} ${s.last_name}`,
                obj: s
            }))
        ];
        setStudentOptions(newStudentOptions);

        // Reset downstream selections
        formik.setFieldValue('selectedGroup', { value: -1, label: 'Please select...' });
        formik.setFieldValue('selectedStudent', { value: -1, label: 'Please select...' });
        formik.setFieldValue('selectedBookList', { value: -1, label: 'Please select a student...' });
        setBookListOptions([{ value: -1, label: 'Please select a student...' }]);
        setSelectedBookListData(null);
    }, [groups, students]);

    // Helper function to extract base name from book list name
    // Handles names like "John Doe - Book List A" -> "Book List A"
    // or just "Book List A" -> "Book List A"
    const extractBaseName = (name) => {
        if (!name) return '';
        // If name contains " - ", extract the part after it
        const parts = name.split(' - ');
        if (parts.length > 1) {
            return parts.slice(1).join(' - ').trim(); // Join in case there are multiple " - "
        }
        return name.trim();
    };

    // Helper function to check if a book is read (has date_completed)
    const isBookRead = (dateCompleted) => {
        if (!dateCompleted) return false;
        if (dateCompleted === 'null' || dateCompleted === null) return false;
        if (typeof dateCompleted === 'string' && dateCompleted.trim() === '') return false;
        // Check if it's a valid date (not '0000-00-00' or similar)
        if (typeof dateCompleted === 'string' && dateCompleted.includes('0000-00-00')) return false;
        return true;
    };

    // Helper function to get reading status for a book across students
    // Helper function to aggregate dates for a book from studentDates (similar to Read By)
    const getBookDatesAggregated = useCallback((bookId, targetStudents, allStudentDates, dateField = 'date_assigned') => {
        if (!bookId || !targetStudents || targetStudents.length === 0 || !allStudentDates || allStudentDates.length === 0) {
            return { dates: [], dateNames: [] };
        }

        const dates = [];
        const dateNames = [];

        for (const student of targetStudents) {
            const studentId = student.user_id || student;
            const studentName = students.find(s => parseInt(s.user_id) === parseInt(studentId))
                ? `${students.find(s => parseInt(s.user_id) === parseInt(studentId)).first_name} ${students.find(s => parseInt(s.user_id) === parseInt(studentId)).last_name}`
                : `Student ${studentId}`;

            // Find all date entries for this student-book combination
            const studentBookDates = allStudentDates.filter(sd =>
                parseInt(sd.student) === parseInt(studentId) &&
                parseInt(sd.book_id) === parseInt(bookId)
            );

            // Get dates from all book lists (student can have same book in multiple lists)
            studentBookDates.forEach(dateEntry => {
                const dateValue = dateEntry[dateField];
                if (dateValue) {
                    const dateStr = formatDateFull(dateValue);
                    dates.push({
                        studentId,
                        studentName,
                        bookListId: dateEntry.book_list_id,
                        date: dateValue,
                        dateFormatted: dateStr
                    });
                    dateNames.push(`${studentName} (${dateStr})`);
                }
            });
        }

        return { dates, dateNames };
    }, [students, formatDateFull]);

    // Returns: { readBy: [studentIds], readByNames: [student names with dates], unreadBy: [studentIds], unreadByNames: [student names] }
    // Now uses studentDates from rp_book_student_dates instead of allBookListItems.date_completed
    const getBookReadingStatus = useCallback((bookId, targetStudents, allBookLists, allBookListItems, studentDates = []) => {
        if (!bookId || !targetStudents || targetStudents.length === 0) {
            return { readBy: [], readByNames: [], unreadBy: [], unreadByNames: [] };
        }

        const readBy = [];
        const readByNames = [];
        const unreadBy = [];
        const unreadByNames = [];

        for (const student of targetStudents) {
            const studentId = String(student.user_id || student);
            // Find book lists for this student
            const studentBookLists = allBookLists.filter(bl => 
                String(bl.student || '') === studentId && parseInt(bl.active) === 1
            );

            let bookRead = false;
            let readDate = null;

            // Check studentDates first (new architecture - dates stored in rp_book_student_dates)
            if (studentDates && studentDates.length > 0) {
                const bookIdStr = String(bookId);
                const bookIdNum = parseInt(bookId);

                // Find all date entries for this student-book combination
                const studentBookDates = studentDates.filter(sd => {
                    const sdStudentId = String(sd.student || '');
                    const sdBookId = String(sd.book_id || '');
                    return sdStudentId === studentId &&
                        (sdBookId === bookIdStr || parseInt(sdBookId) === bookIdNum);
                });

                // Check if any date entry has date_completed
                const completedDate = studentBookDates.find(sd => isBookRead(sd.date_completed));
                if (completedDate) {
                    bookRead = true;
                    readDate = completedDate.date_completed;
                }
            }

            // Fallback to old architecture (allBookListItems) if no dates found in studentDates
            // This ensures backward compatibility during migration
            if (!bookRead && allBookListItems && allBookListItems.length > 0) {
            for (const bookList of studentBookLists) {
                // Find book list items for this book list and book
                const items = allBookListItems.filter(bli => 
                    parseInt(bli.book_list_id) === parseInt(bookList.id) && 
                    parseInt(bli.book_id) === parseInt(bookId)
                );
                
                // Check if any item has date_completed
                const completedItem = items.find(item => isBookRead(item.date_completed));
                if (completedItem) {
                    bookRead = true;
                    readDate = completedItem.date_completed;
                    break;
                    }
                }
            }

            const studentName = student.first_name && student.last_name 
                ? `${student.first_name} ${student.last_name}` 
                : `Student ${studentId}`;

            if (bookRead) {
                readBy.push(studentId);
                const dateStr = readDate ? formatDateFull(readDate) : '';
                readByNames.push(dateStr ? `${studentName} (${dateStr})` : studentName);
            } else {
                unreadBy.push(studentId);
                unreadByNames.push(studentName);
            }
        }

        return { readBy, readByNames, unreadBy, unreadByNames };
    }, []);

    // Helper function to format date for display
    const formatDateFull = (dateStr) => {
        if (!dateStr) return '';
        try {
            // Avoid timezone shifting: treat YYYY-MM-DD as a *local* calendar date.
            // JS `new Date('YYYY-MM-DD')` is parsed as UTC and can display +/- 1 day depending on locale.
            const raw = typeof dateStr === 'string' ? dateStr.trim() : dateStr;
            const s = typeof raw === 'string' ? raw : String(raw);
            const datePart = s.length >= 10 ? s.slice(0, 10) : s;
            const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);

            let date = null;
            if (m) {
                const y = parseInt(m[1], 10);
                const mo = parseInt(m[2], 10);
                const d = parseInt(m[3], 10);
                // local time constructor (no timezone shift)
                date = new Date(y, mo - 1, d);
            } else {
                // fallback for non-ISO strings
                date = new Date(s);
            }

            if (!date || isNaN(date.getTime())) return s;
            // Date only (no weekday) across all tables
            return date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
        } catch (e) {
            return typeof dateStr === 'string' ? dateStr : String(dateStr);
        }
    };

    // Helper function to get aggregated dates for a book (similar to getBookReadingStatus)
    // Returns: { assigned: [{ student, studentName, date, book_list_id }], completed: [{ student, studentName, date, book_list_id }] }
    const getBookDates = useCallback((bookId, targetStudents, allStudentDates, allStudentsList = []) => {
        if (!bookId) {
            return { assigned: [], completed: [] };
        }

        // If no dates provided, return empty
        if (!allStudentDates || allStudentDates.length === 0) {
            return { assigned: [], completed: [] };
        }

        const assigned = [];
        const completed = [];

        // If no target students specified, show dates for ALL students who have dates for this book
        const studentsToCheck = targetStudents && targetStudents.length > 0
            ? targetStudents
            : allStudentsList; // Fall back to all students if no specific target

        // Find all date entries for this book (across all students if no target specified)
        const bookDateEntries = allStudentDates.filter(sd => {
            const sdBookId = sd.book_id;
            return parseInt(sdBookId) === parseInt(bookId) || String(sdBookId) === String(bookId);
        });

        // If target students specified, filter by those students
        // Otherwise, include all students who have dates
        const filteredEntries = targetStudents && targetStudents.length > 0
            ? bookDateEntries.filter(sd => {
                const sdStudent = sd.student || sd.student_id;
                return studentsToCheck.some(student => {
                    const studentId = student.user_id || student;
                    return parseInt(sdStudent) === parseInt(studentId) || String(sdStudent) === String(studentId);
                });
            })
            : bookDateEntries; // Show all dates if no target students

        filteredEntries.forEach(dateEntry => {
            const sdStudent = dateEntry.student || dateEntry.student_id;
            // Find student name from allStudentsList or use ID
            const student = allStudentsList.find(s =>
                parseInt(s.user_id) === parseInt(sdStudent) || String(s.user_id) === String(sdStudent)
            );
            const studentName = student && student.first_name && student.last_name
                ? `${student.first_name} ${student.last_name}`
                : `Student ${sdStudent}`;

            if (dateEntry.date_assigned) {
                const dateStr = formatDateFull(dateEntry.date_assigned);
                assigned.push({
                    student: sdStudent,
                    studentName,
                    date: dateStr,
                    dateRaw: dateEntry.date_assigned,
                    book_list_id: dateEntry.book_list_id,
                    id: dateEntry.id
                });
            }

            if (dateEntry.date_completed) {
                const dateStr = formatDateFull(dateEntry.date_completed);
                completed.push({
                    student: sdStudent,
                    studentName,
                    date: dateStr,
                    dateRaw: dateEntry.date_completed,
                    book_list_id: dateEntry.book_list_id,
                    id: dateEntry.id
                });
            }
        });

        return { assigned, completed };
    }, []);

    // Helper function to find common book lists for a group
    // Returns book lists that all students in the group have (based on matching base names)
    // Handles both exact name matches and names with student prefixes (e.g., "John Doe - Book List A")
    const findCommonBookListsForGroup = useCallback((groupStudents, allBookLists) => {
        if (!groupStudents || groupStudents.length === 0 || !allBookLists || allBookLists.length === 0) {
            return [];
        }

        // Student IDs are strings (UUIDs), not numbers - use string comparison
        const studentIds = groupStudents.map(s => String(s.user_id || s));

        // Group book lists by exact name (same name means same book list)
        // Normalize names by trimming whitespace for comparison
        const bookListsByName = {};
        allBookLists.forEach(bl => {
            // Student IDs are strings - use string comparison
            const blStudentId = String(bl.student || '');
            if (parseInt(bl.active) === 1 && studentIds.includes(blStudentId)) {
                // Use exact name (trimmed) as the key - same name means same book list
                const normalizedName = (bl.name || '').trim();
                if (normalizedName) {
                    if (!bookListsByName[normalizedName]) {
                        bookListsByName[normalizedName] = [];
                    }
                    bookListsByName[normalizedName].push(bl);
                }
            }
        });

        // Find book list names that all students have (same name across all students)
        const commonBookListNames = Object.keys(bookListsByName).filter(name => {
            const bookListsWithName = bookListsByName[name];
            const studentsWithThisBookList = new Set(bookListsWithName.map(bl => String(bl.student || '')));
            // All students must have a book list with this exact name
            return studentIds.every(id => studentsWithThisBookList.has(id));
        });

        // Return the first book list for each common name
        // Prefer a book list that likely has items (we'll verify when loading)
        const commonBookLists = commonBookListNames.map(name => {
            // Try to find a book list that was created more recently (likely to have items)
            // Sort by ID descending to get the most recently created one
            const sortedBookLists = bookListsByName[name].sort((a, b) => parseInt(b.id) - parseInt(a.id));
            const selectedBookList = sortedBookLists[0];
            return {
                ...selectedBookList,
                commonName: name, // Store the exact name for reference
                displayName: name // Use exact name for display
            };
        });

        return commonBookLists;
    }, []);

    // When group changes, auto-select all students in group
    const handleGroupChange = useCallback((e, formik) => {
        formik.handleChange(e);
        const selectedGroup = e.target.value;

        if (!selectedGroup || selectedGroup.value === -1) {
            // No group selected - show all students (filtered by center if applicable)
            const selectedCenter = formik.values.selectedCenter;
            let filteredStudents = students;
            if (selectedCenter && selectedCenter.value !== 'all') {
                filteredStudents = students.filter(s => {
                    const studentCenter = s.center_id || s.rp_primary_center || s.mp_primary_center;
                    return parseInt(studentCenter) === selectedCenter.value;
                });
            }

            const newStudentOptions = [
                { value: -1, label: 'Please select...' },
                ...filteredStudents.map(s => ({
                    value: s.user_id,
                    label: `${s.first_name} ${s.last_name}`,
                    obj: s
                }))
            ];
            setStudentOptions(newStudentOptions);
            formik.setFieldValue('selectedStudent', { value: -1, label: 'Please select...' });
            formik.setFieldValue('selectedBookList', { value: -1, label: 'Please select a student...' });
            setBookListOptions([{ value: -1, label: 'Please select a student...' }]);
            setSelectedBookListData(null);
            // Clear reading status table trigger when no group/student selected
            setSelectedStudentForReadingStatus(null);
            setSelectedGroupForReadingStatus(null);
            return;
        }

        // Get students from the group using groupMembers table
        const groupId = parseInt(selectedGroup.value);
        const membersInGroup = groupMembers.filter(gm => parseInt(gm.group_id) === groupId);

        // Get student user_ids from group members
        const memberStudentIds = membersInGroup.map(gm => gm.student || gm.user_id);

        // Filter students that belong to this group
        const groupStudents = students.filter(s => memberStudentIds.includes(s.user_id));

        // Create options with "All Students in Group" option first
        const newStudentOptions = [
            { value: -1, label: 'Please select...' },
            {
                value: 'all_group',
                label: `All Students in Group (${groupStudents.length})`,
                object: {
                    groupStudents: groupStudents.map(s => ({
                        value: s.user_id,
                        label: `${s.first_name} ${s.last_name}`,
                        obj: s
                    }))
                }
            },
            ...groupStudents.map(s => ({
                value: s.user_id,
                label: `${s.first_name} ${s.last_name}`,
                obj: s
            }))
        ];
        setStudentOptions(newStudentOptions);

        // Auto-select "All Students in Group"
        const allGroupOption = {
            value: 'all_group',
            label: `All Students in Group (${groupStudents.length})`,
            object: {
                groupStudents: groupStudents.map(s => ({
                    value: s.user_id,
                    label: `${s.first_name} ${s.last_name}`,
                    obj: s
                }))
            }
        };
        formik.setFieldValue('selectedStudent', allGroupOption);
        
        // Update reading status table trigger to refresh when group changes
        setSelectedStudentForReadingStatus(allGroupOption);
        setSelectedGroupForReadingStatus(selectedGroup);

        // Populate book list dropdown with common book lists for this group
        // Find book lists that all students in the group have
        const commonBookLists = findCommonBookListsForGroup(groupStudents, bookLists);

        const newBookListOptions = [
            { value: -1, label: 'Please select a book list...' },
            ...commonBookLists.map(bl => ({
                value: parseInt(bl.id),
                label: bl.displayName || bl.commonName || bl.name || 'Unnamed Book List',
                obj: bl,
                isTemplate: false
            }))
        ];
        setBookListOptions(newBookListOptions);
        formik.setFieldValue('selectedBookList', { value: -1, label: 'Please select a book list...' });
        setSelectedBookListData(null);
    }, [students, groupMembers, bookLists, findCommonBookListsForGroup]);

    // When student changes, load their book lists
    const handleStudentChange = useCallback(async (e, formik) => {
        formik.handleChange(e);
        const selectedStudent = e.target.value;
        
        // Update reading status table trigger
        setSelectedStudentForReadingStatus(selectedStudent);
        // Also update group if it's "all_group"
        if (selectedStudent && selectedStudent.value === 'all_group') {
            const formik = formRef.current;
            const currentGroup = formik && formik.values && formik.values.selectedGroup;
            if (currentGroup) {
                setSelectedGroupForReadingStatus(currentGroup);
            }
        } else {
            // Clear group when individual student is selected
            setSelectedGroupForReadingStatus(null);
        }

        // If individual student selected (not "all_group"), clear group selection
        if (selectedStudent && selectedStudent.value !== -1 && selectedStudent.value !== 'all_group') {
            formik.setFieldValue('selectedGroup', { value: -1, label: 'Please select...' });
        }

        if (!selectedStudent || selectedStudent.value === -1) {
            setBookListOptions([{ value: -1, label: 'Please select a book list...' }]);
            formik.setFieldValue('selectedBookList', { value: -1, label: 'Please select a book list...' });
            setSelectedBookListData(null);
            return;
        }

        // If "all_group" is selected, populate with common book lists for the group
        if (selectedStudent.value === 'all_group') {
            const selectedGroup = formik.values.selectedGroup;
            if (selectedGroup && selectedGroup.value !== -1) {
                const groupId = parseInt(selectedGroup.value);
                const membersInGroup = groupMembers.filter(gm => parseInt(gm.group_id) === groupId);
                const memberStudentIds = membersInGroup.map(gm => gm.student || gm.user_id);
                const groupStudents = students.filter(s => memberStudentIds.includes(s.user_id));
                const commonBookLists = findCommonBookListsForGroup(groupStudents, bookLists);

                const newBookListOptions = [
                    { value: -1, label: 'Please select a book list...' },
                    ...commonBookLists.map(bl => ({
                        value: parseInt(bl.id),
                        label: bl.displayName || bl.commonName || bl.name || 'Unnamed Book List',
                        obj: bl,
                        isTemplate: false
                    }))
                ];
                setBookListOptions(newBookListOptions);
                formik.setFieldValue('selectedBookList', { value: -1, label: 'Please select a book list...' });
                setSelectedBookListData(null);
            }
            return;
        }

        // For individual student, populate with their actual book lists
        // Use base name extraction to show clean names, but also show full name if different
        // Student IDs are UUIDs (strings), not numbers - use string comparison
        const studentBookLists = bookLists.filter(bl =>
            String(bl.student || '') === String(selectedStudent.value || '') &&
            parseInt(bl.active) === 1
        );

        // Sort by name for consistent display
        const sortedStudentBookLists = [...studentBookLists].sort((a, b) => {
            const nameA = (a.name || '').toLowerCase();
            const nameB = (b.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
        });

        const newBookListOptions = [
            { value: -1, label: 'Please select a book list...' },
            ...sortedStudentBookLists.map(bl => {
                const baseName = extractBaseName(bl.name || 'Unnamed Book List');
                const fullName = bl.name || 'Unnamed Book List';
                // Show base name if it's different from full name, otherwise show full name
                const displayName = baseName !== fullName ? baseName : fullName;
                return {
                value: parseInt(bl.id),
                    label: displayName,
                obj: bl,
                isTemplate: false
                };
            })
        ];
        setBookListOptions(newBookListOptions);
        formik.setFieldValue('selectedBookList', { value: -1, label: 'Please select a book list...' });
        setSelectedBookListData(null);
    }, [students, groupMembers, bookLists, findCommonBookListsForGroup]);

    // Helper function to apply template to students
    const handleApplyTemplateToStudents = useCallback(async (studentIds, templateId, templateItems) => {
        if (!studentIds || studentIds.length === 0 || !templateId || !templateItems) {
            return;
        }

        setLoading(true);
        try {
            const template = templates.find(t => parseInt(t.id) === templateId);
            if (!template) {
                setApiError('Template not found.');
                setLoading(false);
                return;
            }

            // Create book list for each student
            for (const studentId of studentIds) {
                const bookListPayload = {
                    bookList: {
                        studentId: studentId,
                        name: template.name || 'Book List',
                        notes: template.notes || '',
                        active: 1
                    },
                    bookListItems: templateItems.map((item, index) => ({
                        bookId: item.book_id,
                        sortOrder: index + 1
                    }))
                };

                await createBookList(bookListPayload);
            }

            // Refresh templates data
            const templatesRes = await fetchBookListTemplatesFull();
            const templatesData = templatesRes?.data || {};
            const newTemplates = Array.isArray(templatesData.templates) ? templatesData.templates : [];
            const newTemplateItems = Array.isArray(templatesData.templateItems) ? templatesData.templateItems : [];
            setTemplates(newTemplates);
            setTemplateItems(newTemplateItems);

            setApiError(false);
        } catch (error) {
            console.error('[BOOK LIST MANAGER] Error applying template:', error);
            setApiError('Error applying book list. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [templates, createBookList, fetchBookListTemplatesFull]);

    // When book list (template) changes, either load it for preview/edit or assign it to students
    const handleBookListChange = useCallback(async (e, formik) => {
        // Handle both event object and direct value
        const selectedBL = e?.target?.value || e;

        if (!selectedBL || selectedBL.value === -1 || selectedBL.value === undefined) {
            setSelectedBookListData(null);
            if (formik && e?.target) {
                formik.handleChange(e);
            } else if (formik) {
                formik.setFieldValue('selectedBookList', { value: -1, label: 'Please select a book list...' });
            }
            return;
        }

        // Update formik if provided
        if (formik) {
            if (e?.target) {
                formik.handleChange(e);
            } else {
                formik.setFieldValue('selectedBookList', selectedBL);
            }
        }

        // Book List dropdown should only show actual book lists (not templates)
        // Templates are only available from "Add Book List" dropdown

        // Otherwise, it's an actual book list - fetch its details
        try {
            let booksToUse = books;
            if (!booksToUse || booksToUse.length === 0) {
                const booksRes = await fetchBooksAll();
                booksToUse = getDataArray(booksRes);
                if (booksToUse.length > 0) {
                    setBooks(booksToUse);
                }
            }

            const bookListRes = await fetchBookListsId({ id: selectedBL.value });
            const bookListData = bookListRes.data?.bookLists?.[0];
            const bookListItems = bookListRes.data?.bookListItems || [];
            const studentDatesFromApi = bookListRes.data?.studentDates || [];

            console.log('[BOOK LIST MANAGER] Fetched dates from API:', {
                bookListId: selectedBL.value,
                datesCount: studentDatesFromApi.length,
                dates: studentDatesFromApi
            });

            // Also fetch dates for all book lists with the same name (shared book lists)
            let allStudentDates = [...studentDatesFromApi];
            if (bookListData && bookListData.name) {
                try {
                    // Find all book lists with the same name
                    const sameNameBookLists = bookLists.filter(bl =>
                        bl.name === bookListData.name && parseInt(bl.active) === 1
                    );

                    console.log('[BOOK LIST MANAGER] Found same-name book lists:', sameNameBookLists.length);

                    // Fetch dates for all of them
                    const datesPromises = sameNameBookLists.map(bl =>
                        fetchBookStudentDatesByBookList({ bookListId: bl.id })
                    );
                    const datesResponses = await Promise.all(datesPromises);

                    datesResponses.forEach(res => {
                        if (res && res.status < 400 && res.data) {
                            const dates = getDataArray(res);
                            console.log('[BOOK LIST MANAGER] Fetched dates from book list:', dates.length, dates);
                            allStudentDates = [...allStudentDates, ...dates];
                        }
                    });
                } catch (error) {
                    console.error('[BOOK LIST MANAGER] Error fetching dates for shared book lists:', error);
                }
            }

            console.log('[BOOK LIST MANAGER] Total dates to set:', allStudentDates.length, allStudentDates);

            // Update student dates state
            if (allStudentDates.length > 0) {
                setStudentDates(prev => {
                    // Merge with existing dates, avoiding duplicates
                    const existingMap = new Map(prev.map(sd => `${sd.student}_${sd.book_id}_${sd.book_list_id}`));
                    allStudentDates.forEach(sd => {
                        const key = `${sd.student}_${sd.book_id}_${sd.book_list_id}`;
                        existingMap.set(key, sd);
                    });
                    const mergedDates = Array.from(existingMap.values());
                    console.log('[BOOK LIST MANAGER] Setting student dates:', mergedDates.length, mergedDates);
                    return mergedDates;
                });
            } else {
                console.log('[BOOK LIST MANAGER] No dates to set, clearing student dates');
                setStudentDates([]);
            }

            if (bookListData) {
                // Deduplicate items: keep only one item per book_id and sort_order combination
                // Use a Map to track unique combinations
                const uniqueItemsMap = new Map();
                bookListItems.forEach((item) => {
                    const key = `${item.book_id}_${item.sort_order}`;
                    if (!uniqueItemsMap.has(key)) {
                        uniqueItemsMap.set(key, item);
                    }
                });
                const deduplicatedItems = Array.from(uniqueItemsMap.values());
                
                // When selecting an existing book list from "Book List" dropdown, we should KEEP all dates and completion info
                // This is just viewing/assigning an existing book list, not creating a new one
                // Only "Add Book List" dropdown (templates) creates NEW book lists where dates should be cleared
                // So for handleBookListChange, we always preserve the dates from the existing book list

                const enhancedItems = deduplicatedItems.map((item, idx) => {
                    const book = booksToUse.find(b => {
                        const bookId = b.id || b.book_id;
                        const itemBookId = item.book_id;
                        if (!bookId || !itemBookId) return false;
                        return parseInt(bookId) === parseInt(itemBookId) || String(bookId) === String(itemBookId);
                    });

                    // Always preserve dates when selecting an existing book list
                    // Convert dates to input format (yyyy-mm-dd) if they exist
                    const dateAssigned = item.date_assigned ? convertDateForInput(item.date_assigned) : null;
                    const dateCompleted = item.date_completed ? convertDateForInput(item.date_completed) : null;

                    return {
                        ...item,
                        book_id: parseInt(item.book_id),
                        bookTitle: book?.title || 'Unknown Book',
                        bookAuthor: book?.author || '',
                        date_assigned: dateAssigned,
                        date_completed: dateCompleted,
                        sort_order: parseInt(item.sort_order) || idx + 1,
                        order: idx + 1
                    };
                });

                const bookListName = bookListData.name || '';
                // CRITICAL: Preserve student ID from existing selectedBookListData if API doesn't return it
                // This ensures date fields remain editable even if API response doesn't include student
                const preservedStudent = selectedBookListData?.student || bookListData.student;
                setSelectedBookListData({
                    ...bookListData,
                    parsedItems: enhancedItems,
                    id: bookListData.id || selectedBL.value,
                    isTemplate: false,
                    student: preservedStudent || bookListData.student || null // Always preserve student ID
                });
                // Store original name for change detection
                setOriginalBookListName(bookListName);
            } else {
                setSelectedBookListData(null);
            }
        } catch (error) {
            console.error('[BOOK LIST MANAGER] Error fetching book list details:', error);
            setSelectedBookListData(null);
            setApiError('Error loading book list. Please try again.');
        }
    }, [fetchBookListsId, fetchBooksAll, books, templates, templateItems, students, groupMembers, handleApplyTemplateToStudents, convertDateForInput]);

    const refreshData = useCallback(() => {
        (async function refresh() {
            if (loading) return;
            if (mounted.current) setLoading(true);

            // PRESERVE CURRENT STATE before refresh
            const formik = formRef.current;
            const preservedSelectedCenter = formik?.values?.selectedCenter;
            const preservedSelectedGroup = formik?.values?.selectedGroup;
            const preservedSelectedStudent = formik?.values?.selectedStudent;
            const preservedSelectedBookList = formik?.values?.selectedBookList;
            const preservedBookListId = selectedBookListData?.id;

            try {
                const [booksRes, groupsRes, studentsRes, centersRes, bookListsRes, templatesRes, bookListItemsRes] = await Promise.all([
                    fetchBooksAll(),
                    fetchCycleGroupsAll(),
                    fetchStudentsAll(),
                    fetchRpCentersAll(),
                    fetchBookListsAll(),
                    fetchBookListTemplatesFull(),
                    fetchBookListItemsAll()
                ]);
                const isApiError = checkResponses(booksRes, studentsRes, centersRes, bookListsRes, bookListItemsRes);
                if (isApiError) {
                    if (mounted.current) {
                        setApiError('Error fetching data from the server. Please try again later.');
                        setLoading(false);
                        setHasLoaded(true);
                    }
                    return;
                } else setApiError(false);

                const newBooks = getDataArray(booksRes);

                // Extract groups and groupMembers from response (same structure as CycleGroups page)
                const groupsData = groupsRes.data || {};
                const newGroups = Array.isArray(groupsData.groups) ? groupsData.groups : getDataArray(groupsRes);
                const newGroupMembers = Array.isArray(groupsData.groupMembers) ? groupsData.groupMembers : [];

                const newStudents = getDataArray(studentsRes);
                const newCenters = getDataArray(centersRes);
                const newBookLists = getDataArray(bookListsRes);
                
                // Extract book list items from separate endpoint
                const newBookListItems = getDataArray(bookListItemsRes);
                setAllBookListItems(newBookListItems);

                // Process templates
                const templatesData = templatesRes?.data || {};
                const newTemplates = Array.isArray(templatesData.templates) ? templatesData.templates : [];
                const newTemplateItems = Array.isArray(templatesData.templateItems) ? templatesData.templateItems : [];

                // Build template options (only active templates)
                const activeTemplates = newTemplates.filter(t => parseInt(t.active) === 1);
                const newTemplateOptions = [
                    { value: -1, label: 'Please select...' },
                    { value: 'new', label: '+ New Book List' },
                    ...activeTemplates.map(t => ({
                        value: parseInt(t.id),
                        label: t.name || 'Unnamed Book List',
                        obj: t
                    }))
                ];

                // Build center options
                const newCenterOptions = [
                    { value: 'all', label: 'All' },
                    ...newCenters.map(c => ({ value: parseInt(c.id), label: c.name }))
                ];

                // Build initial group options (all groups)
                const newGroupOptions = [
                    { value: -1, label: 'Please select...' },
                    ...newGroups.map(g => ({
                        value: parseInt(g.id),
                        label: g.name,
                        obj: g
                    }))
                ];

                // Build initial student options (all students - for individual selection)
                const newStudentOptions = [
                    { value: -1, label: 'Please select...' },
                    ...newStudents.map(s => ({
                        value: s.user_id,
                        label: `${s.first_name} ${s.last_name}`,
                        obj: s
                    }))
                ];

                if (mounted.current) {
                    setBooks(newBooks);
                    setGroups(newGroups);
                    setGroupMembers(newGroupMembers);
                    setStudents(newStudents);
                    setCenters(newCenters);
                    setBookLists(newBookLists);
                    setTemplates(newTemplates);
                    setTemplateItems(newTemplateItems);
                    setCenterOptions(newCenterOptions);
                    setGroupOptions(newGroupOptions);
                    setStudentOptions(newStudentOptions);
                    setTemplateOptions(newTemplateOptions);

                    setHasLoaded(true);
                    setLoading(false);

                    // RESTORE PRESERVED FORMIK VALUES - Defer to next tick to ensure React has processed state updates
                    setTimeout(() => {
                        if (!mounted.current || !formRef.current) return;
                        const currentFormik = formRef.current;
                        
                        // Restore center if it still exists
                        if (preservedSelectedCenter) {
                            const centerStillExists = newCenterOptions.find(c => c.value === preservedSelectedCenter.value);
                            if (centerStillExists) {
                                currentFormik.setFieldValue('selectedCenter', preservedSelectedCenter);
                            } else {
                                currentFormik.setFieldValue('selectedCenter', { value: 'all', label: 'All' });
                            }
                        }

                        // Restore group if it still exists
                        if (preservedSelectedGroup && preservedSelectedGroup.value !== -1) {
                            const groupStillExists = newGroupOptions.find(g => g.value === preservedSelectedGroup.value);
                            if (groupStillExists) {
                                currentFormik.setFieldValue('selectedGroup', preservedSelectedGroup);
                                setSelectedGroupForReadingStatus(preservedSelectedGroup);
                            }
                        }

                        // Restore student if it still exists - this will trigger book list options to be built
                        if (preservedSelectedStudent && preservedSelectedStudent.value !== -1) {
                            const studentStillExists = newStudentOptions.find(s => s.value === preservedSelectedStudent.value);
                            if (studentStillExists) {
                                // For "all_group", update book list options using new book lists immediately
                                if (preservedSelectedStudent.value === 'all_group' && preservedSelectedGroup && preservedSelectedGroup.value !== -1) {
                                    const groupId = parseInt(preservedSelectedGroup.value);
                                    const membersInGroup = newGroupMembers.filter(gm => parseInt(gm.group_id) === groupId);
                                    const memberStudentIds = membersInGroup.map(gm => gm.student || gm.user_id);
                                    const groupStudents = newStudents.filter(s => memberStudentIds.includes(s.user_id));
                                    const commonBookLists = findCommonBookListsForGroup(groupStudents, newBookLists);
                                    
                                    const newBookListOptions = [
                                        { value: -1, label: 'Please select a book list...' },
                                        ...commonBookLists.map(bl => ({
                                            value: parseInt(bl.id),
                                            label: bl.displayName || bl.commonName || bl.name || 'Unnamed Book List',
                                            obj: bl,
                                            isTemplate: false
                                        }))
                                    ];
                                    setBookListOptions(newBookListOptions);
                                }
                                
                                // Simulate onChange event to trigger handleStudentChange which builds book list options
                                const simulatedEvent = { target: { value: preservedSelectedStudent } };
                                handleStudentChange(simulatedEvent, currentFormik).then(() => {
                                    // After student is restored and book list options are built, restore book list
                                    if (!mounted.current || !formRef.current) return;
                                    
                                    // Restore book list if it still exists and was previously selected
                                    if (preservedSelectedBookList && preservedSelectedBookList.value !== -1 && preservedBookListId) {
                                        // When restoring for individual student, find book list by name for that student
                                        // (not by ID, because each student has their own copy with same name)
                                        const currentStudentId = preservedSelectedStudent.value;
                                        let bookListToRestore = null;

                                        if (currentStudentId && currentStudentId !== 'all_group' && currentStudentId !== -1) {
                                            // Individual student: find book list by name for this specific student
                                            // Get the original book list name from preserved data
                                            const preservedBookListObj = preservedSelectedBookList.obj;
                                            const preservedBookListName = preservedBookListObj?.name || selectedBookListData?.name || preservedSelectedBookList.label;

                                            // CRITICAL: Student IDs are UUIDs (strings), not numbers - use string comparison
                                            bookListToRestore = newBookLists.find(bl =>
                                                String(bl.student || '') === String(currentStudentId || '') &&
                                                bl.name === preservedBookListName &&
                                                parseInt(bl.active) === 1
                                            );
                                        } else {
                                            // Group view: find by ID
                                            bookListToRestore = newBookLists.find(bl => parseInt(bl.id) === preservedBookListId);
                                        }

                                        if (bookListToRestore) {
                                            // Re-fetch the book list to get latest data
                                            (async () => {
                                                try {
                                                    const bookListRes = await fetchBookListsId({ id: bookListToRestore.id });
                                                    const bookListData = bookListRes.data?.bookLists?.[0];
                                                    const bookListItems = bookListRes.data?.bookListItems || [];
                                                    
                                                    if (bookListData && bookListItems.length > 0 && mounted.current) {
                                                        // Deduplicate items
                                                        const uniqueItemsMap = new Map();
                                                        bookListItems.forEach((item) => {
                                                            const key = `${item.book_id}_${item.sort_order}`;
                                                            if (!uniqueItemsMap.has(key)) {
                                                                uniqueItemsMap.set(key, item);
                                                            }
                                                        });
                                                        const deduplicatedItems = Array.from(uniqueItemsMap.values());
                                                        
                                                        // Enhance items with book data
                                                        const enhancedItems = deduplicatedItems.map((item, idx) => {
                                                            const book = newBooks.find(b => {
                                                                const bookId = b.id || b.book_id;
                                                                const itemBookId = item.book_id;
                                                                if (!bookId || !itemBookId) return false;
                                                                return parseInt(bookId) === parseInt(itemBookId);
                                                            });
                                                            
                                                            return {
                                                                ...item,
                                                                bookTitle: book?.title || 'Unknown Book',
                                                                bookAuthor: book?.author || '',
                                                                order: item.sort_order || idx + 1,
                                                                sort_order: item.sort_order || idx + 1
                                                            };
                                                        });
                                                        
                                                        // Update selected book list data
                                                        setSelectedBookListData({
                                                            ...bookListData,
                                                            parsedItems: enhancedItems,
                                                            id: bookListData.id,
                                                            isTemplate: false
                                                        });
                                                        
                                                        // Restore book list selection in formik - wait a bit more for book list options to be built
                                                        setTimeout(() => {
                                                            if (!mounted.current || !formRef.current) return;
                                                            
                                                            const restoredBookListOption = {
                                                                value: parseInt(bookListData.id),
                                                                label: extractBaseName(bookListData.name || 'Unnamed Book List'),
                                                                obj: bookListData,
                                                                isTemplate: false
                                                            };
                                                            formRef.current.setFieldValue('selectedBookList', restoredBookListOption);
                                                        }, 50);
                                                    } else if (bookListData && bookListItems.length === 0) {
                                                        // Book list exists but has no items - this shouldn't happen, but handle it gracefully
                                                        console.warn('[BOOK LIST MANAGER] Book list restored but has no items:', bookListData.id);
                                                        setSelectedBookListData({
                                                            ...bookListData,
                                                            parsedItems: [],
                                                            id: bookListData.id,
                                                            isTemplate: false
                                                        });
                                                    }
                                                } catch (restoreError) {
                                                    console.error('[BOOK LIST MANAGER] Error restoring book list after refresh:', restoreError);
                                                    // If restore fails, at least preserve the selection value
                                                    if (mounted.current && formRef.current) {
                                                        formRef.current.setFieldValue('selectedBookList', preservedSelectedBookList);
                                                    }
                                                }
                                            })();
                                        } else {
                                            console.warn('[BOOK LIST MANAGER] Book list not found for restoration:', {
                                                preservedBookListId,
                                                currentStudentId,
                                                preservedBookListName: preservedSelectedBookList.obj?.name || preservedSelectedBookList.label
                                            });
                                        }
                                    }
                                });
                            }
                        } else {
                            // No student to restore, but might still need to restore book list if group was selected
                            if (preservedSelectedGroup && preservedSelectedGroup.value !== -1) {
                                const simulatedEvent = { target: { value: { value: 'all_group', label: 'All Students in Group' } } };
                                handleStudentChange(simulatedEvent, currentFormik).then(() => {
                                    // Similar book list restoration logic for group case
                                    if (preservedSelectedBookList && preservedSelectedBookList.value !== -1 && preservedBookListId) {
                                        const bookListStillExists = newBookLists.find(bl => parseInt(bl.id) === preservedBookListId);
                                        if (bookListStillExists) {
                                            // Re-fetch and restore book list (same logic as above)
                                            (async () => {
                                                try {
                                                    const bookListRes = await fetchBookListsId({ id: preservedBookListId });
                                                    const bookListData = bookListRes.data?.bookLists?.[0];
                                                    const bookListItems = bookListRes.data?.bookListItems || [];
                                                    
                                                    if (bookListData && mounted.current) {
                                                        const uniqueItemsMap = new Map();
                                                        bookListItems.forEach((item) => {
                                                            const key = `${item.book_id}_${item.sort_order}`;
                                                            if (!uniqueItemsMap.has(key)) {
                                                                uniqueItemsMap.set(key, item);
                                                            }
                                                        });
                                                        const deduplicatedItems = Array.from(uniqueItemsMap.values());
                                                        
                                                        const enhancedItems = deduplicatedItems.map((item, idx) => {
                                                            const book = newBooks.find(b => {
                                                                const bookId = b.id || b.book_id;
                                                                const itemBookId = item.book_id;
                                                                if (!bookId || !itemBookId) return false;
                                                                return parseInt(bookId) === parseInt(itemBookId);
                                                            });
                                                            
                                                            return {
                                                                ...item,
                                                                bookTitle: book?.title || 'Unknown Book',
                                                                bookAuthor: book?.author || '',
                                                                order: item.sort_order || idx + 1,
                                                                sort_order: item.sort_order || idx + 1
                                                            };
                                                        });
                                                        
                                                        setSelectedBookListData({
                                                            ...bookListData,
                                                            parsedItems: enhancedItems,
                                                            id: bookListData.id,
                                                            isTemplate: false
                                                        });
                                                        
                                                        setTimeout(() => {
                                                            if (!mounted.current || !formRef.current) return;
                                                            const restoredBookListOption = {
                                                                value: parseInt(bookListData.id),
                                                                label: extractBaseName(bookListData.name || 'Unnamed Book List'),
                                                                obj: bookListData,
                                                                isTemplate: false
                                                            };
                                                            formRef.current.setFieldValue('selectedBookList', restoredBookListOption);
                                                        }, 50);
                                                    }
                                                } catch (restoreError) {
                                                    console.error('[BOOK LIST MANAGER] Error restoring book list after refresh:', restoreError);
                                                    if (mounted.current && formRef.current) {
                                                        formRef.current.setFieldValue('selectedBookList', preservedSelectedBookList);
                                                    }
                                                }
                                            })();
                                        }
                                    }
                                });
                            }
                        }
                    }, 0);
                }
            } catch (error) {
                console.error('[BOOK LIST MANAGER] Error:', error);
                if (mounted.current) {
                    setApiError('Error fetching data. Please try again later.');
                    setLoading(false);
                    setHasLoaded(true);
                }
            }
        })();
    }, [mounted, loading, fetchBooksAll, fetchCycleGroupsAll, fetchStudentsAll, fetchRpCentersAll, fetchBookListsAll, fetchBookListTemplatesFull, fetchBookListItemsAll, fetchBookListsId, selectedBookListData, handleStudentChange, extractBaseName, groupMembers, students, bookLists, findCommonBookListsForGroup]);

    useEffect(() => {
        refreshData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [isSavingBookList, setIsSavingBookList] = useState(false);

    const normalizeDateForApi = (value) => {
        if (value === undefined || value === null) return null;
        if (value instanceof Date && !Number.isNaN(value.getTime())) {
            // Backend expects YYYY-MM-DD (same as BookListModal)
            return formatDateApi(value);
        }
        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (trimmed === '' || trimmed.toLowerCase() === 'null') return null;
            // If API gave us a full timestamp, keep only YYYY-MM-DD (same as BookListModal)
            if (trimmed.length >= 10) return trimmed.slice(0, 10);
            return trimmed;
        }
        return value;
    };

    const buildPayloadSignature = (payload) => {
        try {
            const bl = payload?.bookList || {};
            const items = Array.isArray(payload?.bookListItems) ? payload.bookListItems : [];
            // Keep signature stable and reasonably small
            const sig = {
                bookList: {
                    id: bl.id,
                    studentId: bl.studentId,
                    name: bl.name,
                    notes: bl.notes,
                    active: bl.active
                },
                bookListItems: items.map(i => ({
                    bookId: i.bookId,
                    sortOrder: i.sortOrder,
                    dateAssigned: i.dateAssigned,
                    dateCompleted: i.dateCompleted
                }))
            };
            return JSON.stringify(sig);
        } catch (e) {
            return null;
        }
    };

    // Serialize ALL updateBookList calls to avoid backend races (two PUTs landing at the same time)
    const updateBookListQueued = useCallback(async (payload, context = 'unknown') => {
        const run = async () => {
            try {
                setIsSavingBookList(true);
                return await props.updateBookList(payload);
            } finally {
                setIsSavingBookList(false);
            }
        };

        const p = saveQueueRef.current.then(run, run);
        // keep queue chain alive even if one run fails
        saveQueueRef.current = p.catch(() => { });
        return p;
    }, [props.updateBookList]);

    // Helper function to build payload for updateBookList
    const buildUpdatePayload = (items) => {
        const validItems = (items || []).filter(i => i && i.book_id !== null && i.book_id !== undefined);
        // Deduplicate by bookId to avoid backend unique constraint failures
        const uniqueByBookId = new Map();
        for (const i of validItems) {
            const id = parseInt(i.book_id);
            if (!Number.isFinite(id)) continue;
            if (!uniqueByBookId.has(id)) uniqueByBookId.set(id, i);
        }
        const dedupedItems = Array.from(uniqueByBookId.values());

        // Always use array index for sortOrder to preserve the *current* visual order
        // Dates are now stored separately in rp_book_student_dates, so don't include them here
        const bookListItems = dedupedItems.map((i, idx) => ({
            bookId: parseInt(i.book_id),
            dateAssigned: null, // Dates stored separately in rp_book_student_dates
            dateCompleted: null, // Dates stored separately in rp_book_student_dates
            sortOrder: idx + 1
        }));

        // Ensure ID is a number
        const bookListId = selectedBookListData.id ? parseInt(selectedBookListData.id) : null;
        if (!bookListId || isNaN(bookListId)) {
            console.error('[BOOK LIST MANAGER] Invalid book list ID:', selectedBookListData.id);
            throw new Error('Invalid book list ID');
        }

        return {
            bookList: {
                id: bookListId,
                studentId: selectedBookListData.student,
                name: selectedBookListData.name || 'Book List',
                notes: selectedBookListData.notes || '',
                active: selectedBookListData.active !== undefined ? selectedBookListData.active : 1
            },
            bookListItems: bookListItems
        };
    };

    // Handle marking a book as complete
    const handleMarkComplete = async (item, formik) => {
        if (!selectedBookListData || !selectedBookListData.parsedItems) {
            console.error('[BOOK LIST MANAGER] Cannot mark complete: no book list data');
            return;
        }

        const today = formatDateApi(new Date());
        const updatedItems = selectedBookListData.parsedItems.map(i => {
            if (i.book_id === item.book_id) {
                return { ...i, date_completed: today };
            }
            return i;
        });

        try {
            if (!selectedBookListData || !selectedBookListData.id) {
                console.error('[BOOK LIST MANAGER] Cannot mark complete: no book list ID');
                return;
            }
            
            const payload = buildUpdatePayload(updatedItems);
            const response = await updateBookListQueued(payload, 'mark_complete');
            if (!response || response.status >= 400) {
                setApiError(response?.data?.message || 'Error updating book. Please try again.');
                return;
            }
            
            setSelectedBookListData({ ...selectedBookListData, parsedItems: updatedItems });
            await refreshData();
        } catch (error) {
            console.error('[BOOK LIST MANAGER] Error updating book:', error);
            setApiError('Error updating book. Please try again.');
        }
    };

    // Handle moving book up/down
    const handleMoveBook = async (item, direction) => {
        if (!selectedBookListData || !selectedBookListData.parsedItems) {
            console.error('[BOOK LIST MANAGER] Cannot move book: no book list data');
            return;
        }

        const items = [...selectedBookListData.parsedItems];
        const currentIndex = items.findIndex(i => i.book_id === item.book_id);

        if (currentIndex === -1) {
            console.error('[BOOK LIST MANAGER] Cannot move book: item not found');
            return;
        }

        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

        if (newIndex < 0 || newIndex >= items.length) return;

        // Swap items
        [items[currentIndex], items[newIndex]] = [items[newIndex], items[currentIndex]];

        // Update order numbers
        items.forEach((i, idx) => {
            i.order = idx + 1;
            i.sort_order = idx + 1;
        });

        // If this is a new book list (from template) without ID, just update local state (no API call until Save)
        if (selectedBookListData.templateId && !selectedBookListData.id) {
            setSelectedBookListData({
                ...selectedBookListData,
                parsedItems: items
            });
            return;
        }

        try {
            if (!selectedBookListData || !selectedBookListData.id) {
                console.error('[BOOK LIST MANAGER] Cannot move book: no book list ID');
                return;
            }
            
            const payload = buildUpdatePayload(items);
            const response = await updateBookListQueued(payload, 'move_book');
            if (!response || response.status >= 400) {
                setApiError(response?.data?.message || 'Error moving book. Please try again.');
                return;
            }
            
            setSelectedBookListData({ ...selectedBookListData, parsedItems: items });
            await refreshData();
        } catch (error) {
            console.error('[BOOK LIST MANAGER] Error reordering books:', error);
            setApiError('Error moving book. Please try again.');
        }
    };

    // Handle removing a book
    const handleRemoveBook = async (item) => {
        if (!selectedBookListData || !selectedBookListData.parsedItems) {
            console.error('[BOOK LIST MANAGER] Cannot remove book: no book list data');
            return;
        }

        const updatedItems = selectedBookListData.parsedItems.filter(i => i.book_id !== item.book_id);
        updatedItems.forEach((i, idx) => {
            i.order = idx + 1;
            i.sort_order = idx + 1;
        });

        // For new book lists (from templates) without ID, just update local state
        if (selectedBookListData.templateId && !selectedBookListData.id) {
            setSelectedBookListData({ ...selectedBookListData, parsedItems: updatedItems });
            return;
        }

        try {
            if (!selectedBookListData || !selectedBookListData.id) {
                console.error('[BOOK LIST MANAGER] Cannot remove book: no book list ID');
                return;
            }

            const payload = buildUpdatePayload(updatedItems);
            const response = await updateBookListQueued(payload, 'remove_book');
            if (!response || response.status >= 400) {
                setApiError(response?.data?.message || 'Error removing book. Please try again.');
                return;
            }

            setSelectedBookListData({ ...selectedBookListData, parsedItems: updatedItems });
            await refreshData();
        } catch (error) {
            console.error('[BOOK LIST MANAGER] Error removing book:', error);
            setApiError('Error removing book. Please try again.');
        }
    };

    // Handle drag and drop reordering
    const handleDragStart = (e, index) => {
        setDraggedItem(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', e.target);
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDraggedOverIndex(index);
    };

    const handleDragEnd = () => {
        setDraggedItem(null);
        setDraggedOverIndex(null);
    };

    const handleDrop = async (e, dropIndex) => {
        e.preventDefault();
        if (draggedItem === null || draggedItem === dropIndex) {
            setDraggedItem(null);
            setDraggedOverIndex(null);
            return;
        }

        if (!selectedBookListData || !selectedBookListData.parsedItems) {
            setDraggedItem(null);
            setDraggedOverIndex(null);
            return;
        }

        const items = [...selectedBookListData.parsedItems];
        const draggedItemData = items[draggedItem];

        // Remove dragged item
        items.splice(draggedItem, 1);
        // Insert at new position
        items.splice(dropIndex, 0, draggedItemData);

        // Update order numbers
        items.forEach((i, idx) => {
            i.order = idx + 1;
            i.sort_order = idx + 1;
        });

        // If this is a new book list (from template) without ID, just update local state (no API call until Save)
        if (selectedBookListData.templateId && !selectedBookListData.id) {
            setSelectedBookListData({
                ...selectedBookListData,
                parsedItems: items
            });
            setDraggedItem(null);
            setDraggedOverIndex(null);
            return;
        }

        if (!selectedBookListData.id) {
            setDraggedItem(null);
            setDraggedOverIndex(null);
            return;
        }

        try {
            const payload = buildUpdatePayload(items);
            const response = await updateBookList(payload);
            if (!response || response.status >= 400) {
                setApiError(response?.data?.message || 'Error reordering books. Please try again.');
                setDraggedItem(null);
                setDraggedOverIndex(null);
                return;
            }
            
            setSelectedBookListData({ ...selectedBookListData, parsedItems: items });
            await refreshData();
        } catch (error) {
            console.error('[BOOK LIST MANAGER] Error reordering books:', error);
            setApiError('Error reordering books. Please try again.');
        }

        setDraggedItem(null);
        setDraggedOverIndex(null);
    };

    // Handle date editing - saves to rp_book_student_dates (student-book level)
    const handleDateChange = async (bookId, field, newDate) => {
        console.log('[BOOK LIST MANAGER] handleDateChange entry:', {
            bookId,
            field,
            newDate,
            hasSelectedBookListData: !!selectedBookListData,
            hasParsedItems: !!selectedBookListData?.parsedItems,
            selectedBookListDataKeys: selectedBookListData ? Object.keys(selectedBookListData) : []
        });

        if (!selectedBookListData || !selectedBookListData.parsedItems) {
            console.error('[BOOK LIST MANAGER] handleDateChange early return - missing selectedBookListData or parsedItems');
            return;
        }

        // For new book lists (from templates) without ID, update local state only
        if (selectedBookListData.templateId && !selectedBookListData.id) {
            const updatedItems = selectedBookListData.parsedItems.map(i => {
                if (i.book_id === bookId) {
                    return { ...i, [field]: newDate || null };
                }
                return i;
            });
            setSelectedBookListData({ ...selectedBookListData, parsedItems: updatedItems });
            setEditingDate(null);
            return;
        }

        if (!selectedBookListData.id) {
            console.error('[BOOK LIST MANAGER] Cannot save date: book list has no ID. Please save the book list first.');
            // Don't show error message for auto-save operations - just throw silently
            // Error messages will only show when main Save button is clicked
            throw new Error('Book list has no ID. Please save the book list first.');
        }

        try {
            const formik = formRef.current;
            const selectedStudent = formik?.values?.selectedStudent;
            const isGroup = selectedStudent && selectedStudent.value === 'all_group';
            const selectedGroup = formik?.values?.selectedGroup;

            console.log('[BOOK LIST MANAGER] handleDateChange called:', {
                bookId,
                field,
                newDate,
                selectedStudent: selectedStudent?.value,
                isGroup,
                selectedGroup: selectedGroup?.value,
                bookListName: selectedBookListData?.name,
                bookListId: selectedBookListData?.id
            });

            // Determine target students
            let targetStudents = [];
            if (isGroup && selectedGroup && selectedGroup.value !== -1) {
                const groupId = parseInt(selectedGroup.value);
                const membersInGroup = groupMembers.filter(gm => parseInt(gm.group_id) === groupId);
                const memberStudentIds = membersInGroup.map(gm => gm.student || gm.user_id);
                targetStudents = students.filter(s => memberStudentIds.includes(s.user_id));
                console.log('[BOOK LIST MANAGER] Group selected:', { groupId, membersInGroup: membersInGroup.length, targetStudents: targetStudents.length });
            } else if (selectedStudent && selectedStudent.value !== -1 && selectedStudent.value !== 'all_group') {
                // Student IDs are UUIDs (strings), not numbers - use string comparison
                const student = students.find(s => String(s.user_id || '') === String(selectedStudent.value || ''));
                if (student) targetStudents = [student];
                console.log('[BOOK LIST MANAGER] Individual student selected:', { studentId: selectedStudent.value, found: !!student });
            }

            if (targetStudents.length === 0) {
                console.error('[BOOK LIST MANAGER] Cannot update date: no target students', {
                    selectedStudent: selectedStudent?.value,
                    isGroup,
                    selectedGroup: selectedGroup?.value,
                    studentsCount: students.length,
                    groupMembersCount: groupMembers.length
                });
                // Don't show error message for auto-save operations - just throw silently
                // Error messages will only show when main Save button is clicked
                throw new Error('No target students selected. Please select a student or group first.');
            }

            // For group book lists, find all book lists with the same name for each student
            // For individual student, use the selected book list
            const bookListName = selectedBookListData.name;
            // Normalize book list name - remove group prefix if present (e.g., "900909 Suin, Jamie, Hailey Book List A" -> "Suin, Jamie, Hailey Book List A")
            const normalizedBookListName = bookListName.replace(/^\d+\s+/, '').trim();
            console.log('[BOOK LIST MANAGER] Book list name normalization:', { original: bookListName, normalized: normalizedBookListName });

            const savePromises = [];

            for (const student of targetStudents) {
                // Find the book list for this student with the same name (try both original and normalized)
                // Student IDs are UUIDs (strings), not numbers - use string comparison
                let studentBookList = bookLists.find(bl =>
                    String(bl.student || '') === String(student.user_id || '') &&
                    (bl.name === bookListName || bl.name === normalizedBookListName) &&
                    parseInt(bl.active) === 1
                );

                // If still not found, try matching by removing any numeric prefix from both names
                if (!studentBookList) {
                    const studentBookLists = bookLists.filter(bl =>
                        String(bl.student || '') === String(student.user_id || '') &&
                        parseInt(bl.active) === 1
                    );
                    studentBookList = studentBookLists.find(bl => {
                        const blNameNormalized = bl.name.replace(/^\d+\s+/, '').trim();
                        return blNameNormalized === normalizedBookListName || blNameNormalized === bookListName.replace(/^\d+\s+/, '').trim();
                    });
                }

                if (!studentBookList) {
                    console.warn(`[BOOK LIST MANAGER] No book list found for student ${student.user_id} (${student.first_name} ${student.last_name}) with name "${bookListName}" or "${normalizedBookListName}"`, {
                        availableBookLists: bookLists.filter(bl => String(bl.student || '') === String(student.user_id || '')).map(bl => bl.name)
                    });
                    continue;
                }

                console.log(`[BOOK LIST MANAGER] Found book list for student ${student.user_id}:`, { bookListId: studentBookList.id, name: studentBookList.name });

                // Get existing date for this student-book-book_list combination
                const existingDates = studentDates.filter(sd =>
                    String(sd.student) === String(student.user_id) &&
                    parseInt(sd.book_id) === parseInt(bookId) &&
                    parseInt(sd.book_list_id) === parseInt(studentBookList.id)
                );

                const dateEntry = {
                    student: student.user_id,
                    bookId: parseInt(bookId),
                    bookListId: parseInt(studentBookList.id),
                    dateAssigned: field === 'date_assigned' ? normalizeDateForApi(newDate) : null,
                    dateCompleted: field === 'date_completed' ? normalizeDateForApi(newDate) : null
                };

                // If updating one field, preserve the other field from existing dates
                if (existingDates.length > 0) {
                    const existing = existingDates[0];
                    if (field === 'date_assigned') {
                        dateEntry.dateCompleted = existing.date_completed ? normalizeDateForApi(existing.date_completed) : null;
                    } else {
                        dateEntry.dateAssigned = existing.date_assigned ? normalizeDateForApi(existing.date_assigned) : null;
                    }
                }

                console.log('[BOOK LIST MANAGER] Saving date entry:', dateEntry);
                const response = await saveBookStudentDate({ dateEntry });
            if (!response || response.status >= 400) {
                    console.error('[BOOK LIST MANAGER] Failed to save date:', { response, student: `${student.first_name} ${student.last_name}` });
                    throw new Error(response?.data?.message || `Failed to save date for ${student.first_name} ${student.last_name}`);
                }
                console.log('[BOOK LIST MANAGER] Date saved successfully for:', `${student.first_name} ${student.last_name}`);
                savePromises.push(response);
            }

            if (savePromises.length === 0) {
                console.error('[BOOK LIST MANAGER] No dates were saved - no matching book lists found for any students');
                // Don't show error message for auto-save operations - just log silently
                // Error messages will only show when main Save button is clicked
                // Don't clear editing state - let user see the date and try again
                return;
            }
            
            await Promise.all(savePromises);
            console.log('[BOOK LIST MANAGER] All dates saved successfully');

            // Clear editing state after successful save - this will be called from handleDateChange
            // The onBlur handler will also clear it, but this ensures it's cleared even if onBlur doesn't fire

            // Refresh student dates - fetch for all book lists with the same name
            if (selectedBookListData && selectedBookListData.name) {
                try {
                    // Find all book lists with the same name
                    const sameNameBookLists = bookLists.filter(bl =>
                        bl.name === selectedBookListData.name && parseInt(bl.active) === 1
                    );

                    // Fetch dates for all of them
                    const datesPromises = sameNameBookLists.map(bl =>
                        fetchBookStudentDatesByBookList({ bookListId: bl.id })
                    );
                    const datesResponses = await Promise.all(datesPromises);

                    let allNewDates = [];
                    datesResponses.forEach(res => {
                        if (res && res.status < 400 && res.data) {
                            const dates = getDataArray(res);
                            allNewDates = [...allNewDates, ...dates];
                        }
                    });

                    // Update student dates state
                    if (allNewDates.length > 0) {
                        setStudentDates(prev => {
                            // Merge with existing, avoiding duplicates
                            const existingMap = new Map(prev.map(sd => `${sd.student}_${sd.book_id}_${sd.book_list_id}`));
                            allNewDates.forEach(sd => {
                                const key = `${sd.student}_${sd.book_id}_${sd.book_list_id}`;
                                existingMap.set(key, sd);
                            });
                            return Array.from(existingMap.values());
                        });
                    }
                } catch (error) {
                    console.error('[BOOK LIST MANAGER] Error refreshing dates after save:', error);
                }
            }

            // Store pending saved date BEFORE updating state - this ensures it's available even if editingDate is cleared
            const pendingKey = `${bookId}_${field}`;
            if (newDate) {
                pendingSavedDatesRef.current.set(pendingKey, {
                    date: newDate,
                    timestamp: Date.now(),
                    bookId: bookId,
                    field: field
                });
            } else {
                // If clearing the date, mark it as cleared
                pendingSavedDatesRef.current.set(pendingKey, {
                    date: null,
                    timestamp: Date.now(),
                    bookId: bookId,
                    field: field
                });
            }

            // Update student dates state IMMEDIATELY with the new date (optimistic update)
            // This ensures the date appears in the UI right away
            // We use a functional update to ensure we're working with the latest state
            setStudentDates(prev => {
                const updated = [...prev];
                targetStudents.forEach(student => {
                    const studentId = String(student.user_id || student);
                    const bookIdStr = String(bookId);

                    // Find existing date entry for this student-book-book_list combination
                    const existingIndex = updated.findIndex(sd =>
                        String(sd.student) === studentId &&
                        String(sd.book_id) === bookIdStr &&
                        String(sd.book_list_id) === String(selectedBookListData.id)
                    );

                    if (field === 'date_assigned') {
                        if (existingIndex >= 0) {
                            // Update existing entry
                            updated[existingIndex] = {
                                ...updated[existingIndex],
                                date_assigned: newDate || null
                            };
                        } else if (newDate) {
                            // Create new entry
                            updated.push({
                                student: studentId,
                                book_id: bookIdStr,
                                book_list_id: String(selectedBookListData.id),
                                date_assigned: newDate,
                                date_completed: null
                            });
                        }
                    } else if (field === 'date_completed') {
                        if (existingIndex >= 0) {
                            // Update existing entry
                            updated[existingIndex] = {
                                ...updated[existingIndex],
                                date_completed: newDate || null
                            };
                        } else if (newDate) {
                            // Create new entry
                            updated.push({
                                student: studentId,
                                book_id: bookIdStr,
                                book_list_id: String(selectedBookListData.id),
                                date_assigned: null,
                                date_completed: newDate
                            });
                        }
                    }
                });
                return updated;
            });

            // DON'T clear editing state immediately - wait for the optimistic update to be reflected in the DOM
            // Use multiple requestAnimationFrame calls and a setTimeout to ensure the state update is fully processed and rendered
            // This prevents the date from disappearing when clicking on another field
            // The key is to wait long enough for React to process the state update and re-render
            // We use a longer delay (300ms) to ensure the optimistic update has been fully applied
            // BUT: Don't clear editing state if buttons are being used (they need to stay visible)
            // Only clear on blur or when user explicitly moves to another field
            // The buttons will keep editingDate active, so we only clear if it's a blur event
            // For button clicks, editingDate is set again immediately, so it won't be cleared
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    // Double RAF ensures state update is queued
                    setTimeout(() => {
                        // Additional delay ensures React has processed the state update and re-rendered
                        // Only clear if we're still editing the same field AND it wasn't triggered by a button click
                        // Button clicks will set editingDate again immediately, so this check will fail and state won't be cleared
                        if (editingDate && editingDate.bookId === bookId && editingDate.field === field) {
                            // Check if this was triggered by a button click by checking if editingDate was just set
                            // If editingDate is still set to this field, it means a button was clicked and we should keep it
                            // Only clear if the field is no longer being actively edited (blur event)
                            // We'll rely on onBlur to clear the state instead
                            // For now, don't auto-clear - let buttons keep the state active
                            // console.log('[BOOK LIST MANAGER] Date saved successfully, editing state preserved for button visibility');
                        }
                    }, 300); // 300ms delay ensures optimistic update is reflected in DOM
                });
            });

            // Clean up pending saved date after a delay (once we're sure it's in studentDates)
            setTimeout(() => {
                pendingSavedDatesRef.current.delete(pendingKey);
            }, 2000);

            // Refresh data in the background (don't await - let it happen async)
            // This ensures we have the latest data but doesn't block the UI
            (async () => {
                try {
                    // Re-fetch the book list to ensure we have the latest data
                    if (selectedBookListData && selectedBookListData.id) {
                        const bookListRes = await fetchBookListsId({ id: selectedBookListData.id });
                        const bookListData = bookListRes.data?.bookLists?.[0];
                        const bookListItems = bookListRes.data?.bookListItems || [];
                        const studentDatesFromApi = bookListRes.data?.studentDates || [];

                        // Also fetch dates for all book lists with the same name (shared book lists)
                        let allStudentDates = [...studentDatesFromApi];
                        if (bookListData && bookListData.name) {
                            try {
                                // Find all book lists with the same name
                                const sameNameBookLists = bookLists.filter(bl =>
                                    bl.name === bookListData.name && parseInt(bl.active) === 1
                                );

                                // Fetch dates for all of them
                                const datesPromises = sameNameBookLists.map(bl =>
                                    fetchBookStudentDatesByBookList({ bookListId: bl.id })
                                );
                                const datesResponses = await Promise.all(datesPromises);

                                datesResponses.forEach(res => {
                                    if (res && res.status < 400 && res.data) {
                                        const dates = getDataArray(res);
                                        allStudentDates = [...allStudentDates, ...dates];
                                    }
                                });
                            } catch (error) {
                                console.error('[BOOK LIST MANAGER] Error fetching dates for shared book lists:', error);
                            }
                        }

                        // Update student dates state with fresh data from API
                        if (allStudentDates.length > 0) {
                            setStudentDates(prev => {
                                // Merge with existing dates, avoiding duplicates
                                const existingMap = new Map(prev.map(sd => `${sd.student}_${sd.book_id}_${sd.book_list_id}`));
                                allStudentDates.forEach(sd => {
                                    const key = `${sd.student}_${sd.book_id}_${sd.book_list_id}`;
                                    existingMap.set(key, sd);
                                });
                                return Array.from(existingMap.values());
                            });
                        }

                        if (bookListData && bookListItems.length > 0) {
                            // Deduplicate items
                            const uniqueItemsMap = new Map();
                            bookListItems.forEach((item) => {
                                const key = `${item.book_id}_${item.sort_order}`;
                                if (!uniqueItemsMap.has(key)) {
                                    uniqueItemsMap.set(key, item);
                                }
                            });
                            const deduplicatedItems = Array.from(uniqueItemsMap.values());

                            // Enhance items with book data
                            const enhancedItems = deduplicatedItems.map((item, idx) => {
                                const book = books.find(b => {
                                    const bookId = b.id || b.book_id;
                                    const itemBookId = item.book_id;
                                    if (!bookId || !itemBookId) return false;
                                    return parseInt(bookId) === parseInt(itemBookId);
                                });

                                return {
                                    ...item,
                                    bookTitle: book?.title || 'Unknown Book',
                                    bookAuthor: book?.author || '',
                                    order: item.sort_order || idx + 1,
                                    sort_order: item.sort_order || idx + 1
                                };
                            });

                            // Update selected book list data with fresh data
                            setSelectedBookListData({
                                ...bookListData,
                                parsedItems: enhancedItems,
                                id: bookListData.id,
                                isTemplate: false
                            });
                        }
                    }
                } catch (refreshError) {
                    console.error('[BOOK LIST MANAGER] Error refreshing book list after date save:', refreshError);
                }
            })();
        } catch (error) {
            console.error('[BOOK LIST MANAGER] Error updating date:', error);
            // Don't show error message for auto-save operations - just log silently
            // Error messages will only show when main Save button is clicked
            // Don't clear editing state on error - let user see the date and try again
            // The date field will stay visible with the entered value
            throw error; // Re-throw so calling code knows it failed
        }
    };

    // Handle template book updates (add/remove/reorder)
    const handleTemplateBookUpdate = useCallback(async (updatedItems) => {
        if (!selectedBookListData || !selectedBookListData.isTemplate || !selectedBookListData.templateId) {
            return;
        }

        setLoading(true);
        try {
            const template = selectedBookListData.template;
            const templateItems = updatedItems.map((item, index) => ({
                bookId: item.book_id,
                sortOrder: index + 1
            }));

            const response = await updateBookListTemplate({
                template: {
                    id: selectedBookListData.templateId,
                    name: template.name,
                    notes: template.notes || '',
                    active: template.active ?? 1
                },
                templateItems
            });

            if (response && response.status < 400) {
                // Update local state
                setSelectedBookListData({
                    ...selectedBookListData,
                    parsedItems: updatedItems
                });
                // Refresh templates
                await refreshData();
            } else {
                setApiError('Error updating template. Please try again.');
            }
        } catch (error) {
            console.error('[BOOK LIST MANAGER] Error updating template:', error);
            setApiError('Error updating template. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [selectedBookListData, updateBookListTemplate, refreshData]);

    // Handle template selection - populates table with template books
    const handleTemplateChange = useCallback(async (e, formik) => {
        const selectedTemplate = e?.target?.value || e;

        if (!selectedTemplate || selectedTemplate.value === -1) {
            // Clear template selection
            if (formik) {
                formik.setFieldValue('selectedTemplate', { value: -1, label: 'Please select...' });
            }
            setSelectedBookListData(null);
            return;
        }

        // Handle "New Book List Template" option
        if (selectedTemplate.value === 'new') {
            setShowTemplateModal(true);
            setTemplateModalMode('create');
            setSelectedTemplate(null);
            if (formik) {
                formik.setFieldValue('selectedTemplate', { value: -1, label: 'Please select...' });
            }
            return;
        }

        // Update formik if provided
        if (formik) {
            if (e?.target) {
                formik.handleChange(e);
            } else {
                formik.setFieldValue('selectedTemplate', selectedTemplate);
            }
        }

        // Get template items for selected template
        const templateId = parseInt(selectedTemplate.value);
        const template = templates.find(t => parseInt(t.id) === templateId);
        const items = templateItems
            .filter(item => parseInt(item.book_list_template_id) === templateId)
            .sort((a, b) => (parseInt(a.sort_order) || 0) - (parseInt(b.sort_order) || 0));

        // Enhance items with book info
        const enhancedItems = items.map((item, idx) => {
            const book = books.find(b => {
                const bookId = b.id || b.book_id;
                const itemBookId = item.book_id;
                if (!bookId || !itemBookId) return false;
                return parseInt(bookId) === parseInt(itemBookId) || String(bookId) === String(itemBookId);
            });

            return {
                book_id: parseInt(item.book_id),
                bookTitle: book?.title || 'Unknown Book',
                bookAuthor: book?.author || '',
                date_assigned: null,
                date_completed: null,
                sort_order: item.sort_order || idx + 1,
                order: idx + 1
            };
        });

        // When template is selected, it becomes a NEW book list (not a template)
        // Templates can only be edited in Book List Template page
        // Generate name from student/group name + template name
        let generatedName = template?.name || 'Book List';
        if (formik?.values?.selectedStudent && formik.values.selectedStudent.value !== -1 && formik.values.selectedStudent.value !== 'all_group') {
            const student = students.find(s => s.user_id === formik.values.selectedStudent.value);
            if (student) {
                const studentName = `${student.first_name || ''} ${student.last_name || ''}`.trim();
                if (studentName) {
                    generatedName = `${studentName} ${template?.name || 'Book List'}`.trim();
                }
            }
        } else if (formik?.values?.selectedGroup && formik.values.selectedGroup.value !== -1) {
            const group = groups.find(g => parseInt(g.id) === parseInt(formik.values.selectedGroup.value));
            if (group) {
                const groupName = group.name || '';
                if (groupName) {
                    generatedName = `${groupName} ${template?.name || 'Book List'}`.trim();
                }
            }
        }
        
        setSelectedBookListData({
            id: null, // No actual book list yet - this is a NEW book list being created from template
            name: generatedName,
            student: formik?.values?.selectedStudent?.value || null,
            isTemplate: false, // Not a template - it's a new book list
            templateId: templateId,
            template: template, // Keep template object for reference
            parsedItems: enhancedItems
        });
    }, [templates, templateItems, books]);

    // Handle applying template to students (creates actual book lists)
    const handleApplyTemplate = useCallback(async () => {
        if (!selectedBookListData || !selectedBookListData.isTemplate) {
            setApiError('Please select a template first.');
            return;
        }

        const templateId = selectedBookListData.templateId;
        const formik = formRef.current;

        // Get target students
        let targetStudents = [];
        if (formik?.values?.selectedStudent && formik.values.selectedStudent.value === 'all_group') {
            // "All Students in Group" selected - get all students from the group
            if (formik.values.selectedGroup && formik.values.selectedGroup.value !== -1) {
                const groupId = parseInt(formik.values.selectedGroup.value);
                const membersInGroup = groupMembers.filter(gm => parseInt(gm.group_id) === groupId);
                const memberStudentIds = membersInGroup.map(gm => gm.student || gm.user_id);
                targetStudents = students.filter(s => memberStudentIds.includes(s.user_id));
            }
        } else if (formik?.values?.selectedGroup && formik.values.selectedGroup.value !== -1) {
            // Group selected (fallback - should not happen if UI is correct, but handle it)
            const groupId = parseInt(formik.values.selectedGroup.value);
            const membersInGroup = groupMembers.filter(gm => parseInt(gm.group_id) === groupId);
            const memberStudentIds = membersInGroup.map(gm => gm.student || gm.user_id);
            targetStudents = students.filter(s => memberStudentIds.includes(s.user_id));
        } else if (formik?.values?.selectedStudent && formik.values.selectedStudent.value !== -1) {
            // Single student selected
            const student = students.find(s => s.user_id === formik.values.selectedStudent.value);
            if (student) targetStudents = [student];
        }

        if (targetStudents.length === 0) {
            setApiError('Please select a group or student first.');
            return;
        }

        // Create book list for each student
        setLoading(true);
        try {
            let successCount = 0;
            let errorCount = 0;
            const errors = [];

            for (const student of targetStudents) {
                try {
                    // Create book list from template
                    // Use base name only (without student name prefix) so it can be found as a common book list
                    const bookListName = selectedBookListData.name || 'Book List';
                    const payload = {
                        bookList: {
                            studentId: student.user_id,
                            name: bookListName,
                            notes: '',
                            active: 1
                        },
                        bookListItems: selectedBookListData.parsedItems.map((item, index) => ({
                            bookId: item.book_id,
                            dateAssigned: null,
                            dateCompleted: null,
                            sortOrder: item.sort_order || index + 1
                        }))
                    };

                    const response = await createBookList(payload);
                    if (response && response.status < 400) {
                        successCount++;
                    } else {
                        errorCount++;
                                                        // Extract error message from various possible response structures
                                                        let errorMessage = 'Unknown error';
                                                        if (response?.data?.message) {
                                                            errorMessage = response.data.message;
                                                        } else if (response?.message) {
                                                            errorMessage = response.message;
                                                        } else if (response?.data?.error) {
                                                            errorMessage = response.data.error;
                                                        } else if (typeof response?.data === 'string') {
                                                            errorMessage = response.data;
                                                        } else if (response?.statusText) {
                                                            errorMessage = response.statusText;
                                                        }
                                                        errors.push(`Failed for ${student.first_name} ${student.last_name}: ${errorMessage}`);
                    }
                } catch (error) {
                    errorCount++;
                                                    const errorMsg = error?.message || error?.toString() || 'Unknown error';
                                                    errors.push(`Error for ${student.first_name} ${student.last_name}: ${errorMsg}`);
                }
            }

            if (errorCount > 0) {
                setApiError(`Created for ${successCount} students, but ${errorCount} failed. ${errors.join('; ')}`);
            } else {
                setApiError(null);
                // Refresh data and clear template selection
                await refreshData();
                if (formik) {
                    formik.setFieldValue('selectedTemplate', { value: -1, label: 'Please select...' });
                    // Refresh book list dropdown options by re-triggering student/group change handler
                    const selectedStudent = formik.values.selectedStudent;
                    if (selectedStudent && selectedStudent.value === 'all_group') {
                        await handleStudentChange({ target: { value: selectedStudent } }, formik);
                    } else if (selectedStudent && selectedStudent.value !== -1) {
                        await handleStudentChange({ target: { value: selectedStudent } }, formik);
                    }
                }
                setSelectedBookListData(null);
            }
        } catch (error) {
            console.error('[BOOK LIST MANAGER] Error applying template:', error);
            setApiError('Error applying template. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [selectedBookListData, groupMembers, students, createBookList, refreshData]);

    // Handle template modal
    const handleOpenTemplateModal = (mode, template = null) => {
        setTemplateModalMode(mode);
        setSelectedTemplate(template);
        setShowTemplateModal(true);
    };

    const handleCloseTemplateModal = (shouldRefresh = false) => {
        setShowTemplateModal(false);
        setSelectedTemplate(null);
        if (shouldRefresh) {
            refreshData();
        }
    };

    // Helper function to convert API date to input format (yyyy-mm-dd)
    const convertDateForInput = (dateString) => {
        if (!dateString || dateString === 'null' || dateString === null) return '';
        // If already in yyyy-mm-dd format, return as is
        if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
            return dateString;
        }
        // Try to parse and format
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '';
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (e) {
            return '';
        }
    };

    // Helper function to get today's date in yyyy-mm-dd format
    const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Helper function to get current date value for a book/field, considering pending saved dates
    // Logic matches previous requirements:
    // - Individual student: Pre-fill with their previously saved dates
    // - Common book list (group) with common dates: Pre-fill with common date
    // - Common book list (group) with different dates: Field remains blank (different dates shown below)
    const getCurrentDateValue = (bookId, field, assignedDates, completedDates, targetStudents, isGroup, commonDateValue, isCommonDate = false, differentDates = []) => {
        const fieldName = field === 'date_assigned' ? 'date_assigned' : 'date_completed';
        const pendingKey = `${bookId}_${fieldName}`;
        const pendingSaved = pendingSavedDatesRef.current.get(pendingKey);

        // If there's a pending saved date, use it (highest priority)
        if (pendingSaved && pendingSaved.date) {
            return pendingSaved.date;
        }

        // For groups with different dates: Field should be blank (different dates shown below)
        if (isGroup && !isCommonDate && differentDates.length > 0) {
            return '';
        }

        // For common dates in groups: Use the common date
        if (isGroup && isCommonDate && commonDateValue) {
            return commonDateValue;
        }

        // For individual students: Pre-fill with their previously saved dates
        if (!isGroup && targetStudents.length > 0) {
            const dates = field === 'date_assigned' ? assignedDates : completedDates;
            const targetStudentId = String(targetStudents[0].user_id || targetStudents[0] || '');
            const studentDate = dates.find(d => {
                const dStudentId = String(d.student || '');
                return dStudentId === targetStudentId || parseInt(dStudentId) === parseInt(targetStudentId);
            });
            if (studentDate) {
                return convertDateForInput(studentDate.dateRaw || studentDate.date);
            }
        }

        return '';
    };

    return (
        <div className="page-box">
            <BrowserTabTitle>{pageTitle}</BrowserTabTitle>
            {loading && <LoadingOverlay />}
            <div className="card">
                <Formik
                    enableReinitialize
                    initialValues={{
                        selectedCenter: { value: 'all', label: 'All' },
                        selectedGroup: { value: -1, label: 'Please select...' },
                        selectedStudent: { value: -1, label: 'Please select...' },
                        selectedBookList: { value: -1, label: 'Please select a book list...' },
                        addBookList: { value: -1, label: 'Please select...' }
                    }}
                    innerRef={formRef}
                    onSubmit={() => null}
                >
                    {formik => (
                        <form onSubmit={formik.handleSubmit}>
                            <h2>{pageTitle}</h2>
                            <br />

                            {/* Cascading dropdowns - per Guide Page 8 */}
                            <div className="flex flex-row gap-x-4 items-end flex-wrap mb-4">
                                <div className="grid grid-cols-1 gap-y-1 w-32">
                                    <label className="text-sm font-medium">Center</label>
                                    <SelectSingle
                                        id="book-list-center"
                                        name="selectedCenter"
                                        value={formik.values.selectedCenter}
                                        options={centerOptions}
                                        onChange={(e) => handleCenterChange(e, formik)}
                                    />
                                </div>
                                <div className="grid grid-cols-1 gap-y-1 w-64">
                                    <label className="text-sm font-medium">Group</label>
                                    <SelectSingle
                                        id="book-list-group"
                                        name="selectedGroup"
                                        disabled={formik.values.selectedStudent?.value && formik.values.selectedStudent.value !== -1 && formik.values.selectedStudent.value !== 'all_group'}
                                        value={formik.values.selectedGroup}
                                        options={groupOptions}
                                        onChange={(e) => handleGroupChange(e, formik)}
                                    />
                                </div>
                                <div className="grid grid-cols-1 gap-y-1 w-48">
                                    <label className="text-sm font-medium">Student</label>
                                    <SelectSingle
                                        id="book-list-student"
                                        name="selectedStudent"
                                        disabled={formik.values.selectedGroup?.value && formik.values.selectedGroup.value !== -1}
                                        value={formik.values.selectedStudent}
                                        options={studentOptions}
                                        onChange={(e) => handleStudentChange(e, formik)}
                                    />
                                </div>
                                <div className="grid grid-cols-1 gap-y-1 w-64">
                                    <div className="flex flex-row items-center gap-2">
                                        <label className="text-sm font-medium flex-1">Book List</label>
                                        {formik.values.selectedBookList && formik.values.selectedBookList.value !== -1 && (
                                            <TooltipWrapper tooltipText="Detach this book list from current student/group">
                                                <Button
                                                    color="lte-mpLRed"
                                                    variant="icon"
                                                    onClick={async () => {
                                                        if (!formik.values.selectedBookList || formik.values.selectedBookList.value === -1) {
                                                            setApiError('No book list selected to detach.');
                                                            return;
                                                        }
                                                        
                                                        const bookListId = formik.values.selectedBookList.value;
                                                        const bookListName = formik.values.selectedBookList.label;
                                                        
                                                        if (!window.confirm(`Are you sure you want to detach "${bookListName}" from the current student/group? This will remove the association but keep the book list.`)) {
                                                            return;
                                                        }
                                                        
                                                        setLoading(true);
                                                        setApiError(null);
                                                        
                                                        try {
                                                            // For groups, we need to detach from all students in the group
                                                            if (formik.values.selectedStudent && formik.values.selectedStudent.value === 'all_group') {
                                                                const groupId = parseInt(formik.values.selectedGroup.value);
                                                                const membersInGroup = groupMembers.filter(gm => parseInt(gm.group_id) === groupId);
                                                                const memberStudentIds = membersInGroup.map(gm => gm.student || gm.user_id);
                                                                const groupStudents = students.filter(s => memberStudentIds.includes(s.user_id));
                                                                
                                                                // Find all book lists with the same base name for these students
                                                                // CRITICAL: Student IDs are UUIDs (strings), not numbers - use string comparison
                                                                const baseName = extractBaseName(bookListName);
                                                                const bookListsToDetach = bookLists.filter(bl => {
                                                                    const blStudentId = String(bl.student || '');
                                                                    const blBaseName = extractBaseName(bl.name || '');
                                                                    return memberStudentIds.includes(blStudentId) && blBaseName === baseName && parseInt(bl.active) === 1;
                                                                });
                                                                
                                                                // Delete all matching book lists
                                                                for (const bl of bookListsToDetach) {
                                                                    try {
                                                                        await deleteBookList({ id: bl.id });
                                                                    } catch (error) {
                                                                        console.error(`[BOOK LIST MANAGER] Error detaching book list ${bl.id}:`, error);
                                                                    }
                                                                }
                                                            } else if (formik.values.selectedStudent && formik.values.selectedStudent.value !== -1) {
                                                                // For individual student, delete their book list
                                                                await deleteBookList({ id: bookListId });
                                                            } else {
                                                                setApiError('Please select a student or group first.');
                                                                setLoading(false);
                                                                return;
                                                            }
                                                            
                                                            // Refresh data first
                                                            await refreshData();
                                                            
                                                            // Wait a bit for state to update
                                                            await new Promise(resolve => setTimeout(resolve, 100));
                                                            
                                                            // Refresh dropdown options by re-triggering student/group change handler
                                                            if (formik.values.selectedStudent && formik.values.selectedStudent.value === 'all_group') {
                                                                // Re-trigger group change to refresh book list options
                                                                const selectedGroup = formik.values.selectedGroup;
                                                                if (selectedGroup && selectedGroup.value !== -1) {
                                                                    await handleGroupChange({ target: { value: selectedGroup } }, formik);
                                                                }
                                                            } else if (formik.values.selectedStudent && formik.values.selectedStudent.value !== -1) {
                                                                // Re-trigger student change to refresh book list options
                                                                await handleStudentChange({ target: { value: formik.values.selectedStudent } }, formik);
                                                            }
                                                            formik.setFieldValue('selectedBookList', { value: -1, label: 'Please select a book list...' });
                                                            setSelectedBookListData(null);
                                                            setOriginalBookListName(null);
                                                            setApiError(null);
                                                        } catch (error) {
                                                            console.error('[BOOK LIST MANAGER] Error detaching book list:', error);
                                                            setApiError('Error detaching book list: ' + (error.message || 'Please try again.'));
                                                        } finally {
                                                            setLoading(false);
                                                        }
                                                    }}
                                                >
                                                    <SVGIcon fileName="unlink" />
                                                </Button>
                                            </TooltipWrapper>
                                        )}
                                    </div>
                                    <SelectSingle
                                        id="book-list-select"
                                        name="selectedBookList"
                                        value={formik.values.selectedBookList}
                                        options={bookListOptions}
                                        onChange={(e) => handleBookListChange(e, formik)}
                                    />
                                </div>
                            </div>

                            <div className="mb-4">
                                <div className="grid grid-cols-1 gap-y-1 w-64">
                                    <div className="flex flex-row items-center gap-2">
                                        <label className="text-sm font-medium flex-1">Add Book List</label>
                                        {formik.values.addBookList && formik.values.addBookList.value !== -1 && formik.values.addBookList.value !== 'new' && (
                                            <TooltipWrapper tooltipText="Delete this book list template">
                                                <Button
                                                    color="lte-mpLRed"
                                                    variant="icon"
                                                    onClick={async () => {
                                                        const selected = formik.values.addBookList;
                                                        if (!selected || selected.value === -1 || selected.value === 'new') {
                                                            setApiError('No book list template selected to delete.');
                                                            return;
                                                        }
                                                        
                                                        const templateId = selected.value;
                                                        const templateName = selected.label;
                                                        
                                                        if (!window.confirm(`Are you sure you want to delete the book list template "${templateName}"? This action cannot be undone.`)) {
                                                            return;
                                                        }
                                                        
                                                        setLoading(true);
                                                        setApiError(null);
                                                        
                                                        try {
                                                            const response = await deleteBookListTemplate({ id: templateId });
                                                            if (!response || response.status >= 400) {
                                                                setApiError(response?.data?.message || 'Error deleting book list template. Please try again.');
                                                                setLoading(false);
                                                                return;
                                                            }
                                                            
                                                            await refreshData();
                                                            // Refresh templates to update Add Book List dropdown
                                                            const templatesRes = await fetchBookListTemplatesFull();
                                                            if (templatesRes && templatesRes.data) {
                                                                const newTemplates = Array.isArray(templatesRes.data.templates) ? templatesRes.data.templates : [];
                                                                setTemplates(newTemplates);
                                                            }
                                                            formik.setFieldValue('addBookList', { value: -1, label: 'Please select...' });
                                                            setSelectedBookListData(null);
                                                            setOriginalBookListName(null);
                                                            setApiError(null);
                                                        } catch (error) {
                                                            console.error('[BOOK LIST MANAGER] Error deleting book list template:', error);
                                                            setApiError('Error deleting book list template: ' + (error.message || 'Please try again.'));
                                                        } finally {
                                                            setLoading(false);
                                                        }
                                                    }}
                                                >
                                                    <SVGIcon fileName="trash" />
                                                </Button>
                                            </TooltipWrapper>
                                        )}
                                    </div>
                                    <SelectSingle
                                        id="add-book-list-select"
                                        name="addBookList"
                                        value={formik.values.addBookList || { value: -1, label: 'Please select...' }}
                                        options={[
                                            { value: -1, label: 'Please select...' },
                                            { value: 'new', label: '+ New Book List' },
                                            ...templates.filter(t => parseInt(t.active) === 1).map(t => ({
                                                value: parseInt(t.id),
                                                label: t.name || 'Unnamed Book List',
                                                obj: t,
                                                isTemplate: true
                                            }))
                                        ]}
                                        onChange={async (e) => {
                                            const selected = e.target.value;
                                            if (!selected || selected.value === -1) {
                                                setSelectedBookListData(null);
                                                return;
                                            }

                                            if (selected.value === 'new') {
                                                // Open create modal
                                                try {
                                                    if (formik.values.selectedStudent && formik.values.selectedStudent.value === 'all_group') {
                                                        const groupId = parseInt(formik.values.selectedGroup.value);
                                                        const membersInGroup = groupMembers.filter(gm => parseInt(gm.group_id) === groupId);
                                                        const memberStudentIds = membersInGroup.map(gm => gm.student || gm.user_id);
                                                        const groupStudents = students.filter(s => memberStudentIds.includes(s.user_id));
                                                        if (groupStudents.length > 0) {
                                                            handleOpenModal('create', {
                                                                student: groupStudents[0].user_id,
                                                                groupId: groupId,
                                                                groupStudentIds: groupStudents.map(s => s.user_id)
                                                            });
                                                        }
                                                    } else if (formik.values.selectedStudent && formik.values.selectedStudent.value !== -1) {
                                                        const studentObj = students.find(s => s.user_id === formik.values.selectedStudent.value);
                                                        handleOpenModal('create', {
                                                            student: studentObj ? studentObj.user_id : formik.values.selectedStudent.value
                                                        });
                                                    } else {
                                                        setApiError('Please select a group or student first.');
                                                    }
                                                } catch (error) {
                                                    console.error('[BOOK LIST MANAGER] Error opening modal:', error);
                                                    setApiError('Error opening form. Please try again.');
                                                }
                                                formik.setFieldValue('addBookList', { value: -1, label: 'Please select...' });
                                            } else {
                                                // Template selected - populate table below for editing
                                                const templateId = parseInt(selected.value);
                                                const template = templates.find(t => parseInt(t.id) === templateId);
                                                if (!template) {
                                                    setApiError('Template not found.');
                                                    formik.setFieldValue('addBookList', { value: -1, label: 'Please select...' });
                                                    return;
                                                }

                                                // Get template items
                                                const items = templateItems
                                                    .filter(item => parseInt(item.book_list_template_id) === templateId)
                                                    .sort((a, b) => (parseInt(a.sort_order) || 0) - (parseInt(b.sort_order) || 0));

                                                // Ensure books are loaded
                                                let booksToUse = books;
                                                if (!booksToUse || booksToUse.length === 0) {
                                                    const booksRes = await fetchBooksAll();
                                                    booksToUse = getDataArray(booksRes);
                                                    if (booksToUse.length > 0) {
                                                        setBooks(booksToUse);
                                                    }
                                                }

                                                // Enhance items with book info
                                                const enhancedItems = items.map((item, idx) => {
                                                    const book = booksToUse.find(b => {
                                                        const bookId = b.id || b.book_id;
                                                        const itemBookId = item.book_id;
                                                        if (!bookId || !itemBookId) return false;
                                                        return parseInt(bookId) === parseInt(itemBookId) || String(bookId) === String(itemBookId);
                                                    });

                                                    return {
                                                        book_id: parseInt(item.book_id),
                                                        bookTitle: book?.title || 'Unknown Book',
                                                        bookAuthor: book?.author || '',
                                                        date_assigned: null,
                                                        date_completed: null,
                                                        sort_order: item.sort_order || idx + 1,
                                                        order: idx + 1
                                                    };
                                                });

                                                // Generate name from student/group name + template name
                                                let generatedName = template?.name || 'Book List';
                                                if (formik?.values?.selectedStudent && formik.values.selectedStudent.value !== -1 && formik.values.selectedStudent.value !== 'all_group') {
                                                    const student = students.find(s => s.user_id === formik.values.selectedStudent.value);
                                                    if (student) {
                                                        const studentName = `${student.first_name || ''} ${student.last_name || ''}`.trim();
                                                        if (studentName) {
                                                            generatedName = `${studentName} ${template?.name || 'Book List'}`.trim();
                                                        }
                                                    }
                                                } else if (formik?.values?.selectedGroup && formik.values.selectedGroup.value !== -1) {
                                                    const group = groups.find(g => parseInt(g.id) === parseInt(formik.values.selectedGroup.value));
                                                    if (group) {
                                                        const groupName = group.name || '';
                                                        if (groupName) {
                                                            generatedName = `${groupName} ${template?.name || 'Book List'}`.trim();
                                                        }
                                                    }
                                                }

                                                // When template is selected, it becomes a NEW book list (not a template)
                                                // Templates can only be edited in Book List Template page
                                                setSelectedBookListData({
                                                    id: null, // No actual book list yet - this is a NEW book list being created from template
                                                    name: generatedName,
                                                    student: formik?.values?.selectedStudent?.value || null,
                                                    isTemplate: false, // Not a template - it's a new book list
                                                    templateId: templateId, // Keep reference to original template for reference
                                                    template: template, // Keep template object for reference
                                                    parsedItems: enhancedItems
                                                });

                                                // Store original name for change detection (for new book lists, this is the initial name)
                                                setOriginalBookListName(generatedName);

                                                // Reset dropdown
                                                formik.setFieldValue('addBookList', { value: -1, label: 'Please select...' });
                                            }
                                        }}
                                        disabled={(!formik.values.selectedStudent || formik.values.selectedStudent.value === -1) &&
                                            (!formik.values.selectedGroup || formik.values.selectedGroup.value === -1)}
                                    />
                                </div>
                            </div>

                            {/* Unified Save button - automatically handles group vs individual student */}
                            {selectedBookListData && (
                                <div className="mb-4 flex flex-row gap-2 items-center">
                                    <Button
                                        color="mpBlue"
                                        onClick={async () => {
                                            // Unified save handler - automatically detects group vs individual student
                                            const formik = formRef.current;
                                            const selectedStudent = formik?.values?.selectedStudent;
                                            const isGroup = selectedStudent && selectedStudent.value === 'all_group';
                                            const selectedGroup = formik?.values?.selectedGroup;

                                            // Determine target students
                                            let targetStudents = [];
                                            if (isGroup && selectedGroup && selectedGroup.value !== -1) {
                                                // "All Students in Group" selected
                                                const groupId = parseInt(selectedGroup.value);
                                                    const membersInGroup = groupMembers.filter(gm => parseInt(gm.group_id) === groupId);
                                                    const memberStudentIds = membersInGroup.map(gm => gm.student || gm.user_id);
                                                    targetStudents = students.filter(s => memberStudentIds.includes(s.user_id));
                                            } else if (selectedStudent && selectedStudent.value !== -1 && selectedStudent.value !== 'all_group') {
                                                // Single student selected
                                                // Use String() comparison to handle both UUID strings and numeric IDs
                                                const selectedStudentId = String(selectedStudent.value || '');
                                                const student = students.find(s =>
                                                    String(s.user_id || '') === selectedStudentId ||
                                                    parseInt(s.user_id) === parseInt(selectedStudent.value)
                                                );
                                                if (student) targetStudents = [student];
                                            } else if (selectedBookListData.student) {
                                                // Fallback: use the book list's student if no selection
                                                // Use String() comparison to handle both UUID strings and numeric IDs
                                                const bookListStudentId = String(selectedBookListData.student || '');
                                                const student = students.find(s =>
                                                    String(s.user_id || '') === bookListStudentId ||
                                                    parseInt(s.user_id) === parseInt(selectedBookListData.student)
                                                );
                                                if (student) targetStudents = [student];
                                            } else if (isGroup && !selectedGroup && selectedBookListData && selectedBookListData.group_id) {
                                                // Fallback for common book lists: use group_id from book list data
                                                const groupId = parseInt(selectedBookListData.group_id);
                                                const membersInGroup = groupMembers.filter(gm => parseInt(gm.group_id) === groupId);
                                                const memberStudentIds = membersInGroup.map(gm => gm.student || gm.user_id);
                                                targetStudents = students.filter(s => memberStudentIds.includes(s.user_id));
                                            }

                                            if (targetStudents.length === 0) {
                                                console.error('[BOOK LIST MANAGER] Save failed - no target students found:', {
                                                    isGroup,
                                                    selectedGroup: selectedGroup?.value,
                                                    selectedStudent: selectedStudent?.value,
                                                    selectedBookListData: {
                                                        id: selectedBookListData?.id,
                                                        student: selectedBookListData?.student,
                                                        group_id: selectedBookListData?.group_id,
                                                        name: selectedBookListData?.name
                                                    }
                                                });
                                                setApiError('Please select a group or student first. Unable to determine target students for saving.');
                                                return;
                                            }

                                            // Check if this is a template (new book list) or existing book list
                                            const isNewFromTemplate = selectedBookListData.templateId && !selectedBookListData.id;
                                            const isExistingBookList = selectedBookListData.id && !selectedBookListData.templateId;
                                            const isNewDirect = !selectedBookListData.id && !selectedBookListData.templateId; // New book list created directly (not from template)

                                            console.log('[BOOK LIST MANAGER] Save handler - book list type:', {
                                                isNewFromTemplate,
                                                isExistingBookList,
                                                isNewDirect,
                                                hasId: !!selectedBookListData.id,
                                                hasTemplateId: !!selectedBookListData.templateId,
                                                parsedItemsCount: selectedBookListData.parsedItems?.length || 0
                                            });

                                            // For templates, require templateId
                                            if (isNewFromTemplate && !selectedBookListData.templateId) {
                                                setApiError('Please select a template first.');
                                                return;
                                            }

                                            setLoading(true);
                                            setApiError(null);

                                            try {
                                                // Ensure parsedItems exists and is an array
                                                if (!selectedBookListData.parsedItems || !Array.isArray(selectedBookListData.parsedItems)) {
                                                    console.error('[BOOK LIST MANAGER] parsedItems is missing or not an array:', selectedBookListData.parsedItems);
                                                    setApiError('Book list items are missing. Please add books to the list.');
                                                    setLoading(false);
                                                    return;
                                                }

                                                // Filter out incomplete items (new items without book_id)
                                                const validItems = selectedBookListData.parsedItems.filter(item =>
                                                    item.book_id && item.book_id !== null && item.book_id !== undefined
                                                );

                                                // Check if there are valid items BEFORE checking duplicates
                                                if (validItems.length === 0) {
                                                    setApiError('Please add at least one book to the list before saving.');
                                                    setLoading(false);
                                                    return;
                                                }

                                                const bookListName = selectedBookListData.name || 'Book List';
                                                if (!bookListName || bookListName.trim() === '') {
                                                    setApiError('Please enter a book list name.');
                                                    setLoading(false);
                                                    return;
                                                }

                                                // Format book list items for payload
                                                const bookListItems = validItems.map((item, index) => ({
                                                    bookId: parseInt(item.book_id),
                                                    dateAssigned: item.date_assigned || null,
                                                    dateCompleted: item.date_completed || null,
                                                    sortOrder: parseInt(item.sort_order) || index + 1
                                                }));

                                                // Determine if this is an update (existing book list) or create (new from template or new direct)
                                                const isUpdate = isExistingBookList;
                                                const isCreate = isNewFromTemplate || isNewDirect;
                                                const baseName = extractBaseName(bookListName.trim());

                                                console.log('[BOOK LIST MANAGER] Save operation type:', {
                                                    isUpdate,
                                                    isCreate,
                                                    isNewFromTemplate,
                                                    isNewDirect,
                                                    validItemsCount: validItems.length
                                                });

                                                // Save/update book list for each target student
                                                let successCount = 0;
                                                let errorCount = 0;
                                                const errors = [];

                                                for (const student of targetStudents) {
                                                    try {
                                                        if (isUpdate) {
                                                            console.log('[BOOK LIST MANAGER] Updating existing book list for student:', student.user_id);
                                                            // Update existing book list for this student
                                                            // Find existing book list with same base name for this student
                                                            const existingBookList = bookLists.find(bl => {
                                                                const blStudentId = parseInt(bl.student);
                                                                const blBaseName = extractBaseName(bl.name || '');
                                                                return blStudentId === parseInt(student.user_id) && 
                                                                       blBaseName === baseName && 
                                                                       parseInt(bl.active) === 1;
                                                            });

                                                            if (existingBookList) {
                                                                // Update existing - use the base name to ensure consistency across students
                                                                const existingBaseName = extractBaseName(existingBookList.name || '');
                                                                const newBaseName = extractBaseName(bookListName.trim());
                                                                
                                                                // If base names match, keep the existing name (may have student prefix)
                                                                // If base names differ, update to new name
                                                                const nameToUse = (existingBaseName === newBaseName) 
                                                                    ? existingBookList.name 
                                                                    : bookListName.trim();
                                                                
                                                                const updatePayload = {
                                                                    bookList: {
                                                                        id: existingBookList.id,
                                                                        studentId: student.user_id, // Send UUID directly - backend will handle conversion if needed
                                                                        name: nameToUse,
                                                                        notes: selectedBookListData.notes || '',
                                                                        active: selectedBookListData.active !== undefined ? selectedBookListData.active : 1
                                                                    },
                                                                    bookListItems: bookListItems
                                                                };

                                                                const response = await updateBookList(updatePayload);
                                                                if (response && response.status < 400) {
                                                                    successCount++;
                                                                } else {
                                                                    errorCount++;
                                                                    const errorMessage = response?.data?.message || response?.message || 'Unknown error';
                                                                    errors.push(`${student.first_name} ${student.last_name}: ${errorMessage}`);
                                                                }
                                                            } else {
                                                                // Create new for this student (they don't have one yet)
                                                                const createPayload = {
                                                                    bookList: {
                                                                        studentId: student.user_id, // Send UUID directly - backend will handle conversion if needed
                                                                        name: bookListName.trim(),
                                                                        notes: selectedBookListData.notes || '',
                                                                        active: 1
                                                                    },
                                                                    bookListItems: bookListItems
                                                                };

                                                                const response = await createBookList(createPayload);
                                                                if (response && response.status < 400) {
                                                                    successCount++;
                                                                } else {
                                                                    errorCount++;
                                                                    const errorMessage = response?.data?.message || response?.message || 'Unknown error';
                                                                    errors.push(`${student.first_name} ${student.last_name}: ${errorMessage}`);
                                                                }
                                                            }
                                                        } else {
                                                            // Create new book list (from template OR new direct)
                                                            console.log('[BOOK LIST MANAGER] Creating new book list for student:', student.user_id, {
                                                                isNewFromTemplate,
                                                                isNewDirect,
                                                                bookListName: bookListName.trim()
                                                            });
                                                            
                                                            // Check for duplicate name for this student
                                                            // CRITICAL: Student IDs are UUIDs (strings), not numbers - use string comparison
                                                            const existingBookList = bookLists.find(bl => 
                                                                String(bl.student || '') === String(student.user_id || '') && 
                                                                bl.name && 
                                                                bl.name.trim() === bookListName.trim() && 
                                                                parseInt(bl.active) === 1
                                                            );
                                                            
                                                            if (existingBookList) {
                                                                console.warn('[BOOK LIST MANAGER] Duplicate book list name found:', {
                                                                    studentId: student.user_id,
                                                                    existingBookListId: existingBookList.id,
                                                                    bookListName: bookListName.trim()
                                                                });
                                                                errorCount++;
                                                                errors.push(`${student.first_name} ${student.last_name}: A book list with this name already exists`);
                                                                continue;
                                                            }

                                                            const createPayload = {
                                                                bookList: {
                                                                    studentId: student.user_id, // Send UUID directly - backend will handle conversion if needed
                                                                    name: bookListName.trim(),
                                                                    notes: selectedBookListData.template?.notes || selectedBookListData.notes || '',
                                                                    active: 1
                                                                },
                                                                bookListItems: bookListItems
                                                            };

                                                            console.log('[BOOK LIST MANAGER] Creating book list with payload:', {
                                                                studentId: createPayload.bookList.studentId,
                                                                name: createPayload.bookList.name,
                                                                itemsCount: createPayload.bookListItems.length
                                                            });

                                                            const response = await createBookList(createPayload);
                                                            console.log('[BOOK LIST MANAGER] Create book list response:', {
                                                                status: response?.status,
                                                                hasData: !!response?.data,
                                                                success: response && response.status < 400
                                                            });
                                                            
                                                            if (response && response.status < 400) {
                                                                successCount++;
                                                                console.log('[BOOK LIST MANAGER] Successfully created book list for:', student.first_name, student.last_name);
                                                            } else {
                                                                errorCount++;
                                                                const errorMessage = response?.data?.message || response?.message || 'Unknown error';
                                                                console.error('[BOOK LIST MANAGER] Failed to create book list:', {
                                                                    student: `${student.first_name} ${student.last_name}`,
                                                                    error: errorMessage,
                                                                    responseStatus: response?.status,
                                                                    responseData: response?.data
                                                                });
                                                                errors.push(`${student.first_name} ${student.last_name}: ${errorMessage}`);
                                                            }
                                                        }
                                                    } catch (error) {
                                                        errorCount++;
                                                        const errorMsg = error?.message || error?.toString() || 'Unknown error';
                                                        errors.push(`${student.first_name} ${student.last_name}: ${errorMsg}`);
                                                    }
                                                }

                                                if (errorCount > 0) {
                                                    setApiError(`${isUpdate ? 'Updated' : 'Created'} for ${successCount} student${successCount !== 1 ? 's' : ''}, but ${errorCount} failed: ${errors.join('; ')}`);
                                                } else {
                                                setApiError(null);
                                                    console.log(`[BOOK LIST MANAGER] Successfully ${isUpdate ? 'updated' : 'created'} book list for ${successCount} student${successCount !== 1 ? 's' : ''}`);
                                                }

                                                // Refresh data to ensure individual students can see their book lists
                                                await refreshData();
                                                
                                                // Wait for React state to update after refreshData
                                                await new Promise(resolve => setTimeout(resolve, 100));

                                                if (!isUpdate) {
                                                    // For new book lists, clear the selection
                                                    setSelectedBookListData(null);
                                                    formik.setFieldValue('addBookList', { value: -1, label: 'Please select...' });
                                                
                                                // Refresh dropdown options by re-triggering student/group change handler
                                                // This ensures the newly saved book list appears in the dropdown
                                                if (formik.values.selectedStudent && formik.values.selectedStudent.value === 'all_group') {
                                                    await handleStudentChange({ target: { value: formik.values.selectedStudent } }, formik);
                                                } else if (formik.values.selectedStudent && formik.values.selectedStudent.value !== -1) {
                                                    await handleStudentChange({ target: { value: formik.values.selectedStudent } }, formik);
                                                }
                                                } else {
                                                    // For updates, keep the selection and refresh the book list data
                                                    // CRITICAL: Preserve selectedBookListData.student AND book list selection when refreshing
                                                    const currentBookList = formik.values.selectedBookList;
                                                    const preservedStudent = selectedBookListData?.student; // Preserve student ID
                                                    const preservedBookListId = selectedBookListData?.id; // Preserve book list ID
                                                    
                                                    // CRITICAL: DO NOT call handleStudentChange as it clears selectedBookListData
                                                    // Instead, directly refresh the book list data using handleBookListChange
                                                    // This preserves the table visibility
                                                    if (currentBookList && currentBookList.value !== -1) {
                                                        // Re-fetch the book list data to refresh it
                                                        await handleBookListChange(currentBookList, formik);
                                                        // After refresh, ensure student is preserved if API didn't return it
                                                        if (preservedStudent) {
                                                            setSelectedBookListData(prev => {
                                                                if (prev && !prev.student) {
                                                                    return { ...prev, student: preservedStudent };
                                                                }
                                                                return prev;
                                                            });
                                                        }
                                                    } else if (preservedBookListId) {
                                                        // If currentBookList was cleared, restore it from preservedBookListId
                                                        // Create a temporary option and directly fetch the book list
                                                        const restoredOption = {
                                                            value: parseInt(preservedBookListId),
                                                            label: selectedBookListData?.name || 'Book List',
                                                            obj: null,
                                                            isTemplate: false
                                                        };
                                                        formik.setFieldValue('selectedBookList', restoredOption);
                                                        // Try to fetch the book list directly without calling handleStudentChange
                                                        try {
                                                            await handleBookListChange(restoredOption, formik);
                                                            // After refresh, ensure student is preserved if API didn't return it
                                                            if (preservedStudent) {
                                                                setSelectedBookListData(prev => {
                                                                    if (prev && !prev.student) {
                                                                        return { ...prev, student: preservedStudent };
                                                                    }
                                                                    return prev;
                                                                });
                                                            }
                                                        } catch (error) {
                                                            console.error('[BOOK LIST MANAGER] Error restoring book list:', error);
                                                        }
                                                    }
                                                }
                                            } catch (error) {
                                                console.error('[BOOK LIST MANAGER] Error saving book list:', error);
                                                setApiError('Error saving book list: ' + (error.message || 'Please try again.'));
                                            } finally {
                                                setLoading(false);
                                            }
                                        }}
                                    >
                                        {(loading || isSavingBookList) ? 'Saving...' : 'Save'}
                                    </Button>
                                </div>
                            )}

                            {hasLoaded && selectedBookListData && selectedBookListData.parsedItems !== undefined && selectedBookListData.parsedItems !== null && Array.isArray(selectedBookListData.parsedItems) ? (
                                    <div>
                                        {/* Show error message above the form, but don't hide the form */}
                                        {apiError && (
                                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-mpLRed">
                                                {apiError}
                                            </div>
                                        )}
                                        <div className="mb-4">
                                            <div className="flex-1 min-w-[200px]">
                                                <input
                                                    type="text"
                                                    value={selectedBookListData.name || 'Book List'}
                                                    onChange={(e) => {
                                                        // Allow user to type freely - don't add ET while typing
                                                        setSelectedBookListData({
                                                            ...selectedBookListData,
                                                            name: e.target.value
                                                        });
                                                        // Clear error when user starts typing (they're fixing it)
                                                        if (apiError) {
                                                            setApiError(null);
                                                        }
                                                    }}
                                                    onBlur={(e) => {
                                                        // Name editing - no suffix needed (content changes propagate to all with same name)
                                                    }}
                                                    className="text-xl font-semibold border border-gray-300 rounded px-2 py-1 w-full max-w-md"
                                                    placeholder="Book List Name"
                                                />
                                                {selectedBookListData.description && (
                                                    <p className="text-sm text-gray-600">{selectedBookListData.description}</p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Custom Draggable Book List */}
                                        <div className="border border-gray-300 rounded-lg overflow-hidden">
                                            {/* Add Book Button - At Top of Table */}
                                                    {selectedBookListData && selectedBookListData.isTemplate !== true && (
                                                <div className="p-3 border-b border-gray-300 bg-gray-50">
                                                            <Button
                                                                color="lte-mpLBlue"
                                                                onClick={() => {
                                                                    const currentItems = selectedBookListData.parsedItems || [];
                                                                    const newItem = {
                                                                        book_id: null,
                                                                        bookTitle: 'Select a book...',
                                                                        bookAuthor: '',
                                                                        date_assigned: null,
                                                                        date_completed: null,
                                                                sort_order: 1, // Start at 1 since it's at the top
                                                                order: 1,
                                                                        isNew: true
                                                                    };
                                                            // Add new item at the beginning and recalculate all order values
                                                            const updatedItems = [newItem, ...currentItems].map((item, idx) => ({
                                                                ...item,
                                                                sort_order: idx + 1,
                                                                order: idx + 1
                                                            }));
                                                                    setSelectedBookListData({
                                                                        ...selectedBookListData,
                                                                parsedItems: updatedItems
                                                                    });
                                                                }}
                                                            >
                                                        <i className="fas fa-plus mr-2"></i>
                                                        Add Book
                                                            </Button>
                                                </div>
                                            )}
                                            

                                            {/* Table Header */}
                                            <div className="table-header grid grid-cols-12 gap-2 p-3 font-semibold rounded-t-lg">
                                                <div className="col-span-1 text-center">Order</div>
                                                <div className="col-span-3">Book Title</div>
                                                <div className="col-span-2">Date Assigned</div>
                                                <div className="col-span-2">Date Completed</div>
                                                <div className="col-span-2">Read By</div>
                                                <div className="col-span-2 text-center">Actions</div>
                                            </div>

                                            {/* Draggable Book Items */}
                                            <div className="divide-y divide-gray-200">
                                                {selectedBookListData.parsedItems && selectedBookListData.parsedItems.length > 0 ? (
                                                    selectedBookListData.parsedItems.map((item, index) => (
                                                    <div
                                                        key={`book-item-${item.book_id}-${index}`}
                                                        draggable
                                                        onDragStart={(e) => handleDragStart(e, index)}
                                                        onDragOver={(e) => handleDragOver(e, index)}
                                                        onDragEnd={handleDragEnd}
                                                        onDrop={(e) => handleDrop(e, index)}
                                                        className={`grid grid-cols-12 gap-2 p-3 items-center hover:bg-gray-50 cursor-move ${draggedItem === index ? 'opacity-50' : ''
                                                            } ${draggedOverIndex === index ? 'bg-blue-100 border-2 border-blue-400' : ''
                                                            }`}
                                                    >
                                                        {/* Drag Handle / Order */}
                                                        <div className="col-span-1 text-center text-gray-600">
                                                            <i className="fas fa-grip-vertical text-gray-400"></i>
                                                        </div>

                                                        {/* Book Title */}
                                                        <div className="col-span-3">
                                                            {item.isNew ? (() => {
                                                                // Get target students (current student or all students in group)
                                                                const formik = formRef.current;
                                                                const selectedStudent = formik?.values?.selectedStudent;
                                                                let targetStudents = [];
                                                                
                                                                if (selectedStudent && selectedStudent.value === 'all_group') {
                                                                    // Group selected - get all students in group
                                                                    const selectedGroup = formik?.values?.selectedGroup;
                                                                    if (selectedGroup && selectedGroup.value !== -1) {
                                                                        const groupId = parseInt(selectedGroup.value);
                                                                        const membersInGroup = groupMembers.filter(gm => parseInt(gm.group_id) === groupId);
                                                                        const memberStudentIds = membersInGroup.map(gm => gm.student || gm.user_id);
                                                                        targetStudents = students.filter(s => memberStudentIds.includes(s.user_id));
                                                                    }
                                                                } else if (selectedStudent && selectedStudent.value !== -1) {
                                                                    // Individual student selected
                                                                    const student = students.find(s => parseInt(s.user_id) === parseInt(selectedStudent.value));
                                                                    if (student) targetStudents = [student];
                                                                }

                                                                // Show all books (no longer filtering out books already read - allow adding any book)
                                                                const availableBooks = books;

                                                                // Exclude books already in the current list (including other isNew items that have been selected)
                                                                // Exclude the current item being edited, but include all other items with book_id
                                                                const currentItemIndex = index;
                                                                const existingBookIds = (selectedBookListData?.parsedItems || [])
                                                                    .filter((it, idx) => idx !== currentItemIndex && it.book_id && parseInt(it.book_id) > 0)
                                                                    .map(it => parseInt(it.book_id));

                                                                const filteredBooks = availableBooks.filter(b => 
                                                                    !existingBookIds.includes(parseInt(b.id || b.book_id))
                                                                );

                                                                // Calculate reading status for each book to show read count
                                                                const booksWithReadCount = filteredBooks.map(b => {
                                                                    let readCount = 0;
                                                                    if (targetStudents.length > 0) {
                                                                        const readingStatus = getBookReadingStatus(
                                                                            parseInt(b.id || b.book_id),
                                                                            targetStudents,
                                                                            bookLists,
                                                                            allBookListItems,
                                                                            studentDates
                                                                        );
                                                                        readCount = readingStatus.readBy.length;
                                                                    }
                                                                    return {
                                                                        value: parseInt(b.id || b.book_id),
                                                                        label: `${b.title || 'Unknown'}${b.author ? ` by ${b.author}` : ''}`,
                                                                        obj: b,
                                                                        readCount: readCount
                                                                    };
                                                                });

                                                                return (
                                                                <SelectSingle
                                                                    id={`book-select-${index}`}
                                                                    name={`book_select_${index}`}
                                                                    value={{ value: item.book_id || -1, label: item.bookTitle || 'Select a book...' }}
                                                                    options={[
                                                                        { value: -1, label: 'Select a book...', readCount: 0 },
                                                                        ...booksWithReadCount
                                                                    ]}
                                                                    formatOptionLabel={(option, { context }) => {
                                                                        if (option.value === -1) {
                                                                            return option.label;
                                                                        }
                                                                        const baseLabel = option.label;
                                                                        const readCount = option.readCount || 0;
                                                                        if (readCount > 0) {
                                                                            return (
                                                                                <span>
                                                                                    {baseLabel} <span style={{ color: 'red', fontWeight: 'bold' }}>({readCount})</span>
                                                                                </span>
                                                                            );
                                                                        }
                                                                        return baseLabel;
                                                                    }}
                                                                    onChange={async (e) => {
                                                                        const selectedBook = e.target.value;
                                                                        if (selectedBook && selectedBook.value !== -1) {
                                                                            const selectedBookId = parseInt(selectedBook.value);
                                                                            const book = books.find(b => parseInt(b.id || b.book_id) === selectedBookId);
                                                                            
                                                                            // Check for duplicates BEFORE updating state
                                                                            const currentBookIds = (selectedBookListData?.parsedItems || [])
                                                                                .filter((it, idx) => idx !== index && it.book_id) // Exclude current item
                                                                                .map(it => parseInt(it.book_id));
                                                                            
                                                                            if (currentBookIds.includes(selectedBookId)) {
                                                                                console.warn('[BOOK LIST MANAGER] Duplicate book detected:', selectedBookId);
                                                                                setApiError(`Cannot add duplicate books. This book is already in the list.`);
                                                                                return; // Don't update state if duplicate
                                                                            }
                                                                                
                                                                                // Check if book is already read by group members (for groups)
                                                                                let warningMessage = null;
                                                                                if (targetStudents.length > 1) {
                                                                                    const readingStatus = getBookReadingStatus(
                                                                                        selectedBookId,
                                                                                        targetStudents,
                                                                                        bookLists,
                                                                                        allBookListItems,
                                                                                        studentDates
                                                                                    );
                                                                                    if (readingStatus.readByNames.length > 0) {
                                                                                        warningMessage = `Warning: This book has already been read by: ${readingStatus.readByNames.join(', ')}`;
                                                                                    }
                                                                                }

                                                                                if (warningMessage) {
                                                                                    setApiError(warningMessage);
                                                                                    // Still allow selection, just show warning
                                                                                } else {
                                                                                    setApiError(null);
                                                                                }

                                                                            const updatedItems = selectedBookListData.parsedItems.map((it, idx) => {
                                                                                if (idx === index) {
                                                                                    return {
                                                                                        ...it,
                                                                                        book_id: selectedBookId,
                                                                                        bookTitle: book?.title || 'Unknown Book',
                                                                                        bookAuthor: book?.author || '',
                                                                                        isNew: false
                                                                                    };
                                                                                }
                                                                                return it;
                                                                            });
                                                                            
                                                                            // Update local state immediately
                                                                            setSelectedBookListData({
                                                                                ...selectedBookListData,
                                                                                parsedItems: updatedItems
                                                                            });
                                                                            
                                                                                // DISABLED: Auto-save removed - all changes must be saved via Save button
                                                                            // For new book lists (from templates), don't auto-save - user must click Save
                                                                                if (false && selectedBookListData.id && !selectedBookListData.templateId) {
                                                                                try {
                                                                                    // Filter out incomplete items
                                                                                    const validItems = updatedItems.filter(item =>
                                                                                        item.book_id && item.book_id !== null && item.book_id !== undefined
                                                                                    );
                                                                                    
                                                                                    // Additional duplicate check (defensive)
                                                                                    const bookIds = validItems.map(item => parseInt(item.book_id));
                                                                                    const duplicateBookIds = bookIds.filter((id, idx) => bookIds.indexOf(id) !== idx);
                                                                                    if (duplicateBookIds.length > 0) {
                                                                                        console.warn('[BOOK LIST MANAGER] Duplicate books detected in list after update:', duplicateBookIds);
                                                                                        setApiError(`Cannot add duplicate books. Book ID ${duplicateBookIds[0]} appears multiple times.`);
                                                                                        // Revert the change by restoring previous state
                                                                                        const previousItems = selectedBookListData.parsedItems;
                                                                                        setSelectedBookListData({
                                                                                            ...selectedBookListData,
                                                                                            parsedItems: previousItems
                                                                                        });
                                                                                        return;
                                                                                    }
                                                                                    
                                                                                    if (validItems.length > 0) {
                                                                                        const payload = buildUpdatePayload(validItems);
                                                                                        console.log('[BOOK LIST MANAGER] Auto-saving with payload:', JSON.stringify(payload, null, 2));
                                                                                        const response = await updateBookListQueued(payload, 'auto_save_add_book');
                                                                                        if (!response || response.status >= 400) {
                                                                                            const errorMessage = response?.data?.message || response?.message || 'Unknown error';
                                                                                            console.error('[BOOK LIST MANAGER] Error auto-saving book:', errorMessage, response);
                                                                                            
                                                                                            // Show error to user and revert the change
                                                                                            setApiError(`Failed to save: ${errorMessage}. Please try saving manually or contact support if the issue persists.`);
                                                                                            
                                                                                            // Revert to previous state to prevent page breakage
                                                                                            try {
                                                                                                // Check if component is still mounted
                                                                                                if (!mounted.current) {
                                                                                                    console.log('[BOOK LIST MANAGER] Component unmounted during error revert, skipping');
                                                                                                    return;
                                                                                                }
                                                                                                
                                                                                                const bookListRes = await fetchBookListsId({ id: selectedBookListData.id });
                                                                                                const bookListData = bookListRes.data?.bookLists?.[0];
                                                                                                const bookListItems = bookListRes.data?.bookListItems || [];
                                                                                                
                                                                                                if (bookListData && mounted.current) {
                                                                                                    const uniqueItemsMap = new Map();
                                                                                                    bookListItems.forEach((item) => {
                                                                                                        const key = `${item.book_id}_${item.sort_order}`;
                                                                                                        if (!uniqueItemsMap.has(key)) {
                                                                                                            uniqueItemsMap.set(key, item);
                                                                                                        }
                                                                                                    });
                                                                                                    const deduplicatedItems = Array.from(uniqueItemsMap.values());
                                                                                                    
                                                                                                    const enhancedItems = deduplicatedItems.map((item, idx) => {
                                                                                                        const book = books.find(b => {
                                                                                                            const bookId = b.id || b.book_id;
                                                                                                            const itemBookId = item.book_id;
                                                                                                            if (!bookId || !itemBookId) return false;
                                                                                                            return parseInt(bookId) === parseInt(itemBookId);
                                                                                                        });
                                                                                                        
                                                                                                        return {
                                                                                                            ...item,
                                                                                                            bookTitle: book?.title || 'Unknown Book',
                                                                                                            bookAuthor: book?.author || '',
                                                                                                            order: item.sort_order || idx + 1,
                                                                                                            sort_order: item.sort_order || idx + 1
                                                                                                        };
                                                                                                    });
                                                                                                    
                                                                                                    // Use setTimeout to ensure this happens after any potential refreshData() calls
                                                                                                    setTimeout(() => {
                                                                                                        if (!mounted.current) {
                                                                                                            console.log('[BOOK LIST MANAGER] Component unmounted during error revert delay');
                                                                                                            return;
                                                                                                        }
                                                                                                        
                                                                                                        setSelectedBookListData({
                                                                                                            ...bookListData,
                                                                                                            parsedItems: enhancedItems,
                                                                                                            id: bookListData.id,
                                                                                                            isTemplate: false
                                                                                                        });
                                                                                                        
                                                                                                        console.log('[BOOK LIST MANAGER] State reverted after error, items count:', enhancedItems.length);
                                                                                                    }, 150); // Same delay as success case
                                                                                                } else if (!bookListData) {
                                                                                                    console.error('[BOOK LIST MANAGER] Book list data not found during error revert');
                                                                                                    // Keep the current state even if re-fetch fails
                                                                                                }
                                                                                            } catch (revertError) {
                                                                                                console.error('[BOOK LIST MANAGER] Error reverting after failed save:', revertError);
                                                                                                // If revert fails, keep the current state to prevent table from disappearing
                                                                                            }
                                                                                        } else {
                                                                                            // Auto-save succeeded; record signature so manual "Save Changes" doesn't re-send identical payload.
                                                                                            const sig = buildPayloadSignature(payload);
                                                                                            if (sig) lastSavedPayloadSigRef.current = sig;
                                                                                            // Record latest successful save (cooldown-based skip)
                                                                                            lastSuccessfulSaveRef.current = {
                                                                                                ts: Date.now(),
                                                                                                bookListId: payload?.bookList?.id ?? null,
                                                                                                name: payload?.bookList?.name ?? null,
                                                                                                notes: payload?.bookList?.notes ?? null,
                                                                                                active: payload?.bookList?.active ?? null
                                                                                            };
                                                                                            // Successfully saved - re-fetch just this book list to get latest data
                                                                                            try {
                                                                                                // Refresh book list items for reading status calculations
                                                                                                const bookListItemsRes = await fetchBookListItemsAll();
                                                                                                const newBookListItems = getDataArray(bookListItemsRes);
                                                                                                setAllBookListItems(newBookListItems);
                                                                                                
                                                                                                // Re-fetch the book list to get updated data
                                                                                                const bookListRes = await fetchBookListsId({ id: selectedBookListData.id });
                                                                                                const bookListData = bookListRes.data?.bookLists?.[0];
                                                                                                const bookListItems = bookListRes.data?.bookListItems || [];
                                                                                                    const studentDatesFromApi = bookListRes.data?.studentDates || [];

                                                                                                    // Also fetch dates for all book lists with the same name (shared book lists)
                                                                                                    let allStudentDates = [...studentDatesFromApi];
                                                                                                    if (bookListData && bookListData.name) {
                                                                                                        try {
                                                                                                            // Find all book lists with the same name
                                                                                                            const sameNameBookLists = bookLists.filter(bl =>
                                                                                                                bl.name === bookListData.name && parseInt(bl.active) === 1
                                                                                                            );

                                                                                                            // Fetch dates for all of them
                                                                                                            const datesPromises = sameNameBookLists.map(bl =>
                                                                                                                fetchBookStudentDatesByBookList({ bookListId: bl.id })
                                                                                                            );
                                                                                                            const datesResponses = await Promise.all(datesPromises);

                                                                                                            datesResponses.forEach(res => {
                                                                                                                if (res && res.status < 400 && res.data) {
                                                                                                                    const dates = getDataArray(res);
                                                                                                                    allStudentDates = [...allStudentDates, ...dates];
                                                                                                                }
                                                                                                            });
                                                                                                        } catch (error) {
                                                                                                            console.error('[BOOK LIST MANAGER] Error fetching dates for shared book lists:', error);
                                                                                                        }
                                                                                                    }

                                                                                                    // Update student dates state
                                                                                                    if (allStudentDates.length > 0) {
                                                                                                        setStudentDates(prev => {
                                                                                                            // Merge with existing dates, avoiding duplicates
                                                                                                            const existingMap = new Map(prev.map(sd => `${sd.student}_${sd.book_id}_${sd.book_list_id}`));
                                                                                                            allStudentDates.forEach(sd => {
                                                                                                                const key = `${sd.student}_${sd.book_id}_${sd.book_list_id}`;
                                                                                                                existingMap.set(key, sd);
                                                                                                            });
                                                                                                            return Array.from(existingMap.values());
                                                                                                        });
                                                                                                    }
                                                                                                
                                                                                                // Check if component is still mounted before updating state
                                                                                                if (!mounted.current) {
                                                                                                    console.log('[BOOK LIST MANAGER] Component unmounted, skipping state update');
                                                                                                    return;
                                                                                                }
                                                                                                
                                                                                                if (bookListData) {
                                                                                                    // Deduplicate items
                                                                                                    const uniqueItemsMap = new Map();
                                                                                                    bookListItems.forEach((item) => {
                                                                                                        const key = `${item.book_id}_${item.sort_order}`;
                                                                                                        if (!uniqueItemsMap.has(key)) {
                                                                                                            uniqueItemsMap.set(key, item);
                                                                                                        }
                                                                                                    });
                                                                                                    const deduplicatedItems = Array.from(uniqueItemsMap.values());
                                                                                                    
                                                                                                    // Enhance items with book data - use current books state
                                                                                                    // Note: books state should be up-to-date from refreshData() or initial load
                                                                                                    const enhancedItems = deduplicatedItems.map((item, idx) => {
                                                                                                        const book = books.find(b => {
                                                                                                            const bookId = b.id || b.book_id;
                                                                                                            const itemBookId = item.book_id;
                                                                                                            if (!bookId || !itemBookId) return false;
                                                                                                            return parseInt(bookId) === parseInt(itemBookId);
                                                                                                        });
                                                                                                        
                                                                                                        return {
                                                                                                            ...item,
                                                                                                            bookTitle: book?.title || 'Unknown Book',
                                                                                                            bookAuthor: book?.author || '',
                                                                                                            order: item.sort_order || idx + 1,
                                                                                                            sort_order: item.sort_order || idx + 1
                                                                                                        };
                                                                                                    });
                                                                                                    
                                                                                                    // Update selected book list data with fresh data from server
                                                                                                    // Use setTimeout to ensure this happens after any potential refreshData() calls
                                                                                                    // that might have been triggered by Socket events
                                                                                                    setTimeout(() => {
                                                                                                        if (!mounted.current) {
                                                                                                            console.log('[BOOK LIST MANAGER] Component unmounted during delayed update');
                                                                                                            return;
                                                                                                        }
                                                                                                        
                                                                                                        setSelectedBookListData({
                                                                                                            ...bookListData,
                                                                                                            parsedItems: enhancedItems,
                                                                                                            id: bookListData.id,
                                                                                                            isTemplate: false
                                                                                                        });
                                                                                                        
                                                                                                        // Clear any previous errors on success
                                                                                                        setApiError(null);
                                                                                                        
                                                                                                        console.log('[BOOK LIST MANAGER] Book list refreshed after auto-save, items count:', enhancedItems.length);
                                                                                                    }, 150); // Small delay to ensure refreshData() has finished if it was triggered
                                                                                                } else {
                                                                                                    console.error('[BOOK LIST MANAGER] Book list data not found after re-fetch');
                                                                                                    setApiError('Error: Could not refresh book list data. Please refresh the page.');
                                                                                                }
                                                                                            } catch (refreshError) {
                                                                                                console.error('[BOOK LIST MANAGER] Error refreshing book list after save:', refreshError);
                                                                                                setApiError('Error refreshing book list: ' + (refreshError.message || 'Please refresh the page.'));
                                                                                                // Keep the local state even if refresh fails
                                                                                            }
                                                                                        }
                                                                                    }
                                                                                } catch (error) {
                                                                                    console.error('[BOOK LIST MANAGER] Error auto-saving book:', error);
                                                                                    const errorMessage = error?.message || error?.toString() || 'Unknown error occurred';
                                                                                    setApiError(`Failed to save: ${errorMessage}. Please try saving manually.`);
                                                                                    
                                                                                    // Try to revert to prevent page breakage
                                                                                    try {
                                                                                        const bookListRes = await fetchBookListsId({ id: selectedBookListData.id });
                                                                                        const bookListData = bookListRes.data?.bookLists?.[0];
                                                                                        const bookListItems = bookListRes.data?.bookListItems || [];
                                                                                        
                                                                                        if (bookListData) {
                                                                                            const uniqueItemsMap = new Map();
                                                                                            bookListItems.forEach((item) => {
                                                                                                const key = `${item.book_id}_${item.sort_order}`;
                                                                                                if (!uniqueItemsMap.has(key)) {
                                                                                                    uniqueItemsMap.set(key, item);
                                                                                                }
                                                                                            });
                                                                                            const deduplicatedItems = Array.from(uniqueItemsMap.values());
                                                                                            
                                                                                            const enhancedItems = deduplicatedItems.map((item, idx) => {
                                                                                                const book = books.find(b => {
                                                                                                    const bookId = b.id || b.book_id;
                                                                                                    const itemBookId = item.book_id;
                                                                                                    if (!bookId || !itemBookId) return false;
                                                                                                    return parseInt(bookId) === parseInt(itemBookId);
                                                                                                });
                                                                                                
                                                                                                return {
                                                                                                    ...item,
                                                                                                    bookTitle: book?.title || 'Unknown Book',
                                                                                                    bookAuthor: book?.author || '',
                                                                                                    order: item.sort_order || idx + 1,
                                                                                                    sort_order: item.sort_order || idx + 1
                                                                                                };
                                                                                            });
                                                                                            
                                                                                            setSelectedBookListData({
                                                                                                ...bookListData,
                                                                                                parsedItems: enhancedItems,
                                                                                                id: bookListData.id,
                                                                                                isTemplate: false
                                                                                            });
                                                                                        }
                                                                                    } catch (revertError) {
                                                                                        console.error('[BOOK LIST MANAGER] Error reverting after exception:', revertError);
                                                                                    }
                                                                                }
                                                                            }
                                                                        }
                                                                    }}
                                                                    classNames="w-full"
                                                                />
                                                                );
                                                            })() : (
                                                                <a
                                                                    href="#"
                                                                    className="text-blue-600 underline hover:text-blue-800"
                                                                    onClick={(e) => e.preventDefault()}
                                                                >
                                                                    {item.bookTitle || 'Unknown Book'}
                                                                </a>
                                                            )}
                                                        </div>

                                                        {/* Date Assigned - Always editable (learning plan style) */}
                                                        <div className="col-span-2">
                                                            {(() => {
                                                                if (item.isNew || !item.book_id) return <span className="text-gray-400">-</span>;

                                                                const formik = formRef.current;
                                                                const selectedStudent = formik?.values?.selectedStudent;
                                                                const isGroup = selectedStudent && selectedStudent.value === 'all_group';
                                                                const selectedGroup = formik?.values?.selectedGroup;

                                                                let targetStudents = [];
                                                                if (isGroup && selectedGroup && selectedGroup.value !== -1) {
                                                                    const groupId = parseInt(selectedGroup.value);
                                                                    const membersInGroup = groupMembers.filter(gm => parseInt(gm.group_id) === groupId);
                                                                    const memberStudentIds = membersInGroup.map(gm => gm.student || gm.user_id);
                                                                    targetStudents = students.filter(s => memberStudentIds.includes(s.user_id));
                                                                } else if (selectedStudent && selectedStudent.value !== -1 && selectedStudent.value !== 'all_group') {
                                                                    // Use String() comparison to handle both UUID strings and numeric IDs
                                                                    const selectedStudentId = String(selectedStudent.value || '');
                                                                    const student = students.find(s =>
                                                                        String(s.user_id || '') === selectedStudentId ||
                                                                        parseInt(s.user_id) === parseInt(selectedStudent.value)
                                                                    );
                                                                    if (student) targetStudents = [student];
                                                                }

                                                                // Fallback: If targetStudents is empty but we have a selected book list with a student, use that
                                                                // Use String() comparison to handle both UUID strings and numeric IDs
                                                                // This ensures date columns are always editable for individual student book lists
                                                                if (targetStudents.length === 0 && selectedBookListData && selectedBookListData.student) {
                                                                    const bookListStudentId = String(selectedBookListData.student || '');
                                                                    const bookListStudent = students.find(s =>
                                                                        String(s.user_id || '') === bookListStudentId ||
                                                                        parseInt(s.user_id) === parseInt(selectedBookListData.student)
                                                                    );
                                                                    if (bookListStudent) {
                                                                        targetStudents = [bookListStudent];
                                                                    }
                                                                }

                                                                // Get aggregated dates for this book
                                                                // If no target students, show dates for all students who have dates
                                                                const bookDates = getBookDates(item.book_id, targetStudents, studentDates, students);
                                                                let assignedDates = bookDates.assigned;

                                                                // ALWAYS check if there's a pending saved date for this book/field
                                                                // This ensures the date is visible even if editingDate was cleared before state update renders
                                                                // CRITICAL: Always check and merge pendingSavedDatesRef BEFORE rendering
                                                                // This ensures dates are visible immediately after blur, even when switching fields
                                                                const pendingKey = `${item.book_id}_date_assigned`;
                                                                const pendingSaved = pendingSavedDatesRef.current.get(pendingKey);
                                                                if (pendingSaved && pendingSaved.date) {
                                                                    // Merge pending saved date into assignedDates for display
                                                                    const pendingDateFormatted = convertDateForInput(pendingSaved.date);

                                                                    // If we have target students, use the first one; otherwise, we'll need to find the student
                                                                    let targetStudentId = '';
                                                                    let pendingStudentName = 'Student';

                                                                    if (targetStudents.length > 0) {
                                                                        targetStudentId = String(targetStudents[0].user_id || targetStudents[0] || '');
                                                                        pendingStudentName = targetStudents[0].first_name && targetStudents[0].last_name
                                                                            ? `${targetStudents[0].first_name} ${targetStudents[0].last_name}`
                                                                            : (targetStudents[0].name || 'Student');
                                                                    } else if (selectedBookListData && selectedBookListData.student) {
                                                                        // Fallback: use the book list's student
                                                                        targetStudentId = String(selectedBookListData.student || '');
                                                                        const bookListStudent = students.find(s =>
                                                                            String(s.user_id || '') === targetStudentId ||
                                                                            parseInt(s.user_id) === parseInt(selectedBookListData.student)
                                                                        );
                                                                        if (bookListStudent) {
                                                                            pendingStudentName = bookListStudent.first_name && bookListStudent.last_name
                                                                                ? `${bookListStudent.first_name} ${bookListStudent.last_name}`
                                                                                : (bookListStudent.name || 'Student');
                                                                        }
                                                                    }

                                                                    // REPLACE existing date for this student with pending date, or add if it doesn't exist
                                                                    // This ensures the pending date is always shown, even if there's an old date
                                                                    if (targetStudentId) {
                                                                        const existingIndex = assignedDates.findIndex(ad => {
                                                                            const adStudentId = String(ad.student || '');
                                                                            return (adStudentId === targetStudentId || parseInt(adStudentId) === parseInt(targetStudentId));
                                                                        });

                                                                        if (existingIndex >= 0) {
                                                                            // REPLACE existing date with pending date
                                                                            assignedDates = [...assignedDates];
                                                                            assignedDates[existingIndex] = {
                                                                                student: targetStudentId,
                                                                                studentName: pendingStudentName,
                                                                                date: pendingDateFormatted,
                                                                                dateRaw: pendingSaved.date
                                                                            };
                                                                        } else {
                                                                            // Add pending date if it doesn't exist
                                                                            assignedDates = [...assignedDates, {
                                                                                student: targetStudentId,
                                                                                studentName: pendingStudentName,
                                                                                date: pendingDateFormatted,
                                                                                dateRaw: pendingSaved.date
                                                                            }];
                                                                        }
                                                                    }
                                                                }

                                                                // REMOVED: isEditing check that was preventing always-editable DatePicker from showing
                                                                // The always-editable DatePicker path below (line 3809+) handles all cases
                                                                // including when user is typing/editing, so we don't need a separate "editing mode"
                                                                // const isEditing = editingDate && editingDate.bookId === item.book_id && editingDate.field === 'date_assigned';

                                                                // REMOVED: Old editing mode DatePicker that didn't have Today/Clear buttons
                                                                // This entire block (lines 3593-3732) is commented out because it was blocking
                                                                // the always-editable DatePicker path below (line 3809+)
                                                                /*
                                                                if (isEditing) {
                                                                    // Determine the date to pre-fill based on common vs individual logic
                                                                    let currentDate = '';

                                                                    if (isGroup && targetStudents.length > 1 && assignedDates.length > 0) {
                                                                        // For common book lists: Check if dates are common
                                                                        const firstDate = assignedDates[0].dateRaw || assignedDates[0].date;
                                                                        const allSame = assignedDates.every(ad => {
                                                                            const adDate = ad.dateRaw || ad.date;
                                                                            return adDate === firstDate;
                                                                        });

                                                                        if (allSame && assignedDates.length === targetStudents.length) {
                                                                            // All students have the same date - use it
                                                                            currentDate = convertDateForInput(firstDate);
                                                                        }
                                                                        // If dates are different, currentDate stays empty (field will be blank for new date)
                                                                    } else if (!isGroup && targetStudents.length === 1 && assignedDates.length > 0) {
                                                                        // Individual student book list - get their specific date
                                                                        const existingDate = assignedDates.find(ad => {
                                                                            const adStudentId = String(ad.student || '');
                                                                            const targetStudentId = String(targetStudents[0].user_id || targetStudents[0] || '');
                                                                            return adStudentId === targetStudentId || parseInt(adStudentId) === parseInt(targetStudentId);
                                                                        });
                                                                        if (existingDate) {
                                                                            currentDate = convertDateForInput(existingDate.dateRaw || existingDate.date);
                                                                        }
                                                                    } else if (targetStudents.length > 0) {
                                                                        // Fallback: Get date for first target student
                                                                        const existingDate = assignedDates.find(ad => {
                                                                            const adStudentId = String(ad.student || '');
                                                                            const targetStudentId = String(targetStudents[0].user_id || targetStudents[0] || '');
                                                                            return adStudentId === targetStudentId || parseInt(adStudentId) === parseInt(targetStudentId);
                                                                        });
                                                                        if (existingDate) {
                                                                            currentDate = convertDateForInput(existingDate.dateRaw || existingDate.date);
                                                                        }
                                                                    }

                                                                    // Initialize editingDateValue if it's empty or different book
                                                                    // Use a ref to track if we've initialized for this book to avoid resetting during re-renders
                                                                    if (editingDateValue === '' || editingDate.bookId !== item.book_id) {
                                                                        // Only initialize if ref doesn't have a value for this book, or if it's a different book
                                                                        if (editingDateValueRef.current === '' || editingDate.bookId !== item.book_id) {
                                                                            editingDateValueRef.current = currentDate;
                                                                            // Use setTimeout to avoid calling setState during render
                                                                            setTimeout(() => {
                                                                                setEditingDateValue(currentDate);
                                                                            }, 0);
                                                                        }
                                                                    }

                                                                    // Use editingDateValue if it has a value, otherwise use the ref
                                                                    const displayValue = editingDateValue || editingDateValueRef.current || '';

                                                                    // Use a stable key to prevent React from recreating the component
                                                                    // This ensures the input maintains focus and value across re-renders
                                                                    const datePickerKey = `date-assigned-${item.book_id}-${editingDate.bookId === item.book_id ? 'editing' : 'display'}`;

                                                                    return (
                                                                    <DatePicker
                                                                            key={datePickerKey}
                                                                        id={`date-assigned-${item.book_id}`}
                                                                            name={`date-assigned-${item.book_id}`}
                                                                            label="Date Assigned"
                                                                            value={displayValue || ''}
                                                                        onChange={(e) => {
                                                                                const newValue = e.target.value;
                                                                                // Update both ref and state immediately to keep them in sync
                                                                                editingDateValueRef.current = newValue;
                                                                                setEditingDateValue(newValue);

                                                                                // CRITICAL: Set pendingSavedDatesRef IMMEDIATELY on change
                                                                                // This ensures the date is visible even if user clicks another field before blur
                                                                                if (newValue) {
                                                                                    const pendingKey = `${item.book_id}_date_assigned`;
                                                                                    pendingSavedDatesRef.current.set(pendingKey, {
                                                                                        date: newValue,
                                                                                        timestamp: Date.now(),
                                                                                        bookId: item.book_id,
                                                                                        field: 'date_assigned'
                                                                                    });
                                                                                }
                                                                                // Don't save on change - only update local state
                                                                                // Save will happen on blur
                                                                            }}
                                                                            onBlur={async (e) => {
                                                                                // Use the actual input value, not state (state might be stale)
                                                                                const blurValue = e.target.value || editingDateValueRef.current || '';
                                                                                console.log('[BOOK LIST MANAGER] onBlur fired:', { blurValue, editingDateValue, refValue: editingDateValueRef.current, bookId: item.book_id });

                                                                                if (blurValue) {
                                                                                    // CRITICAL: Update ref FIRST before any async operations
                                                                                    editingDateValueRef.current = blurValue;
                                                                                    // Also update state to keep them in sync
                                                                                    setEditingDateValue(blurValue);

                                                                                    // CRITICAL: Set pendingSavedDatesRef IMMEDIATELY before calling handleDateChange
                                                                                    // This ensures the date is visible even if user clicks another field before handleDateChange completes
                                                                                    const pendingKey = `${item.book_id}_date_assigned`;
                                                                                    pendingSavedDatesRef.current.set(pendingKey, {
                                                                                        date: blurValue,
                                                                                        timestamp: Date.now(),
                                                                                        bookId: item.book_id,
                                                                                        field: 'date_assigned'
                                                                                    });

                                                                                    // DON'T clear editing state here - let handleDateChange do it after save completes
                                                                                    // This prevents the field from going blank when switching fields
                                                                                    try {
                                                                                        // handleDateChange will:
                                                                                        // 1. Make optimistic update to studentDates (ensures date is visible)
                                                                                        // 2. Save to backend
                                                                                        // 3. Only clear editingDate if still editing the same field (prevents clearing when user clicks another field)
                                                                                        await handleDateChange(item.book_id, 'date_assigned', blurValue || null);
                                                                                        console.log('[BOOK LIST MANAGER] Date saved successfully in onBlur');
                                                                                    } catch (error) {
                                                                                        console.error('[BOOK LIST MANAGER] Error saving date on blur:', error);
                                                                                        // Don't clear editing state on error - let user see the date and try again
                                                                                        // Keep the value in both ref and state
                                                                                        editingDateValueRef.current = blurValue;
                                                                                        setEditingDateValue(blurValue);
                                                                                        // Keep pendingSavedDatesRef so date remains visible
                                                                                    }
                                                                                } else {
                                                                                    // No value entered, clear after a delay to allow onClick on another field to fire first
                                                                                    setTimeout(() => {
                                                                                        // Only clear if we're still editing this specific field
                                                                                        if (editingDate && editingDate.bookId === item.book_id && editingDate.field === 'date_assigned') {
                                                                                            editingDateValueRef.current = '';
                                                                                setEditingDate(null);
                                                                                            setEditingDateValue('');
                                                                                        }
                                                                                    }, 100);
                                                                                }
                                                                            }}
                                                                            classNames="w-full"
                                                                            autoFocus
                                                                        />
                                                                    );
                                                                }
                                                                */
                                                                // END REMOVED: Old editing mode DatePicker

                                                                // Always show the date column - never return early with just "-"
                                                                // If no dates exist, check pendingSavedDatesRef to see if a date was just saved
                                                                // This ensures the date is visible even if the optimistic update hasn't been reflected yet
                                                                // IMPORTANT: If targetStudents.length > 0 OR selectedBookListData.student exists, always use the always-editable DatePicker path
                                                                if (assignedDates.length === 0) {
                                                                    // Only show "-" if we truly have no way to determine the target student
                                                                    // Check both targetStudents AND selectedBookListData.student as fallback
                                                                    if (targetStudents.length === 0 && (!selectedBookListData || !selectedBookListData.student)) {
                                                                        return <span className="text-gray-400">-</span>;
                                                                    }
                                                                    // If targetStudents.length > 0 OR selectedBookListData.student exists, continue to the always-editable DatePicker path below
                                                                    // (Don't return early - always show DatePicker when students are selected or book list has a student)
                                                                }

                                                                // Show delete icon for existing dates
                                                                const canDelete = targetStudents.length > 0;

                                                                // Format similar to Read By: "All (3): Student1 (date1), Student2 (date2)"
                                                                const assignedNames = assignedDates.map(ad => `${ad.studentName} (${ad.date})`);

                                                                // NEW LOGIC: For common book lists (groups), check if dates are common (same) for all students
                                                                // If dates are common: Pre-fill date field, editable
                                                                // If dates are different: Show dates below with student names, keep field blank for new dates
                                                                let isCommonDate = false;
                                                                let commonDateValue = '';
                                                                let differentDates = [];

                                                                if (isGroup && targetStudents.length > 1 && assignedDates.length > 0) {
                                                                    // Check if all students have the same date
                                                                    const firstDate = assignedDates[0].dateRaw || assignedDates[0].date;
                                                                    isCommonDate = assignedDates.every(ad => {
                                                                        const adDate = ad.dateRaw || ad.date;
                                                                        return adDate === firstDate;
                                                                    });

                                                                    if (isCommonDate && assignedDates.length === targetStudents.length) {
                                                                        // All students have the same date - use it for the field
                                                                        commonDateValue = convertDateForInput(firstDate);
                                                                            } else {
                                                                        // Dates are different - show them below, keep field blank
                                                                        differentDates = assignedDates;
                                                                    }
                                                                } else if (!isGroup && targetStudents.length === 1 && assignedDates.length > 0) {
                                                                    // Individual student book list - pre-fill with their date
                                                                    const studentDate = assignedDates.find(ad => {
                                                                        const adStudentId = String(ad.student || '');
                                                                        const targetStudentId = String(targetStudents[0].user_id || targetStudents[0] || '');
                                                                        return adStudentId === targetStudentId || parseInt(adStudentId) === parseInt(targetStudentId);
                                                                    });
                                                                    if (studentDate) {
                                                                        commonDateValue = convertDateForInput(studentDate.dateRaw || studentDate.date);
                                                                    }
                                                                }

                                                                // Always-editable date field (learning plan style)
                                                                // Get current date value following previous logic:
                                                                // - Individual student: Pre-fill with their previously saved dates
                                                                // - Common book list (group) with common dates: Pre-fill with common date
                                                                // - Common book list (group) with different dates: Field remains blank (different dates shown below)
                                                                const currentDateValue = getCurrentDateValue(
                                                                    item.book_id,
                                                                    'date_assigned',
                                                                    assignedDates,
                                                                    [],
                                                                    targetStudents,
                                                                    isGroup,
                                                                    commonDateValue,
                                                                    isCommonDate,
                                                                    differentDates
                                                                );

                                                                // Check if we're currently editing this field
                                                                const isEditingAssigned = editingDate && editingDate.bookId === item.book_id && editingDate.field === 'date_assigned';

                                                                // Get display value (use editing value if editing, otherwise use current date value)
                                                                const displayDateValue = isEditingAssigned
                                                                    ? (editingDateValue || editingDateValueRef.current || currentDateValue)
                                                                    : currentDateValue;

                                                                return (
                                                                    <div className="flex flex-col gap-1">
                                                                        {/* Always-editable date field with Today/Clear buttons */}
                                                                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full">
                                                                            <div className="flex-1 min-w-0">
                                                                                {/* Always show DatePicker if we have a book_id and targetStudents (even if empty, we'll use fallback) */}
                                                                                {item.book_id && (targetStudents.length > 0 || (selectedBookListData && selectedBookListData.student)) ? (
                                                                                    <DatePicker
                                                                                        id={`date-assigned-${item.book_id}`}
                                                                                        name={`date-assigned-${item.book_id}`}
                                                                                        label=""
                                                                                        value={displayDateValue || ''}
                                                                                        onChange={(e) => {
                                                                                            const newValue = e.target.value;
                                                                                            editingDateValueRef.current = newValue;
                                                                                            setEditingDateValue(newValue);

                                                                                            // Set pending saved date immediately
                                                                                            if (newValue) {
                                                                                                const pendingKey = `${item.book_id}_date_assigned`;
                                                                                                pendingSavedDatesRef.current.set(pendingKey, {
                                                                                                    date: newValue,
                                                                                                    timestamp: Date.now(),
                                                                                                    bookId: item.book_id,
                                                                                                    field: 'date_assigned'
                                                                                                });
                                                                                            }

                                                                                            // Set editing state if not already editing
                                                                                            if (!isEditingAssigned) {
                                                                                                setEditingDate({ bookId: item.book_id, field: 'date_assigned' });
                                                                                            }
                                                                                        }}
                                                                                        onBlur={async (e) => {
                                                                                            const blurValue = e.target.value || editingDateValueRef.current || '';
                                                                                            if (blurValue) {
                                                                                                editingDateValueRef.current = blurValue;
                                                                                                setEditingDateValue(blurValue);
                                                                                                const pendingKey = `${item.book_id}_date_assigned`;
                                                                                                pendingSavedDatesRef.current.set(pendingKey, {
                                                                                                    date: blurValue,
                                                                                                    timestamp: Date.now(),
                                                                                                    bookId: item.book_id,
                                                                                                    field: 'date_assigned'
                                                                                                });
                                                                                                try {
                                                                                                    await handleDateChange(item.book_id, 'date_assigned', blurValue || null);
                                                                                                } catch (error) {
                                                                                                    console.error('[BOOK LIST MANAGER] Error saving date on blur:', error);
                                                                                                }
                                                                            }
                                                                        }}
                                                                        classNames="w-full"
                                                                    />
                                                                                ) : (
                                                                                    <span className="text-gray-400">-</span>
                                                                                )}
                                                                            </div>
                                                                            {/* CRITICAL: Buttons must be INSIDE the same condition as DatePicker to ensure they always show together */}
                                                                            {item.book_id && (targetStudents.length > 0 || (selectedBookListData && selectedBookListData.student)) ? (
                                                                                <div className="flex flex-row gap-1 flex-shrink-0">
                                                                                    <button
                                                                                        type="button"
                                                                                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors min-w-[32px] min-h-[32px] flex items-center justify-center"
                                                                                        title="Set today's date"
                                                                                        onClick={async (e) => {
                                                                                            e.preventDefault();
                                                                                            e.stopPropagation();
                                                                                            const todayDate = getTodayDate();
                                                                                            // Update state and ref - this will trigger DatePicker to update via controlled value
                                                                                            editingDateValueRef.current = todayDate;
                                                                                            setEditingDateValue(todayDate);
                                                                                            // Keep editing state active so buttons remain visible
                                                                                            setEditingDate({ bookId: item.book_id, field: 'date_assigned' });
                                                                                            // Set pending saved date
                                                                                            const pendingKey = `${item.book_id}_date_assigned`;
                                                                                            pendingSavedDatesRef.current.set(pendingKey, {
                                                                                                date: todayDate,
                                                                                                timestamp: Date.now(),
                                                                                                bookId: item.book_id,
                                                                                                field: 'date_assigned'
                                                                                            });
                                                                                            try {
                                                                                                await handleDateChange(item.book_id, 'date_assigned', todayDate);
                                                                                            } catch (error) {
                                                                                                // Silent failure for auto-save operations - just log to console
                                                                                                // Error messages will only show when main Save button is clicked
                                                                                                console.error('[BOOK LIST MANAGER] Error saving today date (auto-save):', error);
                                                                                            }
                                                                                        }}
                                                                    >
                                                                                        <i className="fas fa-calendar-day text-base"></i>
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors min-w-[32px] min-h-[32px] flex items-center justify-center"
                                                                                        title="Clear date"
                                                                                        onClick={async (e) => {
                                                                                            e.preventDefault();
                                                                                            e.stopPropagation();
                                                                                            // Update state and ref - this will trigger DatePicker to update via controlled value
                                                                                            editingDateValueRef.current = '';
                                                                                            setEditingDateValue('');
                                                                                            // Keep editing state active so buttons remain visible
                                                                                            setEditingDate({ bookId: item.book_id, field: 'date_assigned' });
                                                                                            // Clear pending saved date
                                                                                            const pendingKey = `${item.book_id}_date_assigned`;
                                                                                            pendingSavedDatesRef.current.delete(pendingKey);
                                                                                            try {
                                                                                                await handleDateChange(item.book_id, 'date_assigned', null);
                                                                                            } catch (error) {
                                                                                                // Silent failure for auto-save operations - just log to console
                                                                                                // Error messages will only show when main Save button is clicked
                                                                                                console.error('[BOOK LIST MANAGER] Error clearing date (auto-save):', error);
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        <i className="fas fa-times text-base"></i>
                                                                                    </button>
                                                                                </div>
                                                                            ) : null}
                                                                        </div>

                                                                        {/* Show different dates below for groups with non-common dates */}
                                                                        {isGroup && !isCommonDate && differentDates.length > 0 && (
                                                                            <div className="text-xs text-gray-600 mt-1">
                                                                                <div className="font-semibold mb-1">Existing dates:</div>
                                                                                {differentDates.map((ad, idx) => (
                                                                                    <div key={idx} className="text-gray-700">
                                                                                        {ad.studentName}: {ad.date}
                                                                                    </div>
                                                                                ))}
                                                                </div>
                                                            )}
                                                                    </div>
                                                                );
                                                            })()}
                                                        </div>

                                                        {/* Date Completed - Shows all dates for all students (similar to Read By) */}
                                                        <div className="col-span-2">
                                                            {(() => {
                                                                if (item.isNew || !item.book_id) return <span className="text-gray-400">-</span>;

                                                                const formik = formRef.current;
                                                                const selectedStudent = formik?.values?.selectedStudent;
                                                                const isGroup = selectedStudent && selectedStudent.value === 'all_group';
                                                                const selectedGroup = formik?.values?.selectedGroup;

                                                                let targetStudents = [];
                                                                if (isGroup && selectedGroup && selectedGroup.value !== -1) {
                                                                    const groupId = parseInt(selectedGroup.value);
                                                                    const membersInGroup = groupMembers.filter(gm => parseInt(gm.group_id) === groupId);
                                                                    const memberStudentIds = membersInGroup.map(gm => gm.student || gm.user_id);
                                                                    targetStudents = students.filter(s => memberStudentIds.includes(s.user_id));
                                                                } else if (selectedStudent && selectedStudent.value !== -1 && selectedStudent.value !== 'all_group') {
                                                                    const student = students.find(s => parseInt(s.user_id) === parseInt(selectedStudent.value));
                                                                    if (student) targetStudents = [student];
                                                                }

                                                                // Fallback: If targetStudents is empty but we have a selected book list with a student, use that
                                                                // Use String() comparison to handle both UUID strings and numeric IDs
                                                                // CRITICAL: This fallback is essential because after save, formik.values might be reset
                                                                // but selectedBookListData.student still contains the correct student ID
                                                                if (targetStudents.length === 0 && selectedBookListData && selectedBookListData.student) {
                                                                    const bookListStudentId = String(selectedBookListData.student || '');
                                                                    const bookListStudent = students.find(s =>
                                                                        String(s.user_id || '') === bookListStudentId ||
                                                                        parseInt(s.user_id) === parseInt(selectedBookListData.student)
                                                                    );
                                                                    if (bookListStudent) {
                                                                        targetStudents = [bookListStudent];
                                                                        console.log('[BOOK LIST MANAGER] Using fallback targetStudent from selectedBookListData (date_completed):', bookListStudent.first_name, bookListStudent.last_name);
                                                                    }
                                                                }

                                                                // Additional fallback: If still empty, try to get student from formik values even if they're stale
                                                                // This handles cases where formik values exist but the student object wasn't found in the students array
                                                                if (targetStudents.length === 0 && selectedStudent && selectedStudent.value !== -1 && selectedStudent.value !== 'all_group') {
                                                                    const selectedStudentId = String(selectedStudent.value || '');
                                                                    const student = students.find(s =>
                                                                        String(s.user_id || '') === selectedStudentId ||
                                                                        parseInt(s.user_id) === parseInt(selectedStudent.value)
                                                                    );
                                                                    if (student) {
                                                                        targetStudents = [student];
                                                                        console.log('[BOOK LIST MANAGER] Using fallback targetStudent from formik values (date_completed):', student.first_name, student.last_name);
                                                                    }
                                                                }

                                                                // Get aggregated dates for this book
                                                                // If no target students, show dates for all students who have dates
                                                                const bookDates = getBookDates(item.book_id, targetStudents, studentDates, students);
                                                                let completedDates = bookDates.completed;

                                                                // CRITICAL: Always check and merge pendingSavedDatesRef BEFORE rendering
                                                                // This ensures dates are visible immediately after blur, even when switching fields
                                                                const pendingKeyCompleted = `${item.book_id}_date_completed`;
                                                                const pendingSavedCompleted = pendingSavedDatesRef.current.get(pendingKeyCompleted);
                                                                if (pendingSavedCompleted && pendingSavedCompleted.date) {
                                                                    // Merge pending saved date into completedDates for display
                                                                    const pendingDateFormatted = convertDateForInput(pendingSavedCompleted.date);

                                                                    // If we have target students, use the first one; otherwise, we'll need to find the student
                                                                    let targetStudentId = '';
                                                                    let pendingStudentName = 'Student';

                                                                    if (targetStudents.length > 0) {
                                                                        targetStudentId = String(targetStudents[0].user_id || targetStudents[0] || '');
                                                                        pendingStudentName = targetStudents[0].first_name && targetStudents[0].last_name
                                                                            ? `${targetStudents[0].first_name} ${targetStudents[0].last_name}`
                                                                            : (targetStudents[0].name || 'Student');
                                                                    } else if (selectedBookListData && selectedBookListData.student) {
                                                                        // Fallback: use the book list's student
                                                                        targetStudentId = String(selectedBookListData.student || '');
                                                                        const bookListStudent = students.find(s =>
                                                                            String(s.user_id || '') === targetStudentId ||
                                                                            parseInt(s.user_id) === parseInt(selectedBookListData.student)
                                                                        );
                                                                        if (bookListStudent) {
                                                                            pendingStudentName = bookListStudent.first_name && bookListStudent.last_name
                                                                                ? `${bookListStudent.first_name} ${bookListStudent.last_name}`
                                                                                : (bookListStudent.name || 'Student');
                                                                        }
                                                                    }

                                                                    // REPLACE existing date for this student with pending date, or add if it doesn't exist
                                                                    // This ensures the pending date is always shown, even if there's an old date
                                                                    if (targetStudentId) {
                                                                        const existingIndex = completedDates.findIndex(cd => {
                                                                            const cdStudentId = String(cd.student || '');
                                                                            return (cdStudentId === targetStudentId || parseInt(cdStudentId) === parseInt(targetStudentId));
                                                                        });

                                                                        if (existingIndex >= 0) {
                                                                            // REPLACE existing date with pending date
                                                                            completedDates = [...completedDates];
                                                                            completedDates[existingIndex] = {
                                                                                student: targetStudentId,
                                                                                studentName: pendingStudentName,
                                                                                date: pendingDateFormatted,
                                                                                dateRaw: pendingSavedCompleted.date
                                                                            };
                                                                        } else {
                                                                            // Add pending date if it doesn't exist
                                                                            completedDates = [...completedDates, {
                                                                                student: targetStudentId,
                                                                                studentName: pendingStudentName,
                                                                                date: pendingDateFormatted,
                                                                                dateRaw: pendingSavedCompleted.date
                                                                            }];
                                                                        }
                                                                    }
                                                                }

                                                                // REMOVED: Old isEditingCompleted check that was blocking always-editable DatePicker
                                                                // const isEditingCompleted = editingDate && editingDate.bookId === item.book_id && editingDate.field === 'date_completed';
                                                                /*
                                                                if (isEditingCompleted) {
                                                                    // Determine the date to pre-fill based on common vs individual logic
                                                                    let currentDate = '';

                                                                    if (isGroup && targetStudents.length > 1 && completedDates.length > 0) {
                                                                        // For common book lists: Check if dates are common
                                                                        const firstDate = completedDates[0].dateRaw || completedDates[0].date;
                                                                        const allSame = completedDates.every(cd => {
                                                                            const cdDate = cd.dateRaw || cd.date;
                                                                            return cdDate === firstDate;
                                                                        });

                                                                        if (allSame && completedDates.length === targetStudents.length) {
                                                                            // All students have the same date - use it
                                                                            currentDate = convertDateForInput(firstDate);
                                                                        }
                                                                        // If dates are different, currentDate stays empty (field will be blank for new date)
                                                                    } else if (!isGroup && targetStudents.length === 1 && completedDates.length > 0) {
                                                                        // Individual student book list - get their specific date
                                                                        const existingDate = completedDates.find(cd => {
                                                                            const cdStudentId = String(cd.student || '');
                                                                            const targetStudentId = String(targetStudents[0].user_id || targetStudents[0] || '');
                                                                            return cdStudentId === targetStudentId || parseInt(cdStudentId) === parseInt(targetStudentId);
                                                                        });
                                                                        if (existingDate) {
                                                                            currentDate = convertDateForInput(existingDate.dateRaw || existingDate.date);
                                                                        }
                                                                    } else if (targetStudents.length > 0) {
                                                                        // Fallback: Get date for first target student
                                                                        const existingDate = completedDates.find(cd => {
                                                                            const cdStudentId = String(cd.student || '');
                                                                            const targetStudentId = String(targetStudents[0].user_id || targetStudents[0] || '');
                                                                            return cdStudentId === targetStudentId || parseInt(cdStudentId) === parseInt(targetStudentId);
                                                                        });
                                                                        if (existingDate) {
                                                                            currentDate = convertDateForInput(existingDate.dateRaw || existingDate.date);
                                                                        }
                                                                    }

                                                                    // Initialize editingDateValue if it's empty or different book
                                                                    // Use a ref to track if we've initialized for this book to avoid resetting during re-renders
                                                                    if (editingDateValue === '' || editingDate.bookId !== item.book_id) {
                                                                        // Only initialize if ref doesn't have a value for this book, or if it's a different book
                                                                        if (editingDateValueRef.current === '' || editingDate.bookId !== item.book_id) {
                                                                            editingDateValueRef.current = currentDate;
                                                                            // Use setTimeout to avoid calling setState during render
                                                                            setTimeout(() => {
                                                                                setEditingDateValue(currentDate);
                                                                            }, 0);
                                                                        }
                                                                    }

                                                                    // Use editingDateValue if it has a value, otherwise use the ref
                                                                    const displayValue = editingDateValue || editingDateValueRef.current || '';

                                                                    // Use a stable key to prevent React from recreating the component
                                                                    // This ensures the input maintains focus and value across re-renders
                                                                    const datePickerKey = `date-completed-${item.book_id}-${editingDate.bookId === item.book_id ? 'editing' : 'display'}`;

                                                                    return (
                                                                    <DatePicker
                                                                            key={datePickerKey}
                                                                        id={`date-completed-${item.book_id}`}
                                                                            name={`date-completed-${item.book_id}`}
                                                                            label="Date Completed"
                                                                            value={displayValue || ''}
                                                                        onChange={(e) => {
                                                                                const newValue = e.target.value;
                                                                                // Update both ref and state immediately to keep them in sync
                                                                                editingDateValueRef.current = newValue;
                                                                                setEditingDateValue(newValue);

                                                                                // CRITICAL: Set pendingSavedDatesRef IMMEDIATELY on change
                                                                                // This ensures the date is visible even if user clicks another field before blur
                                                                                if (newValue) {
                                                                                    const pendingKey = `${item.book_id}_date_completed`;
                                                                                    pendingSavedDatesRef.current.set(pendingKey, {
                                                                                        date: newValue,
                                                                                        timestamp: Date.now(),
                                                                                        bookId: item.book_id,
                                                                                        field: 'date_completed'
                                                                                    });
                                                                                }
                                                                                // Don't save on change - only update local state
                                                                                // Save will happen on blur
                                                                            }}
                                                                            onBlur={async (e) => {
                                                                                // Use the actual input value, not state (state might be stale)
                                                                                const blurValue = e.target.value || editingDateValueRef.current || '';
                                                                                console.log('[BOOK LIST MANAGER] onBlur fired (completed):', { blurValue, editingDateValue, refValue: editingDateValueRef.current, bookId: item.book_id });

                                                                                if (blurValue) {
                                                                                    // CRITICAL: Update ref FIRST before any async operations
                                                                                    editingDateValueRef.current = blurValue;
                                                                                    // Also update state to keep them in sync
                                                                                    setEditingDateValue(blurValue);

                                                                                    // CRITICAL: Set pendingSavedDatesRef IMMEDIATELY before calling handleDateChange
                                                                                    // This ensures the date is visible even if user clicks another field before handleDateChange completes
                                                                                    const pendingKey = `${item.book_id}_date_completed`;
                                                                                    pendingSavedDatesRef.current.set(pendingKey, {
                                                                                        date: blurValue,
                                                                                        timestamp: Date.now(),
                                                                                        bookId: item.book_id,
                                                                                        field: 'date_completed'
                                                                                    });

                                                                                    // DON'T clear editing state here - let handleDateChange do it after save completes
                                                                                    // This prevents the field from going blank when switching fields
                                                                                    try {
                                                                                        // handleDateChange will:
                                                                                        // 1. Make optimistic update to studentDates (ensures date is visible)
                                                                                        // 2. Save to backend
                                                                                        // 3. Only clear editingDate if still editing the same field (prevents clearing when user clicks another field)
                                                                                        await handleDateChange(item.book_id, 'date_completed', blurValue || null);
                                                                                        console.log('[BOOK LIST MANAGER] Date completed saved successfully in onBlur');
                                                                                    } catch (error) {
                                                                                        console.error('[BOOK LIST MANAGER] Error saving date on blur:', error);
                                                                                        // Don't clear editing state on error - let user see the date and try again
                                                                                        // Keep the value in both ref and state
                                                                                        editingDateValueRef.current = blurValue;
                                                                                        setEditingDateValue(blurValue);
                                                                                        // Keep pendingSavedDatesRef so date remains visible
                                                                                    }
                                                                                } else {
                                                                                    // No value entered, clear after a delay to allow onClick on another field to fire first
                                                                                    setTimeout(() => {
                                                                                        // Only clear if we're still editing this specific field
                                                                                        if (editingDate && editingDate.bookId === item.book_id && editingDate.field === 'date_completed') {
                                                                                            editingDateValueRef.current = '';
                                                                                setEditingDate(null);
                                                                                            setEditingDateValue('');
                                                                                        }
                                                                                    }, 100);
                                                                                }
                                                                            }}
                                                                            classNames="w-full"
                                                                            autoFocus
                                                                        />
                                                                    );
                                                                }
                                                                */
                                                                // END REMOVED: Old editing mode DatePicker for date_completed

                                                                // Always show the date column - never return early with just "-"
                                                                // If no dates exist, check pendingSavedDatesRef to see if a date was just saved
                                                                // This ensures the date is visible even if the optimistic update hasn't been reflected yet
                                                                // IMPORTANT: If targetStudents.length > 0 OR selectedBookListData.student exists, always use the always-editable DatePicker path
                                                                if (completedDates.length === 0) {
                                                                    // Only show "-" if we truly have no way to determine the target student
                                                                    // Check both targetStudents AND selectedBookListData.student as fallback
                                                                    if (targetStudents.length === 0 && (!selectedBookListData || !selectedBookListData.student)) {
                                                                        return <span className="text-gray-400">-</span>;
                                                                    }
                                                                    // If targetStudents.length > 0 OR selectedBookListData.student exists, continue to the always-editable DatePicker path below
                                                                    // (Don't return early - always show DatePicker when students are selected or book list has a student)
                                                                }

                                                                // Show delete icon for existing dates
                                                                const canDeleteCompleted = targetStudents.length > 0;

                                                                // Format similar to Read By: "All (3): Student1 (date1), Student2 (date2)"
                                                                const completedNames = completedDates.map(cd => `${cd.studentName} (${cd.date})`);

                                                                // NEW LOGIC: For common book lists (groups), check if dates are common (same) for all students
                                                                // If dates are common: Pre-fill date field, editable
                                                                // If dates are different: Show dates below with student names, keep field blank for new dates
                                                                let isCommonDateCompleted = false;
                                                                let commonDateValueCompleted = '';
                                                                let differentDatesCompleted = [];

                                                                if (isGroup && targetStudents.length > 1 && completedDates.length > 0) {
                                                                    // Check if all students have the same date
                                                                    const firstDate = completedDates[0].dateRaw || completedDates[0].date;
                                                                    isCommonDateCompleted = completedDates.every(cd => {
                                                                        const cdDate = cd.dateRaw || cd.date;
                                                                        return cdDate === firstDate;
                                                                    });

                                                                    if (isCommonDateCompleted && completedDates.length === targetStudents.length) {
                                                                        // All students have the same date - use it for the field
                                                                        commonDateValueCompleted = convertDateForInput(firstDate);
                                                                            } else {
                                                                        // Dates are different - show them below, keep field blank
                                                                        differentDatesCompleted = completedDates;
                                                                    }
                                                                } else if (!isGroup && targetStudents.length === 1 && completedDates.length > 0) {
                                                                    // Individual student book list - pre-fill with their date
                                                                    const studentDate = completedDates.find(cd => {
                                                                        const cdStudentId = String(cd.student || '');
                                                                        const targetStudentId = String(targetStudents[0].user_id || targetStudents[0] || '');
                                                                        return cdStudentId === targetStudentId || parseInt(cdStudentId) === parseInt(targetStudentId);
                                                                    });
                                                                    if (studentDate) {
                                                                        commonDateValueCompleted = convertDateForInput(studentDate.dateRaw || studentDate.date);
                                                                    }
                                                                }

                                                                // Always-editable date field (learning plan style)
                                                                // Get current date value following previous logic:
                                                                // - Individual student: Pre-fill with their previously saved dates
                                                                // - Common book list (group) with common dates: Pre-fill with common date
                                                                // - Common book list (group) with different dates: Field remains blank (different dates shown below)
                                                                const currentDateValueCompleted = getCurrentDateValue(
                                                                    item.book_id,
                                                                    'date_completed',
                                                                    [],
                                                                    completedDates,
                                                                    targetStudents,
                                                                    isGroup,
                                                                    commonDateValueCompleted,
                                                                    isCommonDateCompleted,
                                                                    differentDatesCompleted
                                                                );

                                                                // Check if we're currently editing this field
                                                                const isEditingCompletedField = editingDate && editingDate.bookId === item.book_id && editingDate.field === 'date_completed';

                                                                // Get display value (use editing value if editing, otherwise use current date value)
                                                                const displayDateValueCompleted = isEditingCompletedField
                                                                    ? (editingDateValue || editingDateValueRef.current || currentDateValueCompleted)
                                                                    : currentDateValueCompleted;

                                                                return (
                                                                    <div className="flex flex-col gap-1">
                                                                        {/* Always-editable date field with Today/Clear buttons */}
                                                                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full">
                                                                            <div className="flex-1 min-w-0">
                                                                                {/* Always show DatePicker if we have a book_id and targetStudents (even if empty, we'll use fallback) */}
                                                                                {item.book_id && (targetStudents.length > 0 || (selectedBookListData && selectedBookListData.student)) ? (
                                                                                    <DatePicker
                                                                                        id={`date-completed-${item.book_id}`}
                                                                                        name={`date-completed-${item.book_id}`}
                                                                                        label=""
                                                                                        value={displayDateValueCompleted || ''}
                                                                                        onChange={(e) => {
                                                                                            const newValue = e.target.value;
                                                                                            editingDateValueRef.current = newValue;
                                                                                            setEditingDateValue(newValue);

                                                                                            // Set pending saved date immediately
                                                                                            if (newValue) {
                                                                                                const pendingKey = `${item.book_id}_date_completed`;
                                                                                                pendingSavedDatesRef.current.set(pendingKey, {
                                                                                                    date: newValue,
                                                                                                    timestamp: Date.now(),
                                                                                                    bookId: item.book_id,
                                                                                                    field: 'date_completed'
                                                                                                });
                                                                                            }

                                                                                            // Set editing state if not already editing
                                                                                            if (!isEditingCompletedField) {
                                                                                                setEditingDate({ bookId: item.book_id, field: 'date_completed' });
                                                                                            }
                                                                                        }}
                                                                                        onBlur={async (e) => {
                                                                                            const blurValue = e.target.value || editingDateValueRef.current || '';
                                                                                            if (blurValue) {
                                                                                                editingDateValueRef.current = blurValue;
                                                                                                setEditingDateValue(blurValue);
                                                                                                const pendingKey = `${item.book_id}_date_completed`;
                                                                                                pendingSavedDatesRef.current.set(pendingKey, {
                                                                                                    date: blurValue,
                                                                                                    timestamp: Date.now(),
                                                                                                    bookId: item.book_id,
                                                                                                    field: 'date_completed'
                                                                                                });
                                                                                                try {
                                                                                                    await handleDateChange(item.book_id, 'date_completed', blurValue || null);
                                                                                                } catch (error) {
                                                                                                    console.error('[BOOK LIST MANAGER] Error saving date on blur:', error);
                                                                                                }
                                                                            }
                                                                        }}
                                                                        classNames="w-full"
                                                                    />
                                                                    ) : (
                                                                        <span className="text-gray-400">-</span>
                                                                    )}
                                                                            </div>
                                                                            {/* Always show Today/Clear icon buttons when we have a book_id - buttons should ALWAYS be visible when DatePicker is visible */}
                                                                            {/* CRITICAL: Use same condition as DatePicker above to ensure buttons always show when date field shows */}
                                                                            {item.book_id && (targetStudents.length > 0 || (selectedBookListData && selectedBookListData.student)) ? (
                                                                                <div className="flex flex-row gap-1 flex-shrink-0">
                                                                                    <button
                                                                                        type="button"
                                                                                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors min-w-[32px] min-h-[32px] flex items-center justify-center"
                                                                                        title="Set today's date"
                                                                                        onClick={async (e) => {
                                                                                            e.preventDefault();
                                                                                            e.stopPropagation();
                                                                                            const todayDate = getTodayDate();
                                                                                            // Update state and ref - this will trigger DatePicker to update via controlled value
                                                                                            editingDateValueRef.current = todayDate;
                                                                                            setEditingDateValue(todayDate);
                                                                                            // Keep editing state active so buttons remain visible
                                                                                            setEditingDate({ bookId: item.book_id, field: 'date_completed' });
                                                                                            // Set pending saved date
                                                                                            const pendingKey = `${item.book_id}_date_completed`;
                                                                                            pendingSavedDatesRef.current.set(pendingKey, {
                                                                                                date: todayDate,
                                                                                                timestamp: Date.now(),
                                                                                                bookId: item.book_id,
                                                                                                field: 'date_completed'
                                                                                            });
                                                                                            try {
                                                                                                await handleDateChange(item.book_id, 'date_completed', todayDate);
                                                                                            } catch (error) {
                                                                                                // Silent failure for auto-save operations - just log to console
                                                                                                // Error messages will only show when main Save button is clicked
                                                                                                console.error('[BOOK LIST MANAGER] Error saving today date (auto-save):', error);
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        <i className="fas fa-calendar-day text-base"></i>
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors min-w-[32px] min-h-[32px] flex items-center justify-center"
                                                                                        title="Clear date"
                                                                                        onClick={async (e) => {
                                                                                            e.preventDefault();
                                                                                            e.stopPropagation();
                                                                                            // Update state and ref - this will trigger DatePicker to update via controlled value
                                                                                            editingDateValueRef.current = '';
                                                                                            setEditingDateValue('');
                                                                                            // Keep editing state active so buttons remain visible
                                                                                            setEditingDate({ bookId: item.book_id, field: 'date_completed' });
                                                                                            // Clear pending saved date
                                                                                            const pendingKey = `${item.book_id}_date_completed`;
                                                                                            pendingSavedDatesRef.current.delete(pendingKey);
                                                                                            try {
                                                                                                await handleDateChange(item.book_id, 'date_completed', null);
                                                                                            } catch (error) {
                                                                                                // Silent failure for auto-save operations - just log to console
                                                                                                // Error messages will only show when main Save button is clicked
                                                                                                console.error('[BOOK LIST MANAGER] Error clearing date (auto-save):', error);
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        <i className="fas fa-times text-base"></i>
                                                                                    </button>
                                                                                </div>
                                                                            ) : null}
                                                                        </div>

                                                                        {/* Show different dates below for groups with non-common dates */}
                                                                        {isGroup && !isCommonDateCompleted && differentDatesCompleted.length > 0 && (
                                                                            <div className="text-xs text-gray-600 mt-1">
                                                                                <div className="font-semibold mb-1">Existing dates:</div>
                                                                                {differentDatesCompleted.map((cd, idx) => (
                                                                                    <div key={idx} className="text-gray-700">
                                                                                        {cd.studentName}: {cd.date}
                                                                                    </div>
                                                                                ))}
                                                                </div>
                                                            )}
                                                                    </div>
                                                                );
                                                            })()}
                                                        </div>

                                                        {/* Read By - Shows which students read this book */}
                                                        <div className="col-span-2">
                                                            {(() => {
                                                                if (item.isNew || !item.book_id) return <span className="text-gray-400">-</span>;
                                                                
                                                                const formik = formRef.current;
                                                                const selectedStudent = formik?.values?.selectedStudent;
                                                                const isGroup = selectedStudent && selectedStudent.value === 'all_group';
                                                                const selectedGroup = formik?.values?.selectedGroup;
                                                                
                                                                let targetStudents = [];
                                                                if (isGroup && selectedGroup && selectedGroup.value !== -1) {
                                                                    const groupId = parseInt(selectedGroup.value);
                                                                    const membersInGroup = groupMembers.filter(gm => parseInt(gm.group_id) === groupId);
                                                                    const memberStudentIds = membersInGroup.map(gm => gm.student || gm.user_id);
                                                                    targetStudents = students.filter(s => memberStudentIds.includes(s.user_id));
                                                                } else if (selectedStudent && selectedStudent.value !== -1) {
                                                                    const student = students.find(s => parseInt(s.user_id) === parseInt(selectedStudent.value));
                                                                    if (student) targetStudents = [student];
                                                                }

                                                                if (targetStudents.length === 0) {
                                                                    return <span className="text-gray-400">-</span>;
                                                                }

                                                                // Build enhanced allBookListItems that includes current item if it has date_completed
                                                                // This ensures the current item's reading status is always considered
                                                                let enhancedAllBookListItems = [...allBookListItems];
                                                                const currentItemIsRead = isBookRead(item.date_completed);
                                                                const currentBookListId = selectedBookListData?.id;
                                                                
                                                                if (currentItemIsRead && currentBookListId && item.book_id) {
                                                                    // Check if this item already exists in allBookListItems
                                                                    const itemExists = enhancedAllBookListItems.some(bli => 
                                                                        parseInt(bli.book_list_id) === parseInt(currentBookListId) &&
                                                                        parseInt(bli.book_id) === parseInt(item.book_id)
                                                                    );
                                                                    
                                                                    if (!itemExists) {
                                                                        // Add current item to enhanced list so getBookReadingStatus can find it
                                                                        enhancedAllBookListItems.push({
                                                                            id: item.id || `temp_${currentBookListId}_${item.book_id}`,
                                                                            book_list_id: currentBookListId,
                                                                            book_id: item.book_id,
                                                                            date_completed: item.date_completed,
                                                                            date_assigned: item.date_assigned,
                                                                            sort_order: item.sort_order || 0,
                                                                            student: selectedBookListData?.student || null
                                                                        });
                                                                    } else {
                                                                        // Update existing item with current date_completed if it's more recent or missing
                                                                        const existingItemIndex = enhancedAllBookListItems.findIndex(bli => 
                                                                            parseInt(bli.book_list_id) === parseInt(currentBookListId) &&
                                                                            parseInt(bli.book_id) === parseInt(item.book_id)
                                                                        );
                                                                        if (existingItemIndex >= 0) {
                                                                            enhancedAllBookListItems[existingItemIndex] = {
                                                                                ...enhancedAllBookListItems[existingItemIndex],
                                                                                date_completed: item.date_completed || enhancedAllBookListItems[existingItemIndex].date_completed,
                                                                                date_assigned: item.date_assigned || enhancedAllBookListItems[existingItemIndex].date_assigned
                                                                            };
                                                                        }
                                                                    }
                                                                }

                                                                // Get reading status from studentDates (new architecture)
                                                                // Fallback to enhancedAllBookListItems for backward compatibility
                                                                let readingStatus = getBookReadingStatus(
                                                                    item.book_id,
                                                                    targetStudents,
                                                                    bookLists,
                                                                    enhancedAllBookListItems,
                                                                    studentDates
                                                                );

                                                                // When a book list is selected for a group, it becomes a NEW book list
                                                                // The dates should only reflect the selected students/group, not the original owner
                                                                // So we should NOT check the original book list owner's reading status
                                                                // Only check the targetStudents (selected group/student)
                                                                // The "Read By" column should only show students from targetStudents who have read the book

                                                                if (readingStatus.readByNames.length === 0) {
                                                                    return <span className="text-gray-400">Not read</span>;
                                                                }

                                                                return (
                                                                    <div className="text-sm">
                                                                        {readingStatus.readByNames.length === targetStudents.length ? (
                                                                            <div className="flex flex-col gap-1">
                                                                                <span className="text-green-600 font-semibold">All ({readingStatus.readByNames.length}):</span>
                                                                                <span className="text-gray-700 text-xs">{readingStatus.readByNames.join(', ')}</span>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="flex flex-col gap-1">
                                                                                <span className="text-blue-600 font-semibold">{readingStatus.readByNames.length} of {targetStudents.length}:</span>
                                                                                <span className="text-gray-700 text-xs">{readingStatus.readByNames.join(', ')}</span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })()}
                                                        </div>

                                                        {/* Actions */}
                                                        <div className="col-span-2 flex flex-row gap-x-1 justify-center">
                                                            <TooltipWrapper tooltipText="Remove book">
                                                                <Button
                                                                    color="lte-mpLRed"
                                                                    variant="icon"
                                                                    onClick={() => {
                                                                        // For new book lists (from templates) without ID, just remove from local state
                                                                        if (selectedBookListData.templateId && !selectedBookListData.id) {
                                                                            const updatedItems = selectedBookListData.parsedItems.filter((it, idx) => idx !== index);
                                                                            updatedItems.forEach((i, idx) => {
                                                                                i.order = idx + 1;
                                                                                i.sort_order = idx + 1;
                                                                            });
                                                                            setSelectedBookListData({
                                                                                ...selectedBookListData,
                                                                                parsedItems: updatedItems
                                                                            });
                                                                        } else if (item && item.book_id) {
                                                                            handleRemoveBook(item);
                                                                        }
                                                                    }}
                                                                >
                                                                    <SVGIcon fileName="trash" />
                                                                </Button>
                                                            </TooltipWrapper>
                                                        </div>
                                                    </div>
                                                    ))
                                                ) : (
                                                    <div className="text-center py-8 text-gray-500">
                                                        This book list is empty. Click "Add Book" to add books.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : hasLoaded && selectedBookListData && selectedBookListData.parsedItems && Array.isArray(selectedBookListData.parsedItems) && selectedBookListData.parsedItems.length === 0 ? (
                                    <div className="text-gray-500 text-center py-8">
                                        This book list is empty. Select a book list from the dropdown above to populate it.
                                    </div>
                                ) : hasLoaded && (
                                    <div className="text-gray-500 text-center py-8">
                                        <p className="mb-2">Select a Group or Student, then choose a Book List from the dropdown above to assign it.</p>
                                        <p className="text-sm">Or use "Add Book List" dropdown to create a new book list or edit an existing one.</p>
                                    </div>
                                )
                            }

                        </form>
                    )}
                </Formik>

                {/* ============================================ */}
                {/* SEPARATE READING STATUS SECTION - ALL BOOKS IN DATABASE */}
                {/* This section is completely separate from the book list edit table above */}
                {/* It shows ALL books in the system, not just books in the selected book list */}
                {/* ============================================ */}
                <div className="mt-20 mb-8 border-t-4 border-blue-500 pt-8 bg-blue-50 px-6 pb-6 rounded-lg shadow-xl" style={{ minHeight: '300px' }}>
                {(() => {
                    // #region agent log
                    // Dev-only telemetry: avoid noisy ERR_CONNECTION_REFUSED in production
                    if (window.location.hostname === 'localhost') {
                            fetch('http://127.0.0.1:7242/ingest/01140156-0285-4096-a432-9a8fed862f92', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: '_BookLists.js:2624', message: 'Reading status section rendering', data: { booksCount: books?.length || 0, hasSelectedStudent: !!selectedStudentForReadingStatus }, timestamp: Date.now(), sessionId: 'debug-session', runId: 'render-check', hypothesisId: 'A' }) }).catch(() => { });
                    }
                    // #endregion
                    const formik = formRef.current;
                    const selectedStudent = selectedStudentForReadingStatus || (formik && formik.values && formik.values.selectedStudent);
                    const isGroup = selectedStudent && selectedStudent.value === 'all_group';
                    const selectedGroup = selectedGroupForReadingStatus || (formik && formik.values && formik.values.selectedGroup);
                
                    let targetStudents = [];
                    if (isGroup && selectedGroup && selectedGroup.value !== -1) {
                        const groupId = parseInt(selectedGroup.value);
                        const membersInGroup = groupMembers.filter(gm => parseInt(gm.group_id) === groupId);
                        const memberStudentIds = membersInGroup.map(gm => gm.student || gm.user_id);
                        targetStudents = students.filter(s => memberStudentIds.includes(s.user_id));
                    } else if (selectedStudent && selectedStudent.value && selectedStudent.value !== -1 && selectedStudent.value !== 'all_group') {
                        const student = students.find(s => String(s.user_id) === String(selectedStudent.value));
                        if (student) {
                            targetStudents = [student];
                        }
                    }

                    // Filter ALL books in the system based on reading status
                    let filteredBooks = books || [];
                    if (readingStatusFilter !== 'all' && books && books.length > 0 && targetStudents.length > 0 && allBookListItems && allBookListItems.length > 0) {
                        filteredBooks = books.filter(book => {
                            const readingStatus = getBookReadingStatus(
                                parseInt(book.id || book.book_id),
                                targetStudents,
                                bookLists,
                                    allBookListItems,
                                    studentDates
                            );
                            
                            if (readingStatusFilter === 'read_by_all') {
                                return readingStatus.readBy.length === targetStudents.length && readingStatus.readBy.length > 0;
                            } else if (readingStatusFilter === 'unread_by_all') {
                                return readingStatus.unreadBy.length === targetStudents.length && readingStatus.readBy.length === 0;
                            } else if (readingStatusFilter === 'read_by_at_least_one') {
                                return readingStatus.readBy.length > 0;
                            }
                            return true;
                        });
                    } else if (readingStatusFilter === 'all' && books && books.length > 0) {
                        // Show all books when filter is 'all'
                        filteredBooks = books;
                    }

                    // #region agent log
                    // Dev-only telemetry: avoid noisy ERR_CONNECTION_REFUSED in production
                    if (window.location.hostname === 'localhost') {
                            fetch('http://127.0.0.1:7242/ingest/01140156-0285-4096-a432-9a8fed862f92', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: '_BookLists.js:2665', message: 'Rendering reading status table', data: { filteredBooksCount: filteredBooks?.length || 0, targetStudentsCount: targetStudents.length, readingStatusFilter, usingAllBooks: true }, timestamp: Date.now(), sessionId: 'debug-session', runId: 'render-check', hypothesisId: 'A' }) }).catch(() => { });
                    }
                    // #endregion
                    // Console log for verification
                    console.log('[READING STATUS SECTION] Rendering with:', {
                        filteredBooksCount: filteredBooks?.length || 0,
                        totalBooksInSystem: books?.length || 0,
                        targetStudentsCount: targetStudents.length,
                        readingStatusFilter,
                        usingAllBooks: true,
                        sectionVisible: true,
                        allBookListItemsCount: allBookListItems?.length || 0,
                        bookListsCount: bookLists?.length || 0,
                        hasTargetStudents: targetStudents.length > 0
                    });
                    
                    return (
                        <div className="p-6 bg-white rounded-lg shadow-lg border-2 border-blue-300" key={`reading-status-${selectedStudentForReadingStatus?.value || 'none'}-${selectedGroupForReadingStatus?.value || selectedGroup?.value || 'none'}-${readingStatusFilter}`}>
                            <h3 className="text-2xl font-bold mb-2 text-blue-800">📚 Book Reading Status - All Books in Database</h3>
                            <p className="text-sm text-gray-700 mb-6 font-medium">This section shows ALL books in the system (not just books in the selected book list). Filter by reading status for the selected student or group.</p>
                            {targetStudents.length === 0 ? (
                                <div className="text-gray-500 text-center py-8">
                                    {selectedStudent && selectedStudent.value && selectedStudent.value !== -1 && selectedStudent.value !== 'all_group' 
                                        ? `Student selected (${selectedStudent.value}) but not found in students list (${students.length} students). Please refresh the page.`
                                        : 'Please select a Student or Group to view reading status.'}
                                </div>
                            ) : !allBookListItems || allBookListItems.length === 0 ? (
                                <div className="text-yellow-600 text-center py-8">
                                    <div className="font-semibold mb-2">Loading reading status data...</div>
                                    <div className="text-sm">Book list items are being loaded. Please wait a moment and refresh if this message persists.</div>
                                </div>
                            ) : (
                                <div>
                                    <div className="mb-4 flex flex-row gap-4 items-center">
                                        <label className="text-sm font-medium">Filter by Reading Status:</label>
                                        <SelectSingle
                                            id="reading-status-filter-all-books"
                                            name="readingStatusFilterAllBooks"
                                                value={{
                                                    value: readingStatusFilter, label:
                                                readingStatusFilter === 'all' ? 'All Books' :
                                                readingStatusFilter === 'read_by_all' ? 'Read by All' :
                                                readingStatusFilter === 'unread_by_all' ? 'Unread by All' :
                                                readingStatusFilter === 'read_by_at_least_one' ? 'Read by At Least One' : 'All Books'
                                            }}
                                            options={[
                                                { value: 'all', label: 'All Books' },
                                                { value: 'read_by_all', label: 'Read by All' },
                                                { value: 'unread_by_all', label: 'Unread by All' },
                                                { value: 'read_by_at_least_one', label: 'Read by At Least One' }
                                            ]}
                                            onChange={(e) => {
                                                setReadingStatusFilter(e.target.value.value);
                                            }}
                                            classNames="w-64"
                                        />
                                    </div>

                                    {/* Table Header */}
                                    <div className="table-header grid grid-cols-12 gap-2 p-3 font-semibold rounded-t-lg bg-gray-100">
                                        <div className="col-span-4">Book Title</div>
                                        <div className="col-span-2">Author</div>
                                        <div className="col-span-2">Genre</div>
                                        <div className="col-span-4">Read By</div>
                                    </div>

                                    {/* Book Items */}
                                    <div className="divide-y divide-gray-200">
                                        {filteredBooks.length > 0 ? filteredBooks.map((book, index) => {
                                            const readingStatus = getBookReadingStatus(
                                                parseInt(book.id || book.book_id),
                                                targetStudents,
                                                bookLists,
                                                    allBookListItems,
                                                    studentDates
                                            );

                                            return (
                                                <div
                                                    key={`all-books-${book.id || book.book_id}-${index}`}
                                                    className="grid grid-cols-12 gap-2 p-3 items-center hover:bg-gray-50"
                                                >
                                                    {/* Book Title with Reading Status Info */}
                                                    <div className="col-span-4">
                                                        <div className="flex flex-col">
                                                            <span className="font-medium">{book.title || 'Unknown'}</span>
                                                            {readingStatus.readByNames.length > 0 && (
                                                                <div className="text-xs mt-1 text-gray-600">
                                                                    <span className="font-semibold text-blue-600">
                                                                        {readingStatus.readByNames.length} {readingStatus.readByNames.length === 1 ? 'student' : 'students'} read this book
                                                                    </span>
                                                                    <div className="mt-1 text-gray-500">
                                                                        {readingStatus.readByNames.map((nameWithDate, idx) => (
                                                                            <div key={idx} className="text-xs">
                                                                                • {nameWithDate}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Author */}
                                                    <div className="col-span-2">
                                                        <span className="text-gray-600">{book.author || '-'}</span>
                                                    </div>

                                                    {/* Genre */}
                                                    <div className="col-span-2">
                                                        <span className="text-gray-600">{book.genre || '-'}</span>
                                                    </div>

                                                    {/* Read By Summary */}
                                                    <div className="col-span-4">
                                                        {readingStatus.readByNames.length === 0 ? (
                                                                <span className="text-gray-400">0 {targetStudents.length > 0 ? `of ${targetStudents.length}` : ''} read</span>
                                                        ) : (
                                                            <div className="text-sm">
                                                                {readingStatus.readByNames.length === targetStudents.length ? (
                                                                    <div>
                                                                        <span className="text-green-600 font-semibold">✓ All ({readingStatus.readByNames.length}/{targetStudents.length})</span>
                                                                    </div>
                                                                ) : (
                                                                    <div>
                                                                            <span className="text-blue-600 font-semibold">{readingStatus.readByNames.length}/{targetStudents.length > 0 ? targetStudents.length : readingStatus.readByNames.length} read</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        }) : (
                                            <div className="text-center py-8 text-gray-500">
                                                No books match the selected filter.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })()}
                </div>
            </div>
            <Socket refreshData={refreshData} page={pageTitle} setVersion={props.setVersion} />

            {showModal && (
                <BookListModal
                    show={showModal}
                    mode={modalMode}
                    bookList={selectedBookList}
                    books={books}
                    groups={groups}
                    students={students}
                    templates={templates}
                    templateItems={templateItems}
                    onClose={handleCloseModal}
                    fetchBookListsId={fetchBookListsId}
                    fetchBookListsStudents={fetchBookListsStudents}
                    createBookList={createBookList}
                    updateBookList={updateBookList}
                    deleteBookList={deleteBookList}
                    fetchBookListTemplatesFull={fetchBookListTemplatesFull}
                />
            )}

            {showTemplateModal && (
                <BookListTemplateModal
                    key={`template-modal-${templateModalMode}-${selectedTemplate?.id || 'new'}`}
                    show={showTemplateModal}
                    mode={templateModalMode}
                    template={selectedTemplate}
                    books={books}
                    onClose={handleCloseTemplateModal}
                    createBookListTemplate={createBookListTemplate}
                    updateBookListTemplate={updateBookListTemplate}
                    deleteBookListTemplate={deleteBookListTemplate}
                />
            )}
        </div>
    );
};

export default connect(null, {
    fetchBookListsAll,
    fetchBookListsId,
    fetchBookListsStudents,
    fetchBooksAll,
    fetchCycleGroupsAll,
    fetchStudentsAll,
    fetchRpCentersAll,
    createBookList,
    updateBookList,
    deleteBookList,
    fetchBookListTemplatesAll,
    fetchBookListTemplatesFull,
    createBookListTemplate,
    updateBookListTemplate,
    deleteBookListTemplate,
    fetchBookListItemsAll,
    fetchBookStudentDatesByBookList,
    saveBookStudentDate
})(BookLists);
